import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, SUPABASE_URL, SUPABASE_KEY } from "./supabase";
import { C, styles, useMobile, showToast, GlobalToast, loadLogoBase64 } from "./ui/core";
import { PROOF_BUCKET } from "./finance/api";
import { proofLinkProps } from "./finance/proofs";
import { SalaryAdminPage, MySalaryPage, BonusAdminPage, MyBonusPage, UpdateSalaryModal, computeMonthPayroll, computePendingPayroll, historyFor, salaryRowOn, getSalaryHistory, payrollPolicy } from "./finance";
import { setStaffSalary } from "./finance/api";
import { lastFinalizedDay } from "./finance/SalaryPages";
import { AccountsPage } from "./accounting";
import { SalesPage } from "./sales";
import { QueenDollarPage } from "./queendollar";
import { prepareUpload, optimizedForUpload, kb } from "./lib/uploadCompression";
import { storeFile } from "./lib/fileStore";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import {
    Home, Globe, LayoutGrid, Megaphone, Users, Contact, Umbrella, Award, Medal,
    PenLine, Calendar, BarChart2, Wallet, Briefcase, CreditCard, Gift, Vote,
    ClipboardList, Building2, Settings, Monitor, ClipboardCheck, TrendingUp,
    LogOut, Menu, Bell, Lock, User, Landmark, Star, ChevronRight, X,
    RefreshCw, ExternalLink, Trash2, MessageCircle, Heart, Send, Eye,
    CheckCircle, XCircle, AlertCircle, Info, Shield, FileText, Layers,
    Printer, Receipt, Download, DollarSign, Percent, Wrench, ImageDown,
    MapPin, Wifi, Fingerprint, Handshake, BadgeDollarSign,
} from "lucide-react";

// ─── Supabase Config ──────────────────────────────────────────────────────────────
// URL + publishable key come from src/supabase.js (environment only).
// Privileged operations (create login, reset password) run in the "admin-users"
// Edge Function — no secret key ever exists in this browser code.

const sb = async (path, options = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    const { headers: extraHeaders, prefer, ...restOptions } = options;
    const safeExtra = Object.fromEntries(
        Object.entries(extraHeaders || {}).filter(([k]) => k !== "apikey" && k !== "Authorization")
    );
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
        ...restOptions,
        headers: {
            "apikey": SUPABASE_KEY,
            // Logged-out requests (public profile page) send only the apikey.
            ...(session?.access_token ? { "Authorization": `Bearer ${session.access_token}` } : {}),
            "Content-Type": "application/json",
            "Prefer": (extraHeaders && extraHeaders["Prefer"]) || prefer || "return=representation",
            ...safeExtra,
        },
    });
    if (!res.ok) {
        const err = await res.text();
        throw new Error(err);
    }
    const text = await res.text();
    return text ? JSON.parse(text) : [];
};

// Server-side SQL functions (validated in the database — see migrations/003)
const rpc = (fn, args = {}) => sb(`rpc/${fn}`, { method: "POST", body: JSON.stringify(args) });

// Supabase Edge Functions (privileged work that must never run in the browser)
async function callFunction(name, body) {
    const { data, error } = await supabase.functions.invoke(name, { body });
    if (error) {
        let msg = error.message;
        try { const j = await error.context.json(); msg = j.message || msg; } catch {}
        throw new Error(msg);
    }
    return data;
}

const STAFF_FULL_COLUMNS = "id,name,email,auth_id,designation,salary,join_date,employment_type,mod_staff,mod_freelancer,allow_remote,dob,bio,age,phone,bank_name,bank_account,bank_branch,esewa_number,profile_image,last_login,role,employment_status,employment_end_date,attendance_affects_payroll,is_test_account";

const db = {
    // Staff — full rows (salary, bank…) only for admins and for the user's own row;
    // everyone else gets the safe staff_directory view.
    getStaff: () => sb(`staff?select=${STAFF_FULL_COLUMNS}&order=name`),
    getOwnStaff: (authId) => sb(`staff?auth_id=eq.${authId}&select=${STAFF_FULL_COLUMNS}&limit=1`),
    getStaffDirectory: () => sb("staff_directory?select=*&order=name"),
    addStaff: (s) => sb("staff", { method: "POST", body: JSON.stringify(s) }),
    updateStaff: (id, s) => sb(`staff?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(s) }),
    deleteStaff: (id) => sb(`staff?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),

    // Attendance
    getAttendance: () => sb("attendance?order=date"),
    getAttendanceByStaff: (staffId) => sb(`attendance?staff_id=eq.${staffId}&order=date`),
    upsertAttendance: (rec) => sb("attendance", { method: "POST", body: JSON.stringify(rec), headers: { "Prefer": "resolution=merge-duplicates,return=representation" } }),
    deleteAttendance: (id) => sb(`attendance?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),

    // Holidays
    getHolidays: () => sb("holidays?order=date"),
    addHoliday: (date, name) => sb("holidays", { method: "POST", body: JSON.stringify({ date, name }), headers: { "Prefer": "resolution=ignore-duplicates,return=representation" } }),
    deleteHoliday: (date) => sb(`holidays?date=eq.${date}`, { method: "DELETE", prefer: "return=minimal" }),
    // Projects
    getProjects: () => sb("projects?order=created_at.desc"),
    addProject: (p) => sb("projects", { method: "POST", body: JSON.stringify(p) }),
    updateProject: (id, p) => sb(`projects?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(p) }),
    deleteProject: (id) => sb(`projects?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // Salary Payments
    getSalaryPayments: () => sb("salary_payments?order=year.desc,month.desc"),
    addSalaryPayment: (r) => sb("salary_payments", { method: "POST", body: JSON.stringify(r) }),
    updateSalaryPayment: (id, r) => sb(`salary_payments?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(r) }),
    deleteSalaryPayment: (id) => sb(`salary_payments?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // Withdraw Requests
    getCommissions: () => sb("commissions?order=created_at.desc"),
    addCommission: (r) => sb("commissions", { method: "POST", body: JSON.stringify(r) }),
    updateCommission: (id, r) => sb(`commissions?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(r) }),
    deleteCommission: (id) => sb(`commissions?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    getBonuses: () => sb("bonuses?order=created_at.desc"),
    addBonus: (r) => sb("bonuses", { method: "POST", body: JSON.stringify(r) }),
    updateBonus: (id, r) => sb(`bonuses?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(r) }),
    deleteBonus: (id) => sb(`bonuses?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    getWithdrawRequests: () => sb("withdraw_requests?order=created_at.desc"),
    addWithdrawRequest: (r) => sb("withdraw_requests", { method: "POST", body: JSON.stringify(r) }),
    updateWithdrawRequest: (id, r) => sb(`withdraw_requests?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(r) }),
    // Points
    getPointsLedger: () => sb("points_ledger?order=created_at.desc"),
    addPoints: (r) => sb("points_ledger", { method: "POST", body: JSON.stringify(r) }),
    // Rewards
    getRewards: () => sb("rewards?order=created_at.desc"),
    addReward: (r) => sb("rewards", { method: "POST", body: JSON.stringify(r) }),
    updateReward: (id, r) => sb(`rewards?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(r) }),
    deleteReward: (id) => sb(`rewards?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // Redemptions
    getRedemptions: () => sb("redemptions?order=created_at.desc"),
    addRedemption: (r) => sb("redemptions", { method: "POST", body: JSON.stringify(r) }),
    updateRedemption: (id, r) => sb(`redemptions?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(r) }),
    // Explore posts
    getPosts: () => sb("explore_posts?order=created_at.desc"),
    addPost: (p) => sb("explore_posts", { method: "POST", body: JSON.stringify(p) }),
    deletePost: (id) => sb(`explore_posts?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    likePost: (id) => rpc("toggle_post_like", { p_post_id: id }),
    // Replies
    getReplies: () => sb("post_replies?order=created_at.asc"),
    // Notifications
    getNotifications: (userId) => sb(`notifications?recipient_id=eq.${userId}&order=created_at.desc`),
    addNotification: (n) => sb("notifications", { method: "POST", body: JSON.stringify(n) }),
    markAllRead: (userId) => sb(`notifications?recipient_id=eq.${userId}&read=eq.false`, { method: "PATCH", body: JSON.stringify({ read: true }) }),
    deleteNotification: (id) => sb(`notifications?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    addReply: (r) => sb("post_replies", { method: "POST", body: JSON.stringify(r) }),
    deleteReply: (id) => sb(`post_replies?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // Moods
    getMoods: () => sb("moods?order=created_at.desc"),
    upsertMood: (m) => sb("moods", { method: "POST", body: JSON.stringify(m), headers: { "Prefer": "resolution=merge-duplicates,return=representation" } }),
    // Staff of month
    getSotm: () => sb("staff_of_month?order=year.desc&order=month.desc"),
    setSotm: (staffId, year, month) => rpc("set_staff_of_month", { p_staff_id: staffId, p_year: year, p_month: month }),
    // Work logs
    getWorkLogs: () => sb("work_logs?order=created_at.desc"),
    addWorkLog: (w) => sb("work_logs", { method: "POST", body: JSON.stringify(w) }),
    deleteWorkLog: (id) => sb(`work_logs?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // Polls
    getPolls: () => sb("polls?order=created_at.desc"),
    addPoll: (p) => sb("polls", { method: "POST", body: JSON.stringify(p) }),
    votePoll: (id, optIdx) => rpc("cast_poll_vote", { p_poll_id: id, p_option: String(optIdx) }),
    deletePoll: (id) => sb(`polls?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // Announcements
    getAnnouncements: () => sb("announcements?order=created_at.desc"),
    addAnnouncement: (a) => sb("announcements", { method: "POST", body: JSON.stringify(a) }),
    deleteAnnouncement: (id) => sb(`announcements?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // Leave requests
    getLeaveRequests: () => sb("leave_requests?order=created_at.desc"),
    addLeaveRequest: (l) => sb("leave_requests", { method: "POST", body: JSON.stringify(l) }),
    updateLeaveRequest: (id, l) => sb(`leave_requests?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(l) }),
    // Meeting rooms
    getMeetingRooms: () => sb("meeting_rooms?order=created_at.desc"),
    addMeetingRoom: (m) => sb("meeting_rooms", { method: "POST", body: JSON.stringify(m) }),
    updateMeetingRoom: (id, m) => sb(`meeting_rooms?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(m) }),
    deleteMeetingRoom: (id) => sb(`meeting_rooms?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // Bookings
    getBookings: () => sb("room_bookings?order=created_at.desc"),
    addBooking: (b) => sb("room_bookings", { method: "POST", body: JSON.stringify(b) }),
    deleteBooking: (id) => sb(`room_bookings?id=eq.${id}`, { method: "DELETE", prefer: "return=minimal" }),
    // App Settings
    getAppSettings: () => sb("app_settings?id=eq.1"),
    upsertAppSettings: (s) => sb("app_settings", { method: "POST", body: JSON.stringify(s), headers: { "Prefer": "resolution=merge-duplicates,return=representation" } }),
};

// ─── Points helpers ───────────────────────────────────────────────────────────
function getStaffPoints(staffId) {
    const earned = _pointsLedger.filter(p => p.staff_id === staffId).reduce((a, p) => a + Number(p.points), 0);
    const redeemed = _redemptions.filter(r => r.staff_id === staffId && r.status === "approved").reduce((a, r) => a + Number(r.points_used), 0);
    return { earned, redeemed, balance: earned - redeemed };
}
// Admin-only: direct ledger insert (the database rejects this for non-admins).
async function awardPoints(staffId, points, reason, reload) {
    await db.addPoints({ id: "pt" + Date.now() + Math.random().toString(36).slice(2, 6), staff_id: staffId, points, reason, created_at: new Date().toISOString() });
    if (reload) await reload();
}
// Staff-earned points: the server checks eligibility, fixes the amount and awards once.
async function claimPoints(fn, args, reload) {
    try { await rpc(fn, args); } catch (e) { console.error("Points claim failed:", e); }
    if (reload) await reload();
}


// ─── Timezone helper ──────────────────────────────────────────────────────────
function normalizeISO(isoStr) {
    if (!isoStr) return null;
    // Supabase sometimes returns timestamps without Z — force UTC parsing
    if (isoStr.endsWith("Z") || isoStr.includes("+")) return isoStr;
    return isoStr + "Z";
}
function fmtTZ(isoStr) {
    if (!isoStr) return null;
    try {
        return new Date(normalizeISO(isoStr)).toLocaleString("en-US", {
            timeZone: _appSettings.timezone || "America/Los_Angeles",
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit"
        });
    } catch { return new Date(normalizeISO(isoStr)).toLocaleString(); }
}
function timeAgoLocal(isoStr) {
    if (!isoStr) return "Never";
    const diff = Date.now() - new Date(normalizeISO(isoStr)).getTime();
    if (diff < 0) return "just now";
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return fmtTZ(isoStr);
}
function isOnline(last_login) {
    if (!last_login) return false;
    return (Date.now() - new Date(normalizeISO(last_login)).getTime()) < 5 * 60 * 1000; // 5 min
}

// ─── Utility helpers ───────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const isSaturday = (dateStr) => new Date(dateStr).getDay() === 6;
const monthName = (m) => ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m];
const fmtDate = (d) => { const dt = new Date(d); return `${pad(dt.getDate())} ${monthName(dt.getMonth())} ${dt.getFullYear()}`; };
const daysInRange = (start, end) => {
    const days = []; let cur = new Date(start); const last = new Date(end);
    while (cur <= last) { days.push(`${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`); cur.setDate(cur.getDate() + 1); }
    return days;
};

// ─── In-memory cache (synced from Supabase) ──────────────────────────────────────
let _staff = [];
let _attendance = {}; // key: staffId_date → record
let _holidays = []; // just dates array for quick lookup
let _holidayRecords = []; // full records with name
let _projects = [];
let _commissions = [];
let _bonuses = [];
let _withdrawRequests = [];
let _salaryPayments = [];
let _salaryHistory = [];            // effective-dated salaries (RLS: own rows, or all for admins)
let _salaryHistoryAvailable = false; // false until migration 005 is applied
let _pointsLedger = [];
let _rewards = [];
let _redemptions = [];
let _posts = [];
let _replies = [];
let _notifications = [];
let _moods = [];
let _sotm = [];
let _workLogs = [];
let _polls = [];
let _announcements = [];
let _leaveRequests = [];
let _meetingRooms = [];
let _bookings = [];
let _appSettings = { timezone: "America/Los_Angeles", post_points: 2, default_page: "explore", id: 1, office_lat: 27.672168937354662, office_lng: 85.38469491534256, office_radius: 100, office_ip: "103.10.31.93,2001:4860:7:50a::fd" };

function haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
let _userMenuAccess = []; // array of menu_id strings granted to current user

// Menus an admin may grant to staff. NEVER add Accounts / finance / payroll /
// payment menus here: company financial data is Admin-only, enforced in the
// database (is_admin()), and a CHECK constraint on user_menu_access rejects
// financial menu ids (migrations/003_security_rls.sql).
const GRANTABLE_MENUS = [
    { id: "trello",        label: "Trello Boards",            icon: LayoutGrid  },
    { id: "teamdashboard", label: "Team Dashboard",           icon: Monitor     },
    { id: "report",        label: "Reports",                  icon: BarChart2   },
    { id: "analytics",     label: "Analytics",                icon: TrendingUp  },
    { id: "editattendance",label: "Edit Attendance",          icon: PenLine     },
    { id: "rentreceipt",   label: "Rent Receipts",            icon: Receipt     },
    { id: "tenants",       label: "Manage Tenants",           icon: Users       },
    { id: "worklogs",      label: "Work Logs (All Staff)",    icon: ClipboardList },
    { id: "leaverequests", label: "Leave Requests (Manage)",  icon: Umbrella    },
];

function getStaffName(id) { return _staff.find(s => s.id === id)?.name || "Unknown"; }
function getAttendanceRecord(staffId, date) { return _attendance[`${staffId}_${date}`] || null; }

function getStatusForDate(staffId, date) {
    // Explicit attendance record always wins so the admin override on a
    // holiday/Saturday actually displays and counts.
    const rec = getAttendanceRecord(staffId, date);
    if (rec) return rec.status;
    if (_holidays.includes(date)) return "holiday";
    if (isSaturday(date)) return "saturday";
    if (date < todayStr()) return "absent";
    if (date === todayStr()) return "not_marked";
    return "upcoming";
}

// Salary rows for a staff member. Before migration 005 exists, fall back to the
// single staff.salary value so the app keeps working during rollout.
function salaryRowsFor(staffId) {
    if (_salaryHistoryAvailable) return historyFor(_salaryHistory, staffId);
    const st = _staff.find(s => s.id === staffId);
    return st && Number(st.salary) > 0 ? [{ id: null, staff_id: staffId, monthly_salary: Number(st.salary), effective_from: "0000-01-01", effective_to: null, review_status: "confirmed" }] : [];
}
function currentSalaryOf(staffId, date = todayStr()) {
    const row = salaryRowOn(salaryRowsFor(staffId), date);
    return row ? Number(row.monthly_salary) : 0;
}
// Salary earned per day on a date (monthly salary in effect ÷ days in that month).
function dayRate(staffId, dateStr) {
    const [y, m] = dateStr.split("-").map(Number);
    return currentSalaryOf(staffId, dateStr) / new Date(y, m, 0).getDate();
}
// Deduction for one absent day under the employee's payroll policy (0 when attendance doesn't affect pay).
function absenceDeduction(staffId, dateStr) {
    return payrollPolicy(_staff.find(s => s.id === staffId)).attendanceAffectsPayroll ? dayRate(staffId, dateStr) : 0;
}
function hasAttendanceInMonth(staffId, y, m) {
    const prefix = `${staffId}_${y}-${pad(m)}-`;
    return Object.keys(_attendance).some(k => k.startsWith(prefix));
}

// Effective-dated, day-prorated payroll for one AD month (see src/finance/payroll.js).
function computeSalarySummary(staffId, month, year) {
    const st = _staff.find(s => s.id === staffId);
    if (!st) return null;
    return computeMonthPayroll({ rows: salaryRowsFor(staffId), year, month, today: todayStr(), statusForDate: d => getStatusForDate(staffId, d), policy: payrollPolicy(st) });
}

// Compute virtual outstanding salary records from attendance (no DB record needed)


async function loadImageBase64(url) {
    try {
        const res = await fetch(url);
        const blob = await res.blob();
        return new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch { return null; }
}

function computePendingRecords(filterStaffId = null) {
    return computePendingPayroll({
        staff: _staff, salaryPayments: _salaryPayments, today: todayStr(),
        salaryRowsFor, hasAttendanceInMonth, statusForDate: getStatusForDate,
    }, filterStaffId);
}

// Returns the "current count" for a page used to compute badge deltas
function getPageCount(pageId, userId, isAdmin) {
    if (isAdmin) {
        if (pageId === "leaverequests") return _leaveRequests.filter(l => l.status === "pending").length;
        if (pageId === "salarypayments") return computePendingRecords().length;
        if (pageId === "withdrawrequests") return _withdrawRequests.filter(r => r.status === "pending").length;
        if (pageId === "commissions") return _commissions.filter(c => c.status !== "paid").length;
        if (pageId === "bonuses") return _bonuses.filter(b => b.status !== "paid").length;
        if (pageId === "worklogs") return _workLogs.length;
        if (pageId === "explore") return _posts.length;
        if (pageId === "polls") return _polls.filter(p => p.active).length;
    } else {
        if (pageId === "mysalary") return computePendingRecords(userId).length;
        if (pageId === "mycommission") return _commissions.filter(c => c.staff_id === userId && c.status !== "paid").length;
        if (pageId === "mybonus") return _bonuses.filter(b => b.staff_id === userId && b.status !== "paid").length;
        if (pageId === "leaverequests") return _leaveRequests.filter(l => l.staff_id === userId && l.status === "pending").length;
        if (pageId === "withdrawalhistory") return _withdrawRequests.filter(r => r.freelancer_id === userId && r.status === "cleared").length;
        if (pageId === "myprojects") return _projects.filter(p => p.freelancer_id === userId && p.status === "pending").length;
        if (pageId === "explore") return _posts.length;
        if (pageId === "polls") return _polls.filter(p => p.active).length;
        if (pageId === "announcements") return _announcements.length;
    }
    return 0;
}

function StatusBadge({ status }) {
    const map = { present: [C.green, "Present"], absent: [C.red, "Absent"], auto_absent: [C.red, "Absent"], saturday: [C.accent, "Saturday"], holiday: [C.yellow, "Holiday"], not_marked: [C.muted, "—"], upcoming: [C.muted, "Upcoming"] };
    const [color, label] = map[status] || [C.muted, status];
    return <span style={styles.badge(color)}>{label}</span>;
}

function StatCard({ icon, label, value, color = C.accent, sub }) {
    const mobile = useMobile();
    return (
        <div style={{ ...styles.card, display: "flex", alignItems: "center", gap: mobile ? 10 : 16, padding: mobile ? "12px 10px" : 20 }}>
            <div style={{ width: mobile ? 36 : 52, height: mobile ? 36 : 52, borderRadius: 12, background: color + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: mobile ? 17 : 24, flexShrink: 0 }}>{icon}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: mobile ? 16 : 24, fontWeight: 800, color: C.white, lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
                <div style={{ fontSize: mobile ? 10 : 13, color: C.muted, marginTop: 2 }}>{label}</div>
                {sub && <div style={{ fontSize: mobile ? 10 : 12, color, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
            </div>
        </div>
    );
}

// ─── Login ────────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
    const mobile = useMobile();
    const getSaved = () => { try { return JSON.parse(localStorage.getItem("foxbeep_remember") || "null"); } catch { return null; } };
    const saved = getSaved();
    const [email, setEmail] = useState(saved?.email || "");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(!!saved);
    const [showPass, setShowPass] = useState(false);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const doLogin = async () => {
        setErr(""); setLoading(true);
        try {
            const { data, error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) { setErr("Incorrect email or password."); return; }

            // Fetch the user's own staff record (RLS only returns their own row).
            // First login of an unlinked account: link it by verified e-mail on the server.
            let staffData = await db.getOwnStaff(data.user.id).catch(() => []);
            if (!Array.isArray(staffData) || staffData.length === 0) {
                await rpc("claim_staff_record").catch(() => null);
                staffData = await db.getOwnStaff(data.user.id).catch(() => []);
            }
            if (!Array.isArray(staffData) || staffData.length === 0) {
                await supabase.auth.signOut();
                setErr("No staff account linked to this email. Contact your administrator.");
                return;
            }
            const staffRecord = staffData[0];

            // Remember only the email, never the password
            if (remember) localStorage.setItem("foxbeep_remember", JSON.stringify({ email }));
            else localStorage.removeItem("foxbeep_remember");

            await db.updateStaff(staffRecord.id, { last_login: new Date().toISOString() });
            onLogin({ ...staffRecord, role: staffRecord.role || "staff" });
        } finally { setLoading(false); }
    };

    return (
        <div style={{ ...styles.app, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
            <div style={{ width: mobile ? "92vw" : 420, maxWidth: "100%" }}>
                <div style={{ textAlign: "center", marginBottom: 40 }}>
                    <img src="/logo.png" alt="Foxbeep" style={{ width: 80, height: 80, borderRadius: 20, objectFit: "cover", marginBottom: 10 }} />
                    <h1 style={{ margin: 0, fontSize: 32, fontWeight: 900, color: C.white, letterSpacing: -1 }}>Foxbeep</h1>
                    <p style={{ color: C.muted, margin: "6px 0 0", fontSize: 15 }}>Staff Management System</p>
                </div>
                <div style={styles.card}>
                    <h2 style={{ margin: "0 0 24px", fontSize: 18, fontWeight: 700 }}>Sign In</h2>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>EMAIL</label>
                        <input style={styles.input} value={email} onChange={e => setEmail(e.target.value)} placeholder="you@foxbeep.com" type="email" autoComplete="email" />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PASSWORD</label>
                        <div style={{ position: "relative" }}>
                            <input style={{ ...styles.input, paddingRight: 44 }} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" type={showPass ? "text" : "password"} autoComplete="current-password" onKeyDown={e => e.key === "Enter" && doLogin()} />
                            <span onClick={() => setShowPass(v => !v)} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", cursor: "pointer", fontSize: 16, color: C.muted }}>{showPass ? "🙈" : "👁️"}</span>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                        <input id="remember" type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ width: 16, height: 16, accentColor: C.accent, cursor: "pointer" }} />
                        <label htmlFor="remember" style={{ fontSize: 13, color: C.muted, cursor: "pointer" }}>Remember me</label>
                    </div>
                    {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 14, padding: "10px 12px", background: C.red + "11", borderRadius: 8, border: `1px solid ${C.red}33` }}>⚠️ {err}</div>}
                    <button style={{ ...styles.btn(), width: "100%", padding: 13, fontSize: 15, opacity: loading ? 0.7 : 1 }} onClick={doLogin} disabled={loading}>
                        {loading ? "Signing in..." : "Sign In →"}
                    </button>
                </div>
            </div>
            <div style={{ textAlign: "center", marginTop: 24, color: C.muted, fontSize: 12 }}>
                Made with ❤️ by <a href="https://foxbeep.com" target="_blank" rel="noreferrer" style={{ color: C.accent, textDecoration: "none", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}><img src="/foxbeep-logo-triangle.png" alt="Foxbeep" style={{ height: 14, width: "auto" }} />Foxbeep Team</a>
            </div>
        </div>
    );
}

// ─── Admin Dashboard ──────────────────────────────────────────────────────────────
function BirthdayBanner() {
    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const birthdays = _staff.filter(s => {
        const md = s.birthday_md || (s.dob ? s.dob.slice(5, 10) : null);
        return md === `${mm}-${dd}`;
    });
    if (birthdays.length === 0) return null;
    return (
        <div style={{ ...styles.card, marginTop: 16, background: "linear-gradient(135deg,#f59e0b22,#ec489922)", border: "1px solid #f59e0b44", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 6 }}>🎂</div>
            <div style={{ fontWeight: 900, color: C.white, fontSize: 16 }}>Happy Birthday{birthdays.length > 1 ? "" : ""}!</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
                {birthdays.map(s => s.name).join(", ")} {birthdays.length === 1 ? "is" : "are"} celebrating today! 🎉
            </div>
        </div>
    );
}

function MoodAdminView() {
    const today = todayStr();
    const todayMoods = _moods.filter(m => m.date === today);
    if (todayMoods.length === 0) return <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 24 }}>No mood check-ins today yet.</div>;
    const moodCount = {};
    todayMoods.forEach(m => { moodCount[m.mood] = (moodCount[m.mood] || 0) + 1; });
    return (
        <div style={styles.card}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.white, marginBottom: 12 }}>😊 Team Mood Today — {todayMoods.length} check-in{todayMoods.length !== 1 ? "s" : ""}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
                {Object.entries(moodCount).map(([emoji, count]) => (
                    <div key={emoji} style={{ display: "flex", alignItems: "center", gap: 6, background: C.card2, borderRadius: 10, padding: "6px 12px" }}>
                        <span style={{ fontSize: 22 }}>{emoji}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: C.white }}>{count}</span>
                    </div>
                ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {todayMoods.map(m => {
                    const s = _staff.find(st => st.id === m.staff_id);
                    return (
                        <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 6, background: C.card2, borderRadius: 8, padding: "4px 10px", fontSize: 12, color: C.muted }}>
                            {s?.profile_image ? <img src={s.profile_image} alt="" style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover" }} /> : <span style={{ width: 20, height: 20, borderRadius: "50%", background: C.border, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>{s?.name?.charAt(0)}</span>}
                            {s?.name || "?"} {m.mood}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function AdminDashboard({ reload }) {
    const mobile = useMobile();
    const today = todayStr();
    const isHoliday = _holidays.includes(today);
    const isSat = isSaturday(today);
    const presentToday = _staff.filter(s => { const st = getStatusForDate(s.id, today); return st === "present" || st === "saturday" || st === "holiday"; }).length;
    const absentToday = _staff.filter(s => { const st = getStatusForDate(s.id, today); return st === "absent" || st === "auto_absent" || st === "not_marked"; }).length;

    return (
        <div>
            <h2 style={{ margin: "0 0 6px", fontWeight: 800, fontSize: 22, color: C.white }}>Dashboard</h2>
            <p style={{ margin: "0 0 28px", color: C.muted }}>{fmtDate(today)}{isHoliday ? " • 🎉 Holiday" : ""}{isSat ? " • 📅 Saturday" : ""}</p>
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 16 }}>
                <StatCard icon="👥" label="Total Staff" value={_staff.length} color={C.accent} />
                <StatCard icon="✅" label="Present Today" value={presentToday} color={C.green} />
                <StatCard icon="❌" label="Absent Today" value={absentToday} color={C.red} />
                <StatCard icon="🎉" label="Holidays Set" value={_holidays.length} color={C.yellow} />
            </div>
            <div style={{ marginTop: 28 }}>
                <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Today's Attendance</h3>
                <div style={styles.card}>
                    {mobile ? (
                        <div>
                            {_staff.map(s => {
                                const rec = getAttendanceRecord(s.id, today);
                                const status = getStatusForDate(s.id, today);
                                return (
                                    <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                                        <div>
                                            <div style={{ fontWeight: 700, color: C.white, fontSize: 14 }}>{s.name}</div>
                                            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{s.designation}</div>
                                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{rec?.clock_in ? `In: ${rec.clock_in}` : ""}{rec?.clock_out ? ` • Out: ${rec.clock_out}` : rec?.clock_in ? " • Active" : ""}</div>
                                        </div>
                                        <StatusBadge status={status} />
                                    </div>
                                );
                            })}
                            {_staff.length === 0 && <div style={{ textAlign: "center", color: C.muted, padding: 20 }}>No staff added yet.</div>}
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}><table style={{ ...styles.table, minWidth: 500 }}>
                            <thead><tr><th style={styles.th}>Staff</th><th style={styles.th}>Designation</th><th style={styles.th}>Clock In</th><th style={styles.th}>Clock Out</th><th style={styles.th}>Status</th></tr></thead>
                            <tbody>
                                {_staff.map(s => {
                                    const rec = getAttendanceRecord(s.id, today);
                                    const status = getStatusForDate(s.id, today);
                                    return (
                                        <tr key={s.id}>
                                            <td style={styles.td}><strong style={{ color: C.white }}>{s.name}</strong></td>
                                            <td style={styles.td}><span style={{ color: C.muted }}>{s.designation}</span></td>
                                            <td style={styles.td}>{rec?.clock_in || "—"}</td>
                                            <td style={styles.td}>{rec ? (rec.clock_out || <span style={{ color: C.green, fontSize: 12 }}>Active</span>) : "—"}</td>
                                            <td style={styles.td}><StatusBadge status={status} /></td>
                                        </tr>
                                    );
                                })}
                                {_staff.length === 0 && <tr><td colSpan={5} style={{ ...styles.td, textAlign: "center", color: C.muted }}>No staff added yet.</td></tr>}
                            </tbody>
                        </table></div>
                    )}
                </div>
            </div>
        </div>
    );
}


// ─── Moderator Dashboard ──────────────────────────────────────────────────────
function ModeratorDashboard({ user, reload, isModStaff = false, isModFreelancer = false }) {
    const mobile = useMobile();
    const today = todayStr();
    const isHoliday = _holidays.includes(today);
    const isSat = isSaturday(today);
    const myStaff = _staff.filter(s => {
        if (isModStaff && isModFreelancer) return true;
        if (isModStaff) return s.employment_type !== "freelancer";
        if (isModFreelancer) return s.employment_type === "freelancer";
        return true;
    });
    const presentToday = myStaff.filter(s => { const st = getStatusForDate(s.id, today); return st === "present" || st === "saturday" || st === "holiday"; }).length;
    const absentToday = myStaff.filter(s => { const st = getStatusForDate(s.id, today); return st === "absent" || st === "auto_absent" || st === "not_marked"; }).length;
    const onlineCount = myStaff.filter(s => isOnline(s.last_login)).length;
    const staffInfo = _staff.find(s => s.id === user.id);

    const OnlineDot = ({ last_login }) => (
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: isOnline(last_login) ? C.green : "#555", display: "inline-block", marginRight: 6, flexShrink: 0, boxShadow: isOnline(last_login) ? `0 0 5px ${C.green}` : "none" }} />
    );

    return (
        <div>
            <h2 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 22, color: C.white }}>Welcome, {user.name.split(" ")[0]} 👋</h2>
            <p style={{ margin: "0 0 6px", color: C.muted }}>{staffInfo?.designation} • {fmtDate(today)}{isHoliday ? " • 🎉 Holiday" : ""}{isSat ? " • 📅 Saturday" : ""}</p>
            <div style={{ marginBottom: 24, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {isModStaff && <span style={{ background: C.green + "33", color: C.green, borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>🛡 Staff Moderator</span>}
                {isModFreelancer && <span style={{ background: C.purple + "33", color: C.purple, borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>🟣 Freelancer Moderator</span>}
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4,1fr)", gap: mobile ? 10 : 16, marginBottom: 28 }}>
                <StatCard icon="👥" label="Visible Staff" value={myStaff.length} color={C.accent} />
                <StatCard icon="✅" label="Present Today" value={presentToday} color={C.green} />
                <StatCard icon="❌" label="Absent Today" value={absentToday} color={C.red} />
                <StatCard icon="🟢" label="Online Now" value={onlineCount} color={C.green} sub="active in last 5 min" />
            </div>

            {/* Today's Attendance */}
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Today's Attendance</h3>
            <div style={styles.card}>
                {mobile ? (
                    <div>
                        {myStaff.map(s => {
                            const rec = getAttendanceRecord(s.id, today);
                            const status = getStatusForDate(s.id, today);
                            return (
                                <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <OnlineDot last_login={s.last_login} />
                                        <div>
                                            <div style={{ fontWeight: 700, color: C.white, fontSize: 14 }}>{s.name}</div>
                                            <div style={{ fontSize: 12, color: C.muted }}>{s.designation}</div>
                                            <div style={{ fontSize: 11, color: C.muted }}>{rec?.clock_in ? `In: ${rec.clock_in}` : ""}{rec?.clock_out ? ` • Out: ${rec.clock_out}` : rec?.clock_in ? " • Active" : ""}</div>
                                        </div>
                                    </div>
                                    <StatusBadge status={status} />
                                </div>
                            );
                        })}
                        {myStaff.length === 0 && <div style={{ textAlign: "center", color: C.muted, padding: 20 }}>No staff in your scope.</div>}
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}><table style={{ ...styles.table, minWidth: 500 }}>
                        <thead><tr><th style={styles.th}>Staff</th><th style={styles.th}>Designation</th><th style={styles.th}>Clock In</th><th style={styles.th}>Clock Out</th><th style={styles.th}>Online</th><th style={styles.th}>Status</th></tr></thead>
                        <tbody>
                            {myStaff.map(s => {
                                const rec = getAttendanceRecord(s.id, today);
                                const status = getStatusForDate(s.id, today);
                                return (
                                    <tr key={s.id}>
                                        <td style={styles.td}><strong style={{ color: C.white }}>{s.name}</strong></td>
                                        <td style={styles.td}><span style={{ color: C.muted }}>{s.designation}</span></td>
                                        <td style={styles.td}>{rec?.clock_in || "—"}</td>
                                        <td style={styles.td}>{rec ? (rec.clock_out || <span style={{ color: C.green, fontSize: 12 }}>Active</span>) : "—"}</td>
                                        <td style={styles.td}><span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: isOnline(s.last_login) ? C.green : "#555", boxShadow: isOnline(s.last_login) ? `0 0 5px ${C.green}` : "none" }} />{isOnline(s.last_login) ? <span style={{ color: C.green, fontWeight: 700 }}>Online</span> : <span style={{ color: C.muted }}>Offline</span>}</span></td>
                                        <td style={styles.td}><StatusBadge status={status} /></td>
                                    </tr>
                                );
                            })}
                            {myStaff.length === 0 && <tr><td colSpan={6} style={{ ...styles.td, textAlign: "center", color: C.muted }}>No staff in your scope.</td></tr>}
                        </tbody>
                    </table></div>
                )}
            </div>
            {/* Birthday banner */}
            <BirthdayBanner />
            {/* SOTM */}
            <div style={{ marginTop: 16 }}><StaffOfMonth isAdmin={true} reload={reload} /></div>
            {/* Team mood */}
            <div style={{ marginTop: 4 }}><div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>😊 TEAM MOOD TODAY</div><MoodAdminView /></div>
        </div>
    );
}

// ─── Admin: Manage Staff ──────────────────────────────────────────────────────────
function AdminManageStaff({ reload }) {
    const mobile = useMobile();
    const [showAdd, setShowAdd] = useState(false);
    const [editStaff, setEditStaff] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", designation: "", salary: "", join_date: todayStr(), employment_type: "full_time", mod_staff: false, mod_freelancer: false, allow_remote: false });
    const [formPw, setFormPw] = useState("");
    const [showFormPw, setShowFormPw] = useState(false);
    const [saving, setSaving] = useState(false);
    const [salaryFor, setSalaryFor] = useState(null); // staff whose salary is being changed (effective-dated)
    const [pwStaff, setPwStaff] = useState(null); // staff object whose password is being set
    const [newPw, setNewPw] = useState("");
    const [pwSaving, setPwSaving] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const resetForm = () => { setForm({ name: "", email: "", designation: "", salary: "", join_date: todayStr(), employment_type: "full_time", mod_staff: false, mod_freelancer: false }); setFormPw(""); setShowFormPw(false); };

    const saveStaff = async () => {
        if (!form.name || !form.email || !form.designation || !form.salary) return;
        if (!editStaff && formPw && formPw.length < 6) { showToast("Password must be at least 6 characters.", C.red); return; }
        setSaving(true);
        try {
            const { password: _pw, ...staffData } = { ...form, salary: Number(form.salary), employment_type: form.employment_type || "full_time" };
            if (editStaff) {
                // Salary is effective-dated: it changes only through "Update salary".
                const { salary: _salary, ...profileData } = staffData;
                await db.updateStaff(editStaff.id, profileData);
                await reload(); setShowAdd(false); setEditStaff(null); resetForm();
                showToast("Staff member updated!");
            } else {
                let auth_id = null;
                if (formPw) {
                    // Create Supabase Auth user with email + password (server-side, admin only)
                    try {
                        const authData = await callFunction("admin-users", { action: "create_user", email: form.email, password: formPw });
                        auth_id = authData.id;
                    } catch (e) {
                        showToast(e.message || "Failed to create auth account.", C.red);
                        setSaving(false); return;
                    }
                }
                const newId = "s" + Date.now();
                await db.addStaff({ id: newId, ...staffData, ...(auth_id ? { auth_id } : {}) });
                if (_salaryHistoryAvailable && staffData.salary > 0 && staffData.employment_type !== "freelancer") {
                    // Starting salary becomes the first effective-dated salary record.
                    await setStaffSalary(newId, staffData.salary, staffData.join_date, "Starting salary").catch(e => showToast(`Staff added, but starting salary was not recorded: ${e.message}`, C.red));
                }
                await reload(); setShowAdd(false); setEditStaff(null); resetForm();
                showToast(auth_id ? "Staff member added with login account!" : "Staff member added! (No password set — use 🔑 to set one later)");
            }
        } finally { setSaving(false); }
    };

    const deleteStaff = async (id) => { await db.deleteStaff(id); await reload(); showToast("Staff member removed.", "#ef4444"); };


    const doSetPassword = async () => {
        if (!pwStaff) return;
        if (!newPw || newPw.length < 6) { showToast("Password must be at least 6 characters.", C.red); return; }
        if (!pwStaff.auth_id) { showToast("No auth account found for this staff. They must log in at least once first.", C.red); return; }
        setPwSaving(true);
        try {
            await callFunction("admin-users", { action: "set_password", auth_id: pwStaff.auth_id, password: newPw });
            showToast(`Password updated for ${pwStaff.name}!`, C.green);
            setPwStaff(null); setNewPw(""); setShowPw(false);
        } catch (e) {
            showToast(e.message || "Failed to update password.", C.red);
        } finally {
            setPwSaving(false);
        }
    };

    return (
        <div>
            <div style={{ display: "flex", alignItems: mobile ? "flex-start" : "center", flexDirection: mobile ? "column" : "row", justifyContent: "space-between", marginBottom: 24, gap: 12 }}>
                <h2 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>Manage Staff</h2>
                <button style={styles.btn()} onClick={() => { setShowAdd(true); setEditStaff(null); resetForm(); }}>+ Add Staff</button>
            </div>
            {pwStaff && (
                <div style={{ ...styles.card, marginBottom: 24, borderColor: C.accent2 + "66" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>🔑 Set Password</h3>
                            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted }}>Setting new password for <strong style={{ color: C.white }}>{pwStaff.name}</strong> ({pwStaff.email})</p>
                        </div>
                        <button style={styles.btnOut} onClick={() => { setPwStaff(null); setNewPw(""); setShowPw(false); }}>✕</button>
                    </div>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
                            <input
                                style={{ ...styles.input, paddingRight: 44 }}
                                type={showPw ? "text" : "password"}
                                value={newPw}
                                onChange={e => setNewPw(e.target.value)}
                                placeholder="New password (min 6 chars)"
                                onKeyDown={e => e.key === "Enter" && doSetPassword()}
                                autoFocus
                            />
                            <button
                                onClick={() => setShowPw(p => !p)}
                                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 16, padding: 4 }}
                            >{showPw ? "🙈" : "👁"}</button>
                        </div>
                        <button style={styles.btn(C.accent2)} onClick={doSetPassword} disabled={pwSaving}>
                            {pwSaving ? "Saving..." : "Update Password"}
                        </button>
                    </div>
                    {!pwStaff.auth_id && <p style={{ margin: "10px 0 0", fontSize: 12, color: C.red }}>⚠ This staff member has no auth account yet — they must log in at least once before you can set their password.</p>}
                </div>
            )}
            {(showAdd || editStaff) && (
                <div style={{ ...styles.card, marginBottom: 24 }}>
                    <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700 }}>{editStaff ? "Edit Staff" : "New Staff Member"}</h3>
                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 20 }}>
                        {[["name", "Full Name", "text"], ["email", "Email", "email"], ["designation", "Designation", "text"], ["salary", "Monthly Salary", "number"], ["join_date", "Join Date", "date"]].map(([key, label, type]) => (
                            <div key={key}>
                                <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>{label.toUpperCase()}</label>
                                <input style={styles.input} type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={label}
                                    disabled={key === "salary" && !!editStaff} title={key === "salary" && editStaff ? "Use Update salary to change it with an effective date" : undefined} />
                                {key === "salary" && editStaff && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Change salary with "Update" in the staff list — it asks for an effective date.</div>}
                            </div>
                        ))}
                        {!editStaff && (
                            <div>
                                <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PASSWORD <span style={{ color: C.muted, fontWeight: 400, textTransform: "none" }}>(optional — set login password now)</span></label>
                                <div style={{ position: "relative" }}>
                                    <input
                                        style={{ ...styles.input, paddingRight: 44 }}
                                        type={showFormPw ? "text" : "password"}
                                        value={formPw}
                                        onChange={e => setFormPw(e.target.value)}
                                        placeholder="Min 6 characters"
                                    />
                                    <button onClick={() => setShowFormPw(p => !p)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 16, padding: 4 }}>{showFormPw ? "🙈" : "👁"}</button>
                                </div>
                                {formPw && formPw.length < 6 && <p style={{ margin: "4px 0 0", fontSize: 11, color: C.red }}>At least 6 characters required</p>}
                                {!formPw && <p style={{ margin: "4px 0 0", fontSize: 11, color: C.muted }}>Leave blank — you can set it later using the 🔑 button</p>}
                            </div>
                        )}
                        <div>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>EMPLOYMENT TYPE</label>
                            <select style={styles.input} value={form.employment_type} onChange={e => setForm(f => ({ ...f, employment_type: e.target.value }))}>
                                <option value="full_time">Full Time</option>
                                <option value="part_time">Part Time</option>
                                <option value="freelancer">Freelancer</option>
                            </select>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gridColumn: "1 / -1" }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 10 }}>MODERATOR ACCESS</label>
                            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 14px", borderRadius: 10, border: `1px solid ${form.mod_staff ? C.green : C.border}`, background: form.mod_staff ? C.green + "11" : C.card2 }}>
                                    <div onClick={() => setForm(f => ({ ...f, mod_staff: !f.mod_staff }))} style={{ width: 38, height: 22, borderRadius: 11, background: form.mod_staff ? C.green : C.border, position: "relative", transition: "background 0.2s", cursor: "pointer", flexShrink: 0 }}>
                                        <div style={{ width: 16, height: 16, borderRadius: "50%", background: C.white, position: "absolute", top: 3, left: form.mod_staff ? 19 : 3, transition: "left 0.2s" }} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 13, color: form.mod_staff ? C.green : C.muted, fontWeight: 700 }}>🛡 Staff Moderator</div>
                                        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>View reports of all staff</div>
                                    </div>
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 14px", borderRadius: 10, border: `1px solid ${form.mod_freelancer ? C.purple : C.border}`, background: form.mod_freelancer ? C.purple + "11" : C.card2 }}>
                                    <div onClick={() => setForm(f => ({ ...f, mod_freelancer: !f.mod_freelancer }))} style={{ width: 38, height: 22, borderRadius: 11, background: form.mod_freelancer ? C.purple : C.border, position: "relative", transition: "background 0.2s", cursor: "pointer", flexShrink: 0 }}>
                                        <div style={{ width: 16, height: 16, borderRadius: "50%", background: C.white, position: "absolute", top: 3, left: form.mod_freelancer ? 19 : 3, transition: "left 0.2s" }} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 13, color: form.mod_freelancer ? C.purple : C.muted, fontWeight: 700 }}>🟣 Freelancer Moderator</div>
                                        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>View reports of freelancers only</div>
                                    </div>
                                </label>
                            </div>
                            {(form.mod_staff && form.mod_freelancer) && <div style={{ marginTop: 8, fontSize: 12, color: C.yellow }}>⚡ Both enabled — can view all staff & freelancer reports</div>}
                        </div>
                        <div style={{ gridColumn: "1 / -1" }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 10 }}>CLOCK-IN PERMISSION</label>
                            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 14px", borderRadius: 10, border: `1px solid ${form.allow_remote ? C.accent : C.border}`, background: form.allow_remote ? C.accent + "11" : C.card2, width: "fit-content" }}>
                                <div onClick={() => setForm(f => ({ ...f, allow_remote: !f.allow_remote }))} style={{ width: 38, height: 22, borderRadius: 11, background: form.allow_remote ? C.accent : C.border, position: "relative", transition: "background 0.2s", cursor: "pointer", flexShrink: 0 }}>
                                    <div style={{ width: 16, height: 16, borderRadius: "50%", background: C.white, position: "absolute", top: 3, left: form.allow_remote ? 19 : 3, transition: "left 0.2s" }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 13, color: form.allow_remote ? C.accent : C.muted, fontWeight: 700 }}>🌍 Allow Remote Clock-In</div>
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>Bypass GPS/WiFi check — staff can clock in from anywhere</div>
                                </div>
                            </label>
                        </div>
                    </div>
                    <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
                        <button style={styles.btn(C.green)} onClick={saveStaff} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
                        <button style={styles.btnOut} onClick={() => { setShowAdd(false); setEditStaff(null); }}>Cancel</button>
                    </div>
                </div>
            )}
            <div style={styles.card}>
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}><table style={{ ...styles.table, minWidth: 480 }}>
                    <thead><tr>
                        <th style={styles.th}>Name</th><th style={styles.th}>Type</th><th style={styles.th}>Designation</th><th style={styles.th}>Email</th>
                        <th style={styles.th}>Join Date</th><th style={styles.th}>Last Login</th><th style={styles.th}>Salary (Rs.)</th><th style={styles.th}>Actions</th>
                    </tr></thead>
                    <tbody>
                        {_staff.map(s => {
                            const etColor = s.employment_type === "freelancer" ? C.purple : s.employment_type === "part_time" ? C.yellow : C.green;
                            const etLabel = s.employment_type === "freelancer" ? "Freelancer" : s.employment_type === "part_time" ? "Part Time" : "Full Time";
                            return (
                                <tr key={s.id}>
                                    <td style={styles.td}><strong style={{ color: C.white }}>{s.name}</strong></td>
                                    <td style={styles.td}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                            <span style={styles.badge(etColor)}>{etLabel}</span>
                                            {s.mod_staff && <span style={styles.badge(C.green)}>🛡 Staff Mod</span>}
                                            {s.mod_freelancer && <span style={styles.badge(C.purple)}>🟣 FL Mod</span>}
                                        </div>
                                    </td>
                                    <td style={styles.td}><span style={styles.badge(C.accent2)}>{s.designation}</span></td>
                                    <td style={styles.td}><span style={{ color: C.muted }}>{s.email}</span></td>
                                    <td style={styles.td}>{fmtDate(s.join_date)}</td>
                                    <td style={{ ...styles.td, fontSize: 12, color: C.muted }}>{s.last_login ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: isOnline(s.last_login) ? C.green : C.red, flexShrink: 0, boxShadow: isOnline(s.last_login) ? `0 0 6px ${C.green}` : "none" }} />{timeAgoLocal(s.last_login)}</span> : <span style={{ color: C.red + "99" }}>Never</span>}</td>
                                    <td style={styles.td}>
                                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                            <span style={{ fontWeight: 600, minWidth: 90 }}>Rs.{currentSalaryOf(s.id).toLocaleString()}</span>
                                            <button style={{ ...styles.btnOut, padding: "6px 12px" }} onClick={() => setSalaryFor(s)}>Update</button>
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            <button style={styles.btn(C.accent2)} onClick={() => { setEditStaff(s); setForm({ name: s.name, email: s.email, designation: s.designation, salary: s.salary, join_date: s.join_date, employment_type: s.employment_type || "full_time", mod_staff: s.mod_staff || false, mod_freelancer: s.mod_freelancer || false }); setShowAdd(true); }}>Edit</button>
                                            <button style={styles.btn("#7c3aed")} onClick={() => { setPwStaff(s); setNewPw(""); setShowPw(false); setShowAdd(false); setEditStaff(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}>🔑</button>
                                            <button style={styles.btn(C.red)} onClick={() => deleteStaff(s.id)}>Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {_staff.length === 0 && <tr><td colSpan={8} style={{ ...styles.td, textAlign: "center", color: C.muted }}>No staff yet. Add your first staff member above.</td></tr>}
                    </tbody>
                </table></div>
            </div>
            <UpdateSalaryModal open={!!salaryFor} staff={salaryFor} rows={salaryFor ? salaryRowsFor(salaryFor.id) : []}
                lockedUntil={salaryFor ? lastFinalizedDay(_salaryPayments, salaryFor.id) : null} today={todayStr()}
                onClose={() => setSalaryFor(null)} onSaved={reload} />
        </div>
    );
}

// ─── Admin: Holidays ──────────────────────────────────────────────────────────────
function AdminHolidays({ reload }) {
    const mobile = useMobile();
    const [selDate, setSelDate] = useState(todayStr());
    const [remarkText, setRemarkText] = useState("");
    const [viewYear, setViewYear] = useState(new Date().getFullYear());
    const [viewMonth, setViewMonth] = useState(new Date().getMonth());
    const [saving, setSaving] = useState(false);
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    const addHoliday = async () => {
        if (!selDate || _holidays.includes(selDate)) return;
        setSaving(true);
        try { await db.addHoliday(selDate, remarkText || "Holiday"); await reload(); setRemarkText(""); showToast("Holiday added!"); } finally { setSaving(false); }
    };
    const removeHoliday = async (d) => { await db.deleteHoliday(d); await reload(); showToast("Holiday removed.", "#ef4444"); };

    return (
        <div>
            <h2 style={{ margin: "0 0 24px", fontWeight: 800, fontSize: 22 }}>Holiday Calendar</h2>
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 20 }}>
                <div style={styles.card}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                        <button style={styles.btnOut} onClick={() => { if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); } else setViewMonth(m => m - 1); }}>‹</button>
                        <strong style={{ fontSize: 16 }}>{monthName(viewMonth)} {viewYear}</strong>
                        <button style={styles.btnOut} onClick={() => { if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); } else setViewMonth(m => m + 1); }}>›</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => <div key={d} style={{ textAlign: "center", fontSize: 11, color: d === "Su" ? C.purple : C.muted, fontWeight: 700, padding: "4px 0" }}>{d}</div>)}
                        {Array.from({ length: firstDay }).map((_, i) => <div key={"e" + i} />)}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const dateStr = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
                            const isHol = _holidays.includes(dateStr);
                            const isSat = new Date(dateStr).getDay() === 6;
                            const isSun = new Date(dateStr).getDay() === 0;
                            const isToday = dateStr === todayStr();
                            return (
                                <div key={day} onClick={() => setSelDate(dateStr)}
                                    title={isHol ? (_holidayRecords.find(h => h.date === dateStr)?.name || "Holiday") : ""}
                                    style={{
                                        textAlign: "center", padding: "8px 4px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: isToday ? 800 : 500,
                                        background: isHol ? C.yellow + "33" : selDate === dateStr ? C.accent + "33" : "transparent",
                                        color: isHol ? C.yellow : isSun ? C.purple : isSat ? C.accent : C.text,
                                        border: isToday ? `2px solid ${C.accent}` : "2px solid transparent"
                                    }}>
                                    {day}
                                    {isHol && <div style={{ width: 4, height: 4, background: C.yellow, borderRadius: "50%", margin: "2px auto 0" }} />}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <div style={{ ...styles.card, marginBottom: 16 }}>
                        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Mark Holiday</h3>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>SELECTED DATE</label>
                            <input style={styles.input} type="date" value={selDate} onChange={e => setSelDate(e.target.value)} />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>REASON / NAME</label>
                            <input style={styles.input} type="text" value={remarkText} onChange={e => setRemarkText(e.target.value)} placeholder="e.g. Christmas, Eid, New Year..." />
                        </div>
                        <button style={styles.btn(C.yellow)} onClick={addHoliday} disabled={saving}>{saving ? "Saving..." : "Mark as Holiday"}</button>
                    </div>
                    <div style={styles.card}>
                        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Holidays ({_holidays.length})</h3>
                        <div style={{ maxHeight: 220, overflowY: "auto" }}>
                            {_holidays.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>No holidays set.</div>}
                            {[..._holidayRecords].sort((a, b) => a.date > b.date ? 1 : -1).map(h => (
                                <div key={h.date} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                                    <div>
                                        <div style={{ fontSize: 14, color: C.white, fontWeight: 600 }}>{h.name || "Holiday"}</div>
                                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{fmtDate(h.date)}</div>
                                    </div>
                                    <button style={{ ...styles.btn(C.red), padding: "4px 10px", fontSize: 12 }} onClick={() => removeHoliday(h.date)}>Remove</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Admin: Edit Attendance ───────────────────────────────────────────────────────
function AdminEditAttendance({ reload }) {
    const mobile = useMobile();
    const now = new Date();
    const [selStaff, setSelStaff] = useState("");
    const [viewYear, setViewYear] = useState(now.getFullYear());
    const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
    const [editModal, setEditModal] = useState(null);
    const [editForm, setEditForm] = useState({ status: "present", clockIn: "09:00", clockOut: "18:00" });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => { if (_staff.length > 0 && !selStaff) setSelStaff(_staff[0].id); }, [selStaff]);

    const startDate = `${viewYear}-${pad(viewMonth)}-01`;
    const lastDay = new Date(viewYear, viewMonth, 0).getDate();
    const endDate = `${viewYear}-${pad(viewMonth)}-${pad(lastDay)}`;
    const days = daysInRange(startDate, endDate);

    const openEdit = (date) => {
        const rec = getAttendanceRecord(selStaff, date);
        const status = getStatusForDate(selStaff, date);
        setEditForm({ status: rec ? rec.status : (status === "absent" ? "absent" : "present"), clockIn: rec?.clock_in || "09:00", clockOut: rec?.clock_out || "18:00" });
        setEditModal({ staffId: selStaff, date });
        setSaved(false);
    };

    const saveEdit = async () => {
        const { staffId, date } = editModal;
        setSaving(true);
        try {
            const id = `${staffId}_${date}`;
            if (editForm.status === "absent") {
                await db.upsertAttendance({ id, staff_id: staffId, date, clock_in: null, clock_in_ts: null, clock_out: null, status: "absent" });
            } else {
                await db.upsertAttendance({ id, staff_id: staffId, date, clock_in: editForm.clockIn, clock_in_ts: Date.now(), clock_out: editForm.clockOut, status: "present" });
            }
            await reload(); setSaved(true);
            setTimeout(() => { setEditModal(null); setSaved(false); }, 800);
        } finally { setSaving(false); }
    };

    const prevMonth = () => { if (viewMonth === 1) { setViewMonth(12); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
    const nextMonth = () => { if (viewMonth === 12) { setViewMonth(1); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };

    return (
        <div>
            <h2 style={{ margin: "0 0 6px", fontWeight: 800, fontSize: 22 }}>Edit Attendance</h2>
            <p style={{ margin: "0 0 24px", color: C.muted }}>Override any staff member's attendance, clock-in or clock-out time for any date.</p>
            <div style={{ ...styles.card, marginBottom: 24 }}>
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <div style={{ minWidth: 220 }}>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>STAFF MEMBER</label>
                        <select style={styles.input} value={selStaff} onChange={e => setSelStaff(e.target.value)}>
                            {_staff.length === 0 && <option value="">No staff added yet</option>}
                            {_staff.map(s => <option key={s.id} value={s.id}>{s.name} — {s.designation}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>MONTH</label>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <button style={styles.btnOut} onClick={prevMonth}>‹</button>
                            <span style={{ fontWeight: 700, fontSize: 15, minWidth: 120, textAlign: "center" }}>{monthName(viewMonth - 1)} {viewYear}</span>
                            <button style={styles.btnOut} onClick={nextMonth}>›</button>
                        </div>
                    </div>
                </div>
            </div>
            {selStaff && (
                <div style={styles.card}>
                    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}><table style={{ ...styles.table, minWidth: 480 }}>
                        <thead><tr>
                            <th style={styles.th}>Date</th><th style={styles.th}>Day</th>
                            <th style={styles.th}>Clock In</th><th style={styles.th}>Clock Out</th>
                            <th style={styles.th}>Status</th><th style={styles.th}>Action</th>
                        </tr></thead>
                        <tbody>
                            {days.map(d => {
                                const rec = getAttendanceRecord(selStaff, d);
                                const status = getStatusForDate(selStaff, d);
                                const isEditable = status !== "upcoming";
                                const dayNum = new Date(d).getDay();
                                return (
                                    <tr key={d} style={{ opacity: status === "upcoming" ? 0.4 : 1 }}>
                                        <td style={styles.td}><strong style={{ color: C.white }}>{fmtDate(d)}</strong></td>
                                        <td style={{ ...styles.td, color: dayNum === 0 ? C.purple : dayNum === 6 ? C.accent : C.muted }}>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dayNum]}</td>
                                        <td style={styles.td}>{rec?.clock_in || "—"}</td>
                                        <td style={styles.td}>{rec?.clock_out || "—"}</td>
                                        <td style={styles.td}><StatusBadge status={status} /></td>
                                        <td style={styles.td}>
                                            {isEditable
                                                ? <button style={{ ...styles.btn(C.accent2), padding: "6px 14px", fontSize: 12 }} onClick={() => openEdit(d)}>✏️ Edit</button>
                                                : <span style={{ color: C.muted, fontSize: 12 }}>—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table></div>
                </div>
            )}

            {editModal && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                    <div style={{ ...styles.card, width: 420 }}>
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>EDITING ATTENDANCE</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: C.white }}>{_staff.find(s => s.id === editModal.staffId)?.name}</div>
                            <div style={{ color: C.muted, fontSize: 14 }}>{fmtDate(editModal.date)}</div>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 8 }}>STATUS</label>
                            <div style={{ display: "flex", gap: 10 }}>
                                {["present", "absent"].map(s => (
                                    <div key={s} onClick={() => setEditForm(f => ({ ...f, status: s }))}
                                        style={{ flex: 1, padding: 12, borderRadius: 10, border: `2px solid ${editForm.status === s ? (s === "present" ? C.green : C.red) : C.border}`, background: editForm.status === s ? (s === "present" ? C.green : C.red) + "22" : "transparent", cursor: "pointer", textAlign: "center", fontWeight: 700, fontSize: 14, color: editForm.status === s ? (s === "present" ? C.green : C.red) : C.muted, transition: "all .15s" }}>
                                        {s === "present" ? "✅ Present" : "❌ Absent"}
                                    </div>
                                ))}
                            </div>
                        </div>
                        {editForm.status === "present" && (
                            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 20, marginBottom: 16 }}>
                                <div>
                                    <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>CLOCK IN</label>
                                    <input style={styles.input} type="time" value={editForm.clockIn} onChange={e => setEditForm(f => ({ ...f, clockIn: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>CLOCK OUT</label>
                                    <input style={styles.input} type="time" value={editForm.clockOut} onChange={e => setEditForm(f => ({ ...f, clockOut: e.target.value }))} />
                                </div>
                            </div>
                        )}
                        {saved && <div style={{ marginBottom: 16, padding: "10px 14px", background: C.green + "22", border: `1px solid ${C.green}44`, borderRadius: 10, color: C.green, fontSize: 13, fontWeight: 600 }}>✅ Saved to database!</div>}
                        <div style={{ display: "flex", gap: 12 }}>
                            <button style={{ ...styles.btn(C.green), flex: 1 }} onClick={saveEdit} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
                            <button style={{ ...styles.btnOut, flex: 1, textAlign: "center" }} onClick={() => setEditModal(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Admin: Report ────────────────────────────────────────────────────────────────
// showPay=false for moderators / granted staff: attendance only, no salary figures.
function AdminReport({ modStaff = true, modFreelancer = true, showPay = true }) {
    const mobile = useMobile();
    const [filterStaff, setFilterStaff] = useState("all");
    const [filterType, setFilterType] = useState("this_month");
    const [customStart, setCustomStart] = useState(todayStr());
    const [customEnd, setCustomEnd] = useState(todayStr());
    const now = new Date();

    const getDateRange = () => {
        if (filterType === "this_month") { const s = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`; const e = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate())}`; return [s, e]; }
        if (filterType === "last_month") { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); const s = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; const e = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate())}`; return [s, e]; }
        return [customStart, customEnd];
    };
    const [startDate, endDate] = getDateRange();
    const today = todayStr();
    const effectiveEnd = endDate > today ? today : endDate;
    const days = daysInRange(startDate, effectiveEnd);
    // Filter visible staff based on moderator permissions
    const allowedStaff = _staff.filter(s => {
        if (modStaff && modFreelancer) return true;
        if (modStaff) return s.employment_type !== "freelancer";
        if (modFreelancer) return s.employment_type === "freelancer";
        return true; // admin sees all
    });
    const staffList = filterStaff === "all" ? allowedStaff : allowedStaff.filter(s => s.id === filterStaff);

    const tableData = staffList.map(s => {
        let present = 0, absent = 0, holidays = 0, saturdays = 0;
        days.forEach(d => { const st = getStatusForDate(s.id, d); if (st === "present") present++; else if (st === "absent" || st === "auto_absent") absent++; else if (st === "holiday") holidays++; else if (st === "saturday") saturdays++; });
        const totalCalDays = new Date(parseInt(startDate.slice(0, 4)), parseInt(startDate.slice(5, 7)), 0).getDate();
        const salary = currentSalaryOf(s.id, startDate);
        const deduction = days.reduce((a, d) => { const st = getStatusForDate(s.id, d); return st === "absent" || st === "auto_absent" ? a + absenceDeduction(s.id, d) : a; }, 0);
        return { ...s, present, absent, holidays, saturdays, workDays: totalCalDays, deduction: Math.round(deduction), net: Math.round(salary - deduction) };
    });

    const exportCSV = () => {
        const rows = showPay
            ? [["Name", "Designation", "Salary", "Present", "Absent", "Holidays", "Saturdays", "Working Days", "Deduction", "Net Salary"]]
            : [["Name", "Designation", "Present", "Absent", "Holidays", "Saturdays", "Working Days"]];
        tableData.forEach(r => rows.push(showPay
            ? [r.name, r.designation, r.salary, r.present, r.absent, r.holidays, r.saturdays, r.workDays, r.deduction, r.net]
            : [r.name, r.designation, r.present, r.absent, r.holidays, r.saturdays, r.workDays]));
        const csv = rows.map(r => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `foxbeep_report_${startDate}_${effectiveEnd}.csv`; a.click();
    };

    return (
        <div>
            <div style={{ display: "flex", alignItems: mobile ? "flex-start" : "center", flexDirection: mobile ? "column" : "row", justifyContent: "space-between", marginBottom: 24, gap: 12 }}>
                <h2 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>Attendance Report</h2>
                <button style={styles.btn(C.green)} onClick={exportCSV}>⬇ Export CSV</button>
            </div>
            <div style={{ ...styles.card, marginBottom: 24 }}>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>STAFF</label>
                        <select style={styles.input} value={filterStaff} onChange={e => setFilterStaff(e.target.value)}>
                            <option value="all">All Staff</option>{allowedStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select></div>
                    <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PERIOD</label>
                        <select style={styles.input} value={filterType} onChange={e => setFilterType(e.target.value)}>
                            <option value="this_month">This Month</option><option value="last_month">Last Month</option><option value="custom">Custom Range</option>
                        </select></div>
                    {filterType === "custom" && <>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>FROM</label><input style={styles.input} type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} /></div>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>TO</label><input style={styles.input} type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} /></div>
                    </>}
                </div>
                <div style={{ marginTop: 12, color: C.muted, fontSize: 13 }}>Range: {fmtDate(startDate)} – {fmtDate(effectiveEnd)}</div>
            </div>
            <div style={styles.card}>
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}><table style={{ ...styles.table, minWidth: 480 }}>
                    <thead><tr>
                        <th style={styles.th}>Name</th><th style={styles.th}>Designation</th>{showPay && <th style={styles.th}>Gross</th>}
                        <th style={styles.th}>Present</th><th style={styles.th}>Absent</th><th style={styles.th}>Holidays</th>
                        <th style={styles.th}>Saturdays</th>{showPay && <><th style={styles.th}>Deduction</th><th style={styles.th}>Net Salary</th></>}
                    </tr></thead>
                    <tbody>
                        {tableData.map(r => (
                            <tr key={r.id}>
                                <td style={styles.td}><strong style={{ color: C.white }}>{r.name}</strong></td>
                                <td style={styles.td}><span style={styles.badge(C.accent2)}>{r.designation}</span></td>
                                {showPay && <td style={styles.td}>Rs.{Number(r.salary || 0).toLocaleString()}</td>}
                                <td style={styles.td}><span style={styles.badge(C.green)}>{r.present}</span></td>
                                <td style={styles.td}><span style={styles.badge(C.red)}>{r.absent}</span></td>
                                <td style={styles.td}><span style={styles.badge(C.yellow)}>{r.holidays}</span></td>
                                <td style={styles.td}><span style={styles.badge(C.accent)}>{r.saturdays}</span></td>
                                {showPay && <>
                                <td style={styles.td}><span style={{ color: r.deduction > 0 ? C.red : C.muted }}>-Rs.{r.deduction.toLocaleString()}</span></td>
                                <td style={styles.td}><strong style={{ color: C.green }}>Rs.{r.net.toLocaleString()}</strong></td>
                                </>}
                            </tr>
                        ))}
                        {tableData.length === 0 && <tr><td colSpan={showPay ? 9 : 6} style={{ ...styles.td, textAlign: "center", color: C.muted }}>No data available.</td></tr>}
                    </tbody>
                </table></div>
            </div>
        </div>
    );
}

// ─── Admin: Freelancer Projects ──────────────────────────────────────────────────
function AdminProjects({ reload }) {
    const mobile = useMobile();
    const freelancers = _staff.filter(s => s.employment_type === "freelancer");
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", price: "", freelancer_id: freelancers[0]?.id || "", points: "" });
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");
    const [editProject, setEditProject] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editSaving, setEditSaving] = useState(false);

    const saveProject = async () => {
        if (!form.title || !form.price || !form.freelancer_id) return;
        setSaving(true);
        try {
            await db.addProject({ id: "p" + Date.now(), ...form, price: Number(form.price), points: Number(form.points) || 0, status: "pending" });
            await reload(); setShowAdd(false); setForm({ title: "", description: "", price: "", freelancer_id: freelancers[0]?.id || "", points: "" });
            showToast("Project added!");
        } finally { setSaving(false); }
    };

    const updateStatus = async (id, status) => {
        await db.updateProject(id, { status });
        if (status === "cleared") {
            const project = _projects.find(p => p.id === id);
            if (project && project.points > 0) {
                await awardPoints(project.freelancer_id, project.points, `Project completed: ${project.title}`, null);
            }
        }
        await reload();
        showToast(status === "cleared" ? "Project marked as cleared!" : "Project status updated!");
    };
    const deleteProject = async (id) => { await db.deleteProject(id); await reload(); showToast("Project deleted.", "#ef4444"); };

    const saveEdit = async () => {
        if (!editForm.title || !editForm.price) return;
        setEditSaving(true);
        try {
            await db.updateProject(editProject, { title: editForm.title, description: editForm.description, price: Number(editForm.price), points: Number(editForm.points) || 0 });
            await reload();
            setEditProject(null);
            showToast("Project updated!");
        } finally { setEditSaving(false); }
    };

    return (
        <div>
            <div style={{ display: "flex", alignItems: mobile ? "flex-start" : "center", flexDirection: mobile ? "column" : "row", justifyContent: "space-between", marginBottom: 24, gap: 12 }}>
                <h2 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>Freelancer Projects</h2>
                <button style={styles.btn()} onClick={() => setShowAdd(v => !v)}>+ Add Project</button>
            </div>
            {showAdd && (
                <div style={{ ...styles.card, marginBottom: 24 }}>
                    <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700 }}>New Project</h3>
                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 20 }}>
                        <div>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PROJECT TITLE</label>
                            <input style={styles.input} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Project title" />
                        </div>
                        <div>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PRICE (Rs.)</label>
                            <input style={styles.input} type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="Amount" />
                        </div>
                        <div>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>ASSIGN TO</label>
                            <select style={styles.input} value={form.freelancer_id} onChange={e => setForm(f => ({ ...f, freelancer_id: e.target.value }))}>
                                {freelancers.length === 0 && <option value="">No freelancers added yet</option>}
                                {freelancers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>DESCRIPTION</label>
                            <input style={styles.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" />
                        </div>
                        <div>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>⭐ POINTS TO AWARD</label>
                            <input style={styles.input} type="number" value={form.points} onChange={e => setForm(f => ({ ...f, points: e.target.value }))} placeholder="e.g. 20" />
                        </div>
                    </div>
                    <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
                        <button style={styles.btn(C.green)} onClick={saveProject} disabled={saving}>{saving ? "Saving..." : "Save Project"}</button>
                        <button style={styles.btnOut} onClick={() => setShowAdd(false)}>Cancel</button>
                    </div>
                </div>
            )}
            {freelancers.length === 0 && (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>
                    No freelancers added yet. Add staff with type "Freelancer" first.
                </div>
            )}
            {freelancers.length > 0 && (
                <>
                    <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `2px solid ${C.border}` }}>
                        {["pending", "completed"].map(tab => (
                            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: "10px 28px", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", background: "transparent", borderBottom: activeTab === tab ? `3px solid ${C.accent}` : "3px solid transparent", color: activeTab === tab ? C.accent : C.muted, marginBottom: -2, transition: "all 0.15s" }}>
                                {tab === "pending" ? "⏳ Pending" : "✔ Completed"}
                            </button>
                        ))}
                    </div>
                    {freelancers.map(fl => {
                        const flProjects = _projects.filter(p => p.freelancer_id === fl.id && (activeTab === "pending" ? p.status === "pending" : p.status === "cleared"));
                        if (flProjects.length === 0) return null;
                        const totalEarned = _projects.filter(p => p.freelancer_id === fl.id && p.status === "cleared").reduce((a, p) => a + Number(p.price), 0);
                        const totalPending = _projects.filter(p => p.freelancer_id === fl.id && p.status === "pending").reduce((a, p) => a + Number(p.price), 0);
                        return (
                            <div key={fl.id} style={{ marginBottom: 28 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.white }}>{fl.name}</h3>
                                    <span style={styles.badge(C.purple)}>Freelancer</span>
                                    <span style={{ ...styles.badge(C.green), marginLeft: "auto" }}>Completed: Rs.{totalEarned.toLocaleString()}</span>
                                    <span style={styles.badge(C.yellow)}>Pending: Rs.{totalPending.toLocaleString()}</span>
                                </div>
                                <div style={styles.card}>
                                    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}><table style={{ ...styles.table, minWidth: 480 }}>
                                        <thead><tr>
                                            <th style={styles.th}>Project</th><th style={styles.th}>Description</th>
                                            <th style={styles.th}>Price</th><th style={styles.th}>⭐ Points</th><th style={styles.th}>Actions</th>
                                        </tr></thead>
                                        <tbody>
                                            {flProjects.map(p => {
                                                return editProject === p.id ? (
                                                    <tr key={p.id} style={{ background: C.accent + "11" }}>
                                                        <td style={styles.td}><input style={{ ...styles.input, padding: "5px 8px", fontSize: 13 }} value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} /></td>
                                                        <td style={styles.td}><input style={{ ...styles.input, padding: "5px 8px", fontSize: 13 }} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" /></td>
                                                        <td style={styles.td}><input style={{ ...styles.input, padding: "5px 8px", fontSize: 13, width: 90 }} type="number" value={editForm.price} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))} /></td>
                                                        <td style={styles.td}><input style={{ ...styles.input, padding: "5px 8px", fontSize: 13, width: 80 }} type="number" value={editForm.points} onChange={e => setEditForm(f => ({ ...f, points: e.target.value }))} placeholder="0" /></td>
                                                        <td style={styles.td}>
                                                            <div style={{ display: "flex", gap: 6 }}>
                                                                <button style={{ ...styles.btn(C.green), padding: "5px 10px", fontSize: 12 }} onClick={saveEdit} disabled={editSaving}>{editSaving ? "..." : "✓ Save"}</button>
                                                                <button style={{ ...styles.btnOut, padding: "5px 10px", fontSize: 12 }} onClick={() => setEditProject(null)}>✕</button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={p.id}>
                                                        <td style={styles.td}><strong style={{ color: C.white }}>{p.title}</strong></td>
                                                        <td style={{ ...styles.td, color: C.muted }}>{p.description || "—"}</td>
                                                        <td style={styles.td}><strong style={{ color: C.green }}>Rs.{Number(p.price).toLocaleString()}</strong></td>
                                                        <td style={styles.td}>{p.points > 0 ? <span style={{ color: "#f59e0b", fontWeight: 700 }}>⭐ {p.points}</span> : <span style={{ color: C.muted }}>—</span>}</td>
                                                        <td style={styles.td}>
                                                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                                <button style={{ ...styles.btn(C.accent2), padding: "5px 10px", fontSize: 12 }} onClick={() => { setEditProject(p.id); setEditForm({ title: p.title, description: p.description || "", price: p.price, points: p.points || "" }); }}>✏ Edit</button>
                                                                {p.status === "pending" && <button style={{ ...styles.btn(C.green), padding: "5px 10px", fontSize: 12 }} onClick={() => updateStatus(p.id, "cleared")}>✔ Done</button>}
                                                                {p.status === "cleared" && <button style={{ ...styles.btn(C.yellow), padding: "5px 10px", fontSize: 12 }} onClick={() => updateStatus(p.id, "pending")}>↩ Pending</button>}
                                                                <button style={{ ...styles.btn(C.red), padding: "5px 10px", fontSize: 12 }} onClick={() => deleteProject(p.id)}>🗑</button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table></div>
                                </div>
                            </div>
                        );
                    })}
                    {freelancers.every(fl => _projects.filter(p => p.freelancer_id === fl.id && (activeTab === "pending" ? p.status === "pending" : p.status === "cleared")).length === 0) && (
                        <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>
                            No {activeTab === "pending" ? "pending" : "completed"} projects found.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ─── Admin: Withdraw Requests ─────────────────────────────────────────────────────
function AdminWithdrawRequests({ reload }) {
    const mobile = useMobile();
    const [clearModal, setClearModal] = useState(null);
    const [clearForm, setClearForm] = useState({ payment_method: "bank_transfer", txn_number: "" });
    const [clearFile, setClearFile] = useState(null);
    const [clearFilePreview, setClearFilePreview] = useState(null);
    const [clearing, setClearing] = useState(false);

    const pending = _withdrawRequests.filter(r => r.status === "pending");
    const cleared = _withdrawRequests.filter(r => r.status === "cleared");

    const openClearModal = (r) => {
        setClearModal(r);
        setClearForm({ payment_method: "bank_transfer", txn_number: "" });
        setClearFile(null); setClearFilePreview(null);
    };

    const [clearInfo, setClearInfo] = useState(null);           // optimisation result for the chosen screenshot
    const handleClearFile = async (e) => {
        const raw = e.target.files[0]; if (!raw) return;
        let r = null; try { r = await prepareUpload(raw); } catch { r = null; }
        const file = r?.file || raw; setClearInfo(r);
        setClearFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setClearFilePreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const confirmCleared = async () => {
        if (!clearModal) return;
        setClearing(true);
        try {
            let screenshotUrl = null;
            if (clearFile) {
                const ext = clearFile.name.split(".").pop();
                const file = await optimizedForUpload(clearFile);
                try {   // R2 when switched on (registered when the request is saved), otherwise the private bucket path — opened via signed URL
                    screenshotUrl = (await storeFile({ target: "payroll.withdraw_proof", blob: file, fileName: file.name, mime: file.type || "application/octet-stream",
                        legacy: { bucket: PROOF_BUCKET, path: `withdraw_${clearModal.id}_${Date.now()}.${ext}`, upsert: true } })).ref;
                } catch { screenshotUrl = null; }
            }
            await db.updateWithdrawRequest(clearModal.id, {
                status: "cleared",
                cleared_at: new Date().toISOString(),
                admin_payment_method: clearForm.payment_method,
                txn_number: clearForm.txn_number || null,
                payment_screenshot: screenshotUrl,
            });
            setClearModal(null);
            await reload();
            showToast("Payment marked as cleared!");
        } finally { setClearing(false); }
    };

    const methodLabel = (m) => ({ esewa: "eSewa", khalti: "Khalti", bank_transfer: "Bank Transfer", cash: "Cash", bank: "Bank", other: "Other" })[m] || m || "—";

    return (
        <div>
            <h2 style={{ margin: "0 0 6px", fontWeight: 800, fontSize: 22 }}>Withdrawal Requests</h2>
            <p style={{ margin: "0 0 24px", color: C.muted }}>Review and clear freelancer payment withdrawal requests.</p>

            {/* Pending */}
            {pending.length > 0 ? (
                <div style={{ marginBottom: 28 }}>
                    <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: C.yellow }}>⏳ Pending Requests ({pending.length})</h3>
                    <div style={styles.card}>
                        {mobile ? pending.map(r => (
                            <div key={r.id} style={{ padding: "14px 0", borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                                    <div>
                                        <div style={{ fontWeight: 700, color: C.white }}>{getStaffName(r.freelancer_id)}</div>
                                        <div style={{ color: C.green, fontWeight: 700, fontSize: 16 }}>Rs.{Number(r.amount).toLocaleString()}</div>
                                    </div>
                                    <span style={styles.badge(C.accent)}>{methodLabel(r.payment_method)}</span>
                                </div>
                                <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Acct: {r.account_number}</div>
                                <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Requested: {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</div>
                                {r.notes && <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Note: {r.notes}</div>}
                                <button style={{ ...styles.btn(C.green), padding: "8px 16px", fontSize: 13, width: "100%" }} onClick={() => openClearModal(r)}>✅ Mark Paid</button>
                            </div>
                        )) : (
                            <div style={{ overflowX: "auto" }}><table style={{ ...styles.table, minWidth: 520 }}>
                                <thead><tr>
                                    <th style={styles.th}>Freelancer</th><th style={styles.th}>Amount</th>
                                    <th style={styles.th}>Requested Via</th><th style={styles.th}>Account</th>
                                    <th style={styles.th}>Requested</th><th style={styles.th}>Notes</th><th style={styles.th}>Action</th>
                                </tr></thead>
                                <tbody>
                                    {pending.map(r => (
                                        <tr key={r.id}>
                                            <td style={styles.td}><strong style={{ color: C.white }}>{getStaffName(r.freelancer_id)}</strong></td>
                                            <td style={styles.td}><strong style={{ color: C.green }}>Rs.{Number(r.amount).toLocaleString()}</strong></td>
                                            <td style={styles.td}><span style={styles.badge(C.accent)}>{methodLabel(r.payment_method)}</span></td>
                                            <td style={{ ...styles.td, color: C.muted }}>{r.account_number}</td>
                                            <td style={{ ...styles.td, color: C.muted, fontSize: 12 }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                                            <td style={{ ...styles.td, color: C.muted, fontSize: 12 }}>{r.notes || "—"}</td>
                                            <td style={styles.td}>
                                                <button style={{ ...styles.btn(C.green), padding: "6px 14px", fontSize: 12 }} onClick={() => openClearModal(r)}>✅ Mark Paid</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40, marginBottom: 28 }}>No pending withdrawal requests.</div>
            )}

            {/* Cleared */}
            {cleared.length > 0 && (
                <div>
                    <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: C.green }}>✅ Cleared Requests ({cleared.length})</h3>
                    <div style={styles.card}>
                        <div style={{ overflowX: "auto" }}><table style={{ ...styles.table, minWidth: 580 }}>
                            <thead><tr>
                                <th style={styles.th}>Freelancer</th><th style={styles.th}>Amount</th>
                                <th style={styles.th}>Paid Via</th><th style={styles.th}>Account</th>
                                <th style={styles.th}>TXN #</th><th style={styles.th}>Proof</th>
                                <th style={styles.th}>Cleared</th>
                            </tr></thead>
                            <tbody>
                                {cleared.map(r => (
                                    <tr key={r.id}>
                                        <td style={styles.td}><strong style={{ color: C.white }}>{getStaffName(r.freelancer_id)}</strong></td>
                                        <td style={styles.td}><strong style={{ color: C.green }}>Rs.{Number(r.amount).toLocaleString()}</strong></td>
                                        <td style={styles.td}><span style={styles.badge(C.green)}>{methodLabel(r.admin_payment_method || r.payment_method)}</span></td>
                                        <td style={{ ...styles.td, color: C.muted }}>{r.account_number}</td>
                                        <td style={{ ...styles.td, color: C.muted, fontSize: 12 }}>{r.txn_number || "—"}</td>
                                        <td style={styles.td}>
                                            {r.payment_screenshot
                                                ? <a {...proofLinkProps(r.payment_screenshot)} style={{ color: C.accent, fontSize: 12 }}>🖼 View</a>
                                                : <span style={{ color: C.muted, fontSize: 12 }}>—</span>}
                                        </td>
                                        <td style={{ ...styles.td, color: C.green, fontSize: 12 }}>{r.cleared_at ? new Date(r.cleared_at).toLocaleDateString() : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                    </div>
                </div>
            )}

            {/* Mark Paid Modal */}
            {clearModal && (
                <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                    <div style={{ ...styles.card, width: "100%", maxWidth: 440, maxHeight: "90vh", overflowY: "auto" }}>
                        <h3 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 18 }}>Mark Withdrawal Paid</h3>
                        <p style={{ margin: "0 0 4px", color: C.muted, fontSize: 13 }}>{getStaffName(clearModal.freelancer_id)}</p>
                        <p style={{ margin: "0 0 16px", color: C.muted, fontSize: 12 }}>
                            They requested via {methodLabel(clearModal.payment_method)} · Acct: {clearModal.account_number}
                        </p>
                        <div style={{ padding: "12px 16px", background: C.green + "11", borderRadius: 10, border: `1px solid ${C.green}33`, textAlign: "center", marginBottom: 16 }}>
                            <div style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>Withdrawal Amount</div>
                            <div style={{ fontSize: 28, fontWeight: 900, color: C.green }}>Rs.{Number(clearModal.amount).toLocaleString()}</div>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PAYMENT METHOD USED *</label>
                            <select style={styles.input} value={clearForm.payment_method} onChange={e => setClearForm(f => ({ ...f, payment_method: e.target.value }))}>
                                <option value="esewa">eSewa</option>
                                <option value="khalti">Khalti</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="cash">Cash</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>TRANSACTION NUMBER (optional)</label>
                            <input style={styles.input} placeholder="e.g. TXN-20240408-001" value={clearForm.txn_number} onChange={e => setClearForm(f => ({ ...f, txn_number: e.target.value }))} />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PAYMENT SCREENSHOT (optional)</label>
                            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: C.card2, border: `1px dashed ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
                                <span style={{ fontSize: 20 }}>📎</span>
                                <span style={{ fontSize: 13, color: clearFile ? C.white : C.muted }}>{clearFile ? clearFile.name : "Click to attach screenshot"}</span>
                                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleClearFile} />
                            </label>
                            {clearFile && clearInfo && <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }} data-testid="optimize-note">
                                {clearInfo.optimized ? `Original ${kb(clearInfo.original)} → optimised ${kb(clearInfo.final)} (this version is uploaded)` : `Size ${kb(clearInfo.original)}${clearInfo.note ? ` · ${clearInfo.note}` : ""}`}</div>}
                            {clearFilePreview && (
                                <div style={{ marginTop: 8, position: "relative", display: "inline-block" }}>
                                    <img src={clearFilePreview} alt="preview" style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8, border: `1px solid ${C.border}`, objectFit: "contain" }} />
                                    <button onClick={() => { setClearFile(null); setClearFilePreview(null); }} style={{ position: "absolute", top: 4, right: 4, background: C.red, border: "none", borderRadius: "50%", width: 20, height: 20, color: "#fff", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                                </div>
                            )}
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            <button style={{ ...styles.btn(C.green), flex: 1 }} onClick={confirmCleared} disabled={clearing}>{clearing ? "Saving..." : "✅ Confirm Paid"}</button>
                            <button style={{ ...styles.btn(C.card2), flex: 1 }} onClick={() => setClearModal(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Freelancer My Projects Page ─────────────────────────────────────────────────
function FreelancerProjectsPage({ user }) {
    const mobile = useMobile();
    const [tab, setTab] = useState("ongoing");
    const myProjects = _projects.filter(p => p.freelancer_id === user.id);
    const ongoing = myProjects.filter(p => p.status === "pending");
    const completed = myProjects.filter(p => p.status === "cleared");
    const list = tab === "ongoing" ? ongoing : completed;

    const totalEarned = completed.reduce((a, p) => a + Number(p.price), 0);
    const totalPending = ongoing.reduce((a, p) => a + Number(p.price), 0);

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><Briefcase size={22} /> My Projects</h2>
                <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>All projects assigned to you</p>
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: mobile ? 10 : 16, marginBottom: 24 }}>
                <StatCard icon="📁" label="Total Projects" value={myProjects.length} color={C.accent} sub="all time" />
                <StatCard icon="⏳" label="Ongoing" value={`Rs.${totalPending.toLocaleString()}`} color={C.yellow} sub={`${ongoing.length} project(s)`} />
                <StatCard icon="✅" label="Completed" value={`Rs.${totalEarned.toLocaleString()}`} color={C.green} sub={`${completed.length} project(s)`} />
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: `2px solid ${C.border}` }}>
                {[["ongoing", "⏳ Ongoing", ongoing.length], ["completed", "✅ Completed", completed.length]].map(([id, label, count]) => (
                    <button key={id} onClick={() => setTab(id)} style={{ padding: "10px 24px", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", background: "transparent", borderBottom: tab === id ? `3px solid ${C.accent}` : "3px solid transparent", color: tab === id ? C.accent : C.muted, marginBottom: -2 }}>
                        {label} <span style={{ background: tab === id ? C.accent + "33" : C.card2, color: tab === id ? C.accent : C.muted, borderRadius: 20, padding: "1px 8px", fontSize: 12, marginLeft: 4 }}>{count}</span>
                    </button>
                ))}
            </div>

            {list.length === 0 ? (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>No {tab} projects.</div>
            ) : (
                <div style={styles.card}>
                    {list.map(p => (
                        <div key={p.id} style={{ padding: "16px 0", borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, color: C.white, fontSize: 15 }}>{p.title}</div>
                                    {p.description && <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{p.description}</div>}
                                    <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
                                        📅 {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                                        {p.status === "cleared" && p.updated_at && <> &nbsp;•&nbsp; ✅ Cleared: {new Date(p.updated_at).toLocaleDateString()}</>}
                                    </div>
                                </div>
                                <div style={{ textAlign: "right", flexShrink: 0 }}>
                                    <div style={{ fontWeight: 900, color: C.green, fontSize: 18 }}>Rs.{Number(p.price).toLocaleString()}</div>
                                    {p.points > 0 && <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, marginTop: 4 }}><span style={{ fontSize: 13 }}>⭐</span><span style={{ fontWeight: 700, color: "#f59e0b", fontSize: 13 }}>{p.points} pts</span></div>}
                                    <div style={{ marginTop: 6 }}><span style={styles.badge(p.status === "cleared" ? C.green : C.yellow)}>{p.status === "cleared" ? "✅ Completed" : "⏳ Ongoing"}</span></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Staff ID Card ─────────────────────────────────────────────────────────────────
function staffProfileUrl(staff) {
    const slug = staff.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    return `https://att.foxbeep.com/#p/${slug}`;
}

// Clip an image into a circle on an offscreen canvas → returns base64 PNG
async function circularImageBase64(dataUrl, size = 200) {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
            const c = document.createElement("canvas");
            c.width = c.height = size;
            const ctx = c.getContext("2d");
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(img, 0, 0, size, size);
            resolve(c.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null);
        img.crossOrigin = "anonymous";
        img.src = dataUrl;
    });
}


function IdCardPreview({ staff, scale = 4.5 }) {
    const mm = v => v * scale;
    const initials = staff.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
    const etLabel = staff.employment_type === "freelancer" ? "Freelancer" : staff.employment_type === "part_time" ? "Part Time" : "Full Time";
    const etColor = staff.employment_type === "freelancer" ? "#7c3aed" : staff.employment_type === "part_time" ? "#d97706" : "#059669";
    const [qrImg, setQrImg] = useState(null);
    useEffect(() => {
        QRCode.toDataURL(staffProfileUrl(staff), { width: 140, margin: 1, color: { dark: "#1a1a2e", light: "#ffffff" } })
            .then(setQrImg).catch(() => {});
    }, [staff.name]); // eslint-disable-line react-hooks/exhaustive-deps

    const infoFields = [
        ["ID No", staff.id.slice(-8).toUpperCase()],
        ["Joined", staff.join_date ? new Date(staff.join_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"],
        ["Type", etLabel],
        ["Phone", staff.phone || "—"],
    ];

    return (
        <div style={{ width: mm(86), height: mm(54), background: "#f0f4f8", borderRadius: mm(2.5), overflow: "visible", position: "relative", fontFamily: "'DM Sans', sans-serif", boxShadow: "0 8px 32px #0004", flexShrink: 0 }}>
            {/* Blue header */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: mm(20), background: "linear-gradient(135deg, #81007F 0%, #a0009d 60%, #c06bbf 100%)", borderRadius: `${mm(2.5)}px ${mm(2.5)}px 0 0`, overflow: "hidden" }}>
                {/* Geometric diamonds */}
                {[[55,4,7],[66,1,4.5],[74,7,5.5],[48,11,3.5],[80,3,3],[60,10,3],[72,13,2.5]].map(([x,y,r], i) => (
                    <div key={i} style={{ position: "absolute", left: mm(x-r), top: mm(y-r), width: mm(r*2), height: mm(r*2), background: "rgba(255,255,255,0.10)", transform: "rotate(45deg)", borderRadius: 2 }} />
                ))}
                {/* Logo top-right */}
                <div style={{ position: "absolute", right: mm(3), top: mm(2), display: "flex", flexDirection: "column", alignItems: "center", gap: mm(0.5) }}>
                    <div style={{ width: mm(9), height: mm(9), borderRadius: "50%", overflow: "hidden", border: `${mm(0.4)}px solid rgba(255,255,255,0.4)` }}>
                        <img src="/logo.png" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                    <div style={{ fontSize: mm(2.2), fontWeight: 900, color: "#fff", letterSpacing: 0.5 }}>FOXBEEP</div>
                    <div style={{ fontSize: mm(1.7), color: "rgba(200,220,255,0.9)" }}>TECHNOLOGY</div>
                </div>
                {/* Name & designation inside header (right of photo space) */}
                <div style={{ position: "absolute", left: mm(31), top: mm(3), right: mm(22) }}>
                    <div style={{ fontSize: mm(4), fontWeight: 900, color: "#fff", lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{staff.name}</div>
                    <div style={{ fontSize: mm(2.4), color: "rgba(200,225,255,0.95)", marginTop: mm(0.6), fontWeight: 500 }}>{staff.designation || "Staff"}</div>
                    <div style={{ marginTop: mm(1), display: "inline-block", background: "rgba(255,255,255,0.18)", borderRadius: mm(1), padding: `${mm(0.4)}px ${mm(1.5)}px`, fontSize: mm(1.8), color: "#fff", fontWeight: 700 }}>{etLabel.toUpperCase()}</div>
                </div>
            </div>

            {/* White body */}
            <div style={{ position: "absolute", top: mm(20), left: 0, right: 0, bottom: 0, background: "#f8fafc", borderRadius: `0 0 ${mm(2.5)}px ${mm(2.5)}px` }} />

            {/* Profile photo — overlapping header/body */}
            <div style={{ position: "absolute", left: mm(5), top: mm(10), width: mm(18), height: mm(18), borderRadius: "50%", overflow: "hidden", border: `${mm(1)}px solid #fff`, boxShadow: `0 ${mm(0.5)}px ${mm(2)}px rgba(0,0,0,0.25)`, background: "#81007F", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3 }}>
                {staff.profile_image
                    ? <img src={staff.profile_image} alt={staff.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <span style={{ fontSize: mm(7), fontWeight: 900, color: "#fff" }}>{initials}</span>}
            </div>

            {/* Info grid — starts after avatar (avatar ends at x=23mm) */}
            <div style={{ position: "absolute", top: mm(22), left: mm(25), right: mm(20), display: "grid", gridTemplateColumns: "1fr 1fr", gap: `${mm(0.5)}px ${mm(1)}px` }}>
                {infoFields.map(([label, val], i) => (
                    <div key={i} style={{ borderLeft: `${mm(0.4)}px solid #a0009d`, paddingLeft: mm(1.2), paddingTop: mm(0.4) }}>
                        <div style={{ fontSize: mm(1.8), color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
                        <div style={{ fontSize: mm(2.2), color: "#0f172a", fontWeight: 700, marginTop: mm(0.3), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{val}</div>
                    </div>
                ))}
            </div>

            {/* QR code */}
            <div style={{ position: "absolute", right: mm(3), top: mm(21), width: mm(16), height: mm(16), background: "#fff", borderRadius: mm(1), overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 ${mm(0.3)}px ${mm(1)}px #0002` }}>
                {qrImg ? <img src={qrImg} alt="QR" style={{ width: "100%", height: "100%" }} /> : <div style={{ width: "100%", height: "100%", background: "#e2e8f0" }} />}
            </div>
            <div style={{ position: "absolute", right: mm(3), top: mm(38), fontSize: mm(1.7), color: "#94a3b8", textAlign: "center", width: mm(16) }}>Scan to verify</div>

            {/* Purple footer */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: mm(8), background: "#81007F", borderRadius: `0 0 ${mm(2.5)}px ${mm(2.5)}px`, display: "flex", alignItems: "center", padding: `0 ${mm(3)}px`, justifyContent: "space-between" }}>
                <div style={{ fontSize: mm(1.8), color: "rgba(200,220,255,0.85)" }}>Madhyapur Thimi, Bhaktapur, Nepal</div>
                <div style={{ fontSize: mm(2), color: "#fff", fontWeight: 700 }}>www.foxbeep.com</div>
            </div>
        </div>
    );
}

function IdCardWithDownload({ staff, scale = 4.5, compact = false }) {
    const cardRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const handleDownload = async () => {
        if (!cardRef.current || busy) return;
        setBusy(true);
        try {
            const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, allowTaint: false, backgroundColor: null, logging: false });
            const link = document.createElement("a");
            link.download = `ID_Card_${staff.name.replace(/\s+/g, "_")}.jpg`;
            link.href = canvas.toDataURL("image/jpeg", 0.95);
            link.click();
            showToast("ID card downloaded!");
        } catch { showToast("Download failed, try again."); }
        finally { setBusy(false); }
    };
    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                {compact
                    ? <div><div style={{ fontWeight: 700, fontSize: 14, color: C.white }}>{staff.name}</div><div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{staff.designation} • <span style={{ color: staff.employment_type === "freelancer" ? C.purple : staff.employment_type === "part_time" ? C.yellow : C.green, fontWeight: 600 }}>{staff.employment_type === "freelancer" ? "Freelancer" : staff.employment_type === "part_time" ? "Part Time" : "Full Time"}</span></div></div>
                    : <div><h3 style={{ margin: "0 0 2px", fontWeight: 800, fontSize: 16 }}>🪪 Your Staff ID Card</h3><p style={{ margin: 0, fontSize: 12, color: C.muted }}>86 × 54mm — standard credit card size</p></div>}
                <button onClick={handleDownload} disabled={busy} style={{ ...styles.btn(C.accent), display: "flex", alignItems: "center", gap: compact ? 6 : 7, padding: compact ? "8px 14px" : undefined, fontSize: compact ? 12 : undefined }}>
                    <Download size={compact ? 13 : 14} /> {busy ? "Downloading..." : "Download ID Card"}
                </button>
            </div>
            <div style={{ overflowX: "auto" }}>
                <div ref={cardRef} style={{ display: "inline-block" }}>
                    <IdCardPreview staff={staff} scale={scale} />
                </div>
            </div>
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: C.muted, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "4px 10px", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    📐 Card size: <strong style={{ color: C.white }}>86 × 54 mm</strong> (CR80 / Standard Credit Card Size)
                </span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>— buy this size card holder or sleeve from any stationery shop</span>
            </div>
        </div>
    );
}

// ─── Freelancer Dashboard ─────────────────────────────────────────────────────────
function FreelancerDashboard({ user, reload }) {
    const mobile = useMobile();
    const [showWithdraw, setShowWithdraw] = useState(false);
    const [withdrawForm, setWithdrawForm] = useState({ amount: "", payment_method: "bank", account_number: "", notes: "" });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [projectFilter, setProjectFilter] = useState("all");
    const MIN_WITHDRAW = 5000;

    const myProjects = _projects.filter(p => p.freelancer_id === user.id);
    const myRequests = _withdrawRequests.filter(r => r.freelancer_id === user.id);
    const clearedProjects = myProjects.filter(p => p.status === "cleared");
    const pendingProjects = myProjects.filter(p => p.status === "pending");
    const myCommissions = _commissions.filter(c => c.staff_id === user.id);
    const totalCommissions = myCommissions.reduce((a, c) => a + Number(c.commission_amount), 0);
    const totalEarned = clearedProjects.reduce((a, p) => a + Number(p.price), 0) + totalCommissions;
    const totalPending = pendingProjects.reduce((a, p) => a + Number(p.price), 0);
    const pendingWithdrawals = myRequests.filter(r => r.status === "pending").reduce((a, r) => a + Number(r.amount), 0);
    const clearedWithdrawals = myRequests.filter(r => r.status === "cleared").reduce((a, r) => a + Number(r.amount), 0);
    const available = totalEarned - clearedWithdrawals - pendingWithdrawals;
    const staff = _staff.find(s => s.id === user.id);

    const submitWithdraw = async () => {
        if (!withdrawForm.amount || !withdrawForm.account_number) return;
        if (Number(withdrawForm.amount) > available) return;
        if (Number(withdrawForm.amount) < MIN_WITHDRAW) return;
        setSaving(true);
        try {
            await db.addWithdrawRequest({ id: "w" + Date.now(), freelancer_id: user.id, ...withdrawForm, amount: Number(withdrawForm.amount), status: "pending", created_at: new Date().toISOString() });
            await reload(); setSaved(true);
            setTimeout(() => { setShowWithdraw(false); setSaved(false); setWithdrawForm({ amount: "", payment_method: "bank", account_number: "", notes: "" }); }, 1200);
        } finally { setSaving(false); }
    };

    const newlyClearedRequests = myRequests.filter(r => r.status === "cleared");

    return (
        <div>
            <h2 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 22 }}>Welcome, {user.name.split(" ")[0]} 👋</h2>
            <p style={{ margin: "0 0 28px", color: C.muted }}>{staff?.designation} • <span style={styles.badge(C.purple)}>Freelancer</span></p>
            {newlyClearedRequests.length > 0 && (
                <div style={{ ...styles.card, marginBottom: 20, background: C.green + "11", border: `1px solid ${C.green}44` }}>
                    <div style={{ color: C.green, fontWeight: 700 }}>✅ {newlyClearedRequests.length} withdrawal request(s) have been cleared by admin!</div>
                </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 28 }}>
                <StatCard icon="✅" label="Available to Withdraw" value={`Rs.${available.toLocaleString()}`} color={C.green} sub="Ready to request" />
                <StatCard icon="⏳" label="Pending Payment" value={`Rs.${totalPending.toLocaleString()}`} color="#38bdf8" sub="Awaiting clearance" />
                <StatCard icon="📤" label="Withdrawn" value={`Rs.${(clearedWithdrawals + pendingWithdrawals).toLocaleString()}`} color={C.muted} sub="Total requested" />
                <StatCard icon="💰" label="Total Earned" value={`Rs.${totalEarned.toLocaleString()}`} color="#1e293b" sub="Completed projects" />
            </div>
            <div style={{ display: "flex", gap: 20, marginBottom: 24, flexWrap: "wrap" }}>
                <button style={{ ...styles.btn(C.accent), fontSize: 15, padding: "12px 32px" }} onClick={() => setShowWithdraw(true)} disabled={available < MIN_WITHDRAW}>
                    💸 Request Withdrawal
                </button>
                {available < MIN_WITHDRAW && <span style={{ color: C.red, fontSize: 13, alignSelf: "center" }}>⚠️ Minimum withdrawal is Rs.{MIN_WITHDRAW.toLocaleString()}. Available: Rs.{available.toLocaleString()}</span>}
            </div>
            {showWithdraw && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: mobile ? "flex-end" : "center", justifyContent: "center", zIndex: 1000, overflowY: "auto", padding: mobile ? 0 : 16 }}>
                    <div style={{ ...styles.card, width: mobile ? "100%" : 440, borderRadius: mobile ? "20px 20px 0 0" : 16, maxHeight: mobile ? "90vh" : "unset", overflowY: "auto" }}>
                        <h3 style={{ margin: "0 0 20px", fontSize: 18, fontWeight: 800, color: C.white }}>💸 Request Withdrawal</h3>
                        <div style={{ ...styles.card2, marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: C.muted }}>Available Balance</span>
                            <strong style={{ color: C.green }}>Rs.{available.toLocaleString()}</strong>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>AMOUNT (Rs.)</label>
                            <input style={styles.input} type="number" value={withdrawForm.amount} onChange={e => setWithdrawForm(f => ({ ...f, amount: e.target.value }))} placeholder={`Max: Rs.${available.toLocaleString()}`} />
                            {Number(withdrawForm.amount) > available && <div style={{ color: C.red, fontSize: 12, marginTop: 4 }}>⚠️ Amount exceeds available balance</div>}
                            {Number(withdrawForm.amount) > 0 && Number(withdrawForm.amount) < MIN_WITHDRAW && <div style={{ color: C.yellow, fontSize: 12, marginTop: 4 }}>⚠️ Minimum withdrawal amount is Rs.{MIN_WITHDRAW.toLocaleString()}</div>}
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PAYMENT METHOD</label>
                            <div style={{ display: "flex", gap: 10 }}>
                                {["bank", "esewa"].map(m => (
                                    <div key={m} onClick={() => setWithdrawForm(f => ({ ...f, payment_method: m }))}
                                        style={{ flex: 1, padding: 12, borderRadius: 10, border: `2px solid ${withdrawForm.payment_method === m ? C.accent : C.border}`, background: withdrawForm.payment_method === m ? C.accent + "22" : "transparent", cursor: "pointer", textAlign: "center", fontWeight: 700, fontSize: 14, color: withdrawForm.payment_method === m ? C.accent : C.muted }}>
                                        {m === "bank" ? "🏦 Bank" : "📱 eSewa"}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>{withdrawForm.payment_method === "esewa" ? "ESEWA NUMBER" : "BANK ACCOUNT NUMBER"}</label>
                            <input style={styles.input} value={withdrawForm.account_number} onChange={e => setWithdrawForm(f => ({ ...f, account_number: e.target.value }))} placeholder={withdrawForm.payment_method === "esewa" ? "98XXXXXXXX" : "Account number"} />
                        </div>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>NOTES (Optional)</label>
                            <input style={styles.input} value={withdrawForm.notes} onChange={e => setWithdrawForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any message to admin..." />
                        </div>
                        {saved && <div style={{ marginBottom: 14, padding: "10px 14px", background: C.green + "22", border: `1px solid ${C.green}44`, borderRadius: 10, color: C.green, fontSize: 13, fontWeight: 600 }}>✅ Withdrawal request sent to admin!</div>}
                        <div style={{ display: "flex", gap: 12 }}>
                            <button style={{ ...styles.btn(C.accent), flex: 1 }} onClick={submitWithdraw} disabled={saving || Number(withdrawForm.amount) > available || Number(withdrawForm.amount) < MIN_WITHDRAW}>{saving ? "Sending..." : "Send Request"}</button>
                            <button style={{ ...styles.btnOut, flex: 1, textAlign: "center" }} onClick={() => setShowWithdraw(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 20 }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>My Projects</h3>
                        <select style={{ ...styles.input, width: "auto", padding: "6px 12px", fontSize: 12 }} value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
                            <option value="all">All</option>
                            <option value="pending">⏳ Pending</option>
                            <option value="cleared">✅ Cleared</option>
                        </select>
                    </div>
                    <div style={styles.card}>
                        {myProjects.length === 0 && <div style={{ color: C.muted, textAlign: "center", padding: 20 }}>No projects assigned yet.</div>}
                        {myProjects.filter(p => projectFilter === "all" || p.status === projectFilter).map(p => (
                            <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                                <div>
                                    <div style={{ fontWeight: 700, color: C.white }}>{p.title}</div>
                                    {p.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{p.description}</div>}
                                    {p.created_at && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>📅 {new Date(p.created_at).toLocaleDateString()}</div>}
                                </div>
                                <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                                    <div style={{ fontWeight: 700, color: C.green, marginBottom: 4 }}>Rs.{Number(p.price).toLocaleString()}</div>
                                    {p.points > 0 && <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, marginBottom: 4 }}><span style={{ fontSize: 13 }}>⭐</span><span style={{ fontWeight: 700, color: "#f59e0b", fontSize: 13 }}>{p.points} pts</span></div>}
                                    <span style={styles.badge(p.status === "cleared" ? C.muted : C.yellow)}>{p.status === "cleared" ? "✔ Completed" : "⏳ Pending"}</span>
                                </div>
                            </div>
                        ))}
                        {myProjects.filter(p => projectFilter === "all" || p.status === projectFilter).length === 0 && <div style={{ color: C.muted, textAlign: "center", padding: 20 }}>No {projectFilter} projects.</div>}
                    </div>
                </div>
                <div>
                    <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Withdrawal History</h3>
                    <div style={styles.card}>
                        {myRequests.length === 0 && <div style={{ color: C.muted, textAlign: "center", padding: 20 }}>No withdrawal requests yet.</div>}
                        {myRequests.map(r => (
                            <div key={r.id} style={{ padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <div>
                                        <div style={{ fontWeight: 700, color: C.white }}>Rs.{Number(r.amount).toLocaleString()}</div>
                                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{r.payment_method === "esewa" ? "📱 eSewa" : "🏦 Bank"} • {r.account_number}</div>
                                        <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>📅 Requested: {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</div>
                                        {r.status === "cleared" && r.cleared_at && <div style={{ fontSize: 11, color: C.green, marginTop: 2 }}>✅ Cleared: {new Date(r.cleared_at).toLocaleDateString()}</div>}
                                        {r.txn_number && <div style={{ fontSize: 11, color: C.accent, marginTop: 2 }}>🔖 TXN: {r.txn_number}</div>}
                                        {r.payment_screenshot && <a {...proofLinkProps(r.payment_screenshot)} style={{ fontSize: 11, color: C.accent, marginTop: 2, display: "inline-block" }}>🖼 Payment Proof</a>}
                                    </div>
                                    <span style={styles.badge(r.status === "cleared" ? C.green : C.yellow)}>{r.status === "cleared" ? "✅ Cleared" : "⏳ Pending"}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* ── ID Card ── */}
            {(() => { const s = _staff.find(x => x.id === user.id); if (!s) return null; return (
                <div style={{ ...styles.card, marginTop: 24 }}><IdCardWithDownload staff={s} /></div>
            ); })()}
        </div>
    );
}

// ─── Staff Dashboard ──────────────────────────────────────────────────────────────
function StaffDashboard({ user, reload, isModerator = false, isModStaff = false, isModFreelancer = false }) {
    const mobile = useMobile();
    const [tick, setTick] = useState(0);
    const today = todayStr();
    const now = new Date();
    const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    const rec = getAttendanceRecord(user.id, today);
    const isHoliday = _holidays.includes(today);
    const isSat = isSaturday(today);
    const staff = _staff.find(s => s.id === user.id);
    const hour = now.getHours();

    const canClockIn = !rec && hour < 11 && !isHoliday && !isSat;
    const tooLate = !rec && hour >= 11 && !isHoliday && !isSat;
    const minutesSinceClockIn = rec && !rec.clock_out ? Math.floor((Date.now() - (rec.clock_in_ts || 0)) / 60000) : 0;
    const canClockOut = rec && !rec.clock_out && minutesSinceClockIn >= 10;
    const cooldownLeft = rec && !rec.clock_out && minutesSinceClockIn < 10 ? (10 - minutesSinceClockIn) : 0;

    useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 1000); return () => clearInterval(t); }, []);

    // ── IP + Location ────────────────────────────────────────────────────────
    const [ipInfo, setIpInfo] = useState({ ip: null, loading: true });
    const [locationInfo, setLocationInfo] = useState({ label: null, coords: null, loading: false, denied: false });

    useEffect(() => {
        fetch("https://api.ipify.org?format=json")
            .then(r => r.json())
            .then(d => setIpInfo({ ip: d.ip, loading: false }))
            .catch(() => setIpInfo({ ip: "Unavailable", loading: false }));
    }, []);

    useEffect(() => {
        if (!navigator.geolocation) return;
        setLocationInfo(prev => ({ ...prev, loading: true }));
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude: lat, longitude: lon } = pos.coords;
                let label = `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
                try {
                    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`);
                    const d = await res.json();
                    label = d.address?.city || d.address?.town || d.address?.village || d.address?.county || label;
                } catch {}
                setLocationInfo({ coords: { lat, lon }, label, loading: false, denied: false });
            },
            () => setLocationInfo({ coords: null, label: null, loading: false, denied: true }),
            { timeout: 6000, maximumAge: 60000 }
        );
    }, []);

    useEffect(() => {
        if (rec && !rec.clock_out && hour >= 18) {
            db.upsertAttendance({ id: `${user.id}_${today}`, staff_id: user.id, date: today, clock_in: rec.clock_in, clock_in_ts: rec.clock_in_ts, clock_out: "18:00 (Auto)", status: "present" }).then(reload);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tick]);

    // Monthly perfect attendance bonus (50 pts) — awarded on last day if no absents
    useEffect(() => {
        const now2 = new Date();
        const lastDay = new Date(now2.getFullYear(), now2.getMonth() + 1, 0).getDate();
        if (now2.getDate() !== lastDay) return;
        const bonusKey = `bonus_${user.id}_${now2.getFullYear()}_${now2.getMonth() + 1}`;
        if (sessionStorage.getItem(bonusKey)) return;
        const yr = now2.getFullYear(), mo = now2.getMonth() + 1;
        const days = Array.from({ length: lastDay }, (_, i) => `${yr}-${String(mo).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`);
        const hasAbsent = days.some(d => { const st = getStatusForDate(user.id, d); return st === "absent" || st === "auto_absent" || st === "not_marked"; });
        if (!hasAbsent) {
            sessionStorage.setItem(bonusKey, "1");
            claimPoints("claim_perfect_attendance", { p_year: yr, p_month: mo }, reload);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clockIn = async () => {
        // ── Location + IP verification ──────────────────────────────────────
        const staffRecord = _staff.find(s => s.id === user.id);
        if (staffRecord?.allow_remote) {
            // Remote clock-in allowed — skip location check
        } else {
        const allowedIps = (_appSettings.office_ip || "").split(",").map(s => s.trim()).filter(Boolean);
        const officeLat = Number(_appSettings.office_lat);
        const officeLng = Number(_appSettings.office_lng);
        const officeRadius = Number(_appSettings.office_radius) || 100;

        let ipAllowed = false;
        let gpsAllowed = false;
        let verifyError = null;

        // Check IP
        try {
            const res = await fetch("https://api.ipify.org?format=json");
            const { ip } = await res.json();
            if (allowedIps.some(a => a.toLowerCase() === ip.toLowerCase())) ipAllowed = true;
        } catch { /* network error — skip IP check */ }

        // Check GPS
        if (!ipAllowed) {
            try {
                const pos = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, maximumAge: 0 });
                });
                const dist = haversineDistance(pos.coords.latitude, pos.coords.longitude, officeLat, officeLng);
                if (dist <= officeRadius) gpsAllowed = true;
                else verifyError = `You are ${Math.round(dist)}m from the office (limit: ${officeRadius}m). Please be at the office to clock in.`;
            } catch (e) {
                verifyError = e.code === 1
                    ? "Location permission denied. Please allow location access or connect to office WiFi."
                    : "Could not get your location. Please connect to office WiFi or enable GPS.";
            }
        }

        if (!ipAllowed && !gpsAllowed) {
            showToast(verifyError || "You must be at the office or on office WiFi to clock in.", "#ef4444");
            return;
        }
        } // end remote check
        // ────────────────────────────────────────────────────────────────────

        const n = new Date();
        const clockInTime = `${pad(n.getHours())}:${pad(n.getMinutes())}`;
        await db.upsertAttendance({ id: `${user.id}_${today}`, staff_id: user.id, date: today, clock_in: clockInTime, clock_in_ts: Date.now(), clock_out: null, status: "present" });
        await claimPoints("claim_attendance_points", { p_kind: "clock_in", p_date: today }, reload);
    };

    const clockOut = async () => {
        const n = new Date();
        const clockOutTime = `${pad(n.getHours())}:${pad(n.getMinutes())}`;
        await db.upsertAttendance({ id: `${user.id}_${today}`, staff_id: user.id, date: today, clock_in: rec.clock_in, clock_in_ts: rec.clock_in_ts, clock_out: clockOutTime, status: "present" });
        await claimPoints("claim_attendance_points", { p_kind: "clock_out", p_date: today }, reload);
    };

    const d = new Date();
    const summary = staff ? computeSalarySummary(user.id, d.getMonth() + 1, d.getFullYear()) : null;

    // Attendance progress for current month
    const monthDays = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const daysPassed = d.getDate();
    const monthProgress = Math.round((daysPassed / monthDays) * 100);
    const greetHour = now.getHours();
    const greeting = greetHour < 12 ? "Good morning" : greetHour < 17 ? "Good afternoon" : "Good evening";
    const attendancePct = summary && summary.workingDays > 0 ? Math.round((summary.present / summary.workingDays) * 100) : 0;

    // ── Time-of-day theme ────────────────────────────────────────────────────
    const tod = hour >= 5 && hour < 8 ? "dawn" : hour >= 8 && hour < 12 ? "morning" : hour >= 12 && hour < 17 ? "afternoon" : hour >= 17 && hour < 20 ? "evening" : "night";
    const todTheme = {
        dawn:      { bg: "linear-gradient(150deg, #1c0a00 0%, #7c2d12 35%, #c2410c 65%, #fbbf24 100%)", blob1: "#f97316", blob2: "#fbbf24", icons: [{ e: "🌅", t: -10, r: 20, s: 72, o: 0.18 }, { e: "☁️", t: 30, r: 120, s: 44, o: 0.2 }, { e: "🌤", b: 10, r: 60, s: 38, o: 0.15 }] },
        morning:   { bg: "linear-gradient(150deg, #0f172a 0%, #1e3a5f 35%, #0369a1 70%, #38bdf8 100%)", blob1: "#38bdf8", blob2: "#7dd3fc", icons: [{ e: "☀️", t: -14, r: 18, s: 80, o: 0.22 }, { e: "☁️", t: 28, r: 110, s: 50, o: 0.2 }, { e: "☁️", b: 8, r: 55, s: 34, o: 0.13 }] },
        afternoon: { bg: "linear-gradient(150deg, #0c1445 0%, #1d4ed8 45%, #0ea5e9 80%, #38bdf8 100%)", blob1: "#0ea5e9", blob2: "#38bdf8", icons: [{ e: "☀️", t: -16, r: 16, s: 90, o: 0.25 }, { e: "☁️", t: 32, r: 100, s: 46, o: 0.18 }, { e: "🌤", b: 6, r: 50, s: 32, o: 0.13 }] },
        evening:   { bg: "linear-gradient(150deg, #0f0318 0%, #7c2d12 28%, #c2410c 52%, #6b21a8 80%, #0f0318 100%)", blob1: "#f97316", blob2: "#9333ea", icons: [{ e: "🌇", t: -12, r: 16, s: 76, o: 0.2 }, { e: "✨", t: 26, r: 105, s: 32, o: 0.22 }, { e: "🌆", b: 6, r: 52, s: 36, o: 0.14 }] },
        night:     { bg: "linear-gradient(150deg, #020409 0%, #0f172a 40%, #1e1b4b 75%, #0a0015 100%)", blob1: "#6366f1", blob2: "#a855f7", icons: [{ e: "🌙", t: -10, r: 22, s: 68, o: 0.22 }, { e: "⭐", t: 30, r: 100, s: 28, o: 0.25 }, { e: "✨", b: 8, r: 55, s: 22, o: 0.2 }, { e: "⭐", t: 16, r: 75, s: 18, o: 0.15 }] },
    }[tod];

    return (
        <div>
            {/* ── HERO BANNER ── */}
            <div style={{ borderRadius: 20, overflow: "hidden", marginBottom: 20, background: todTheme.bg, border: `1px solid ${todTheme.blob1}22`, position: "relative" }}>
                {/* Decorative blobs */}
                <div style={{ position: "absolute", top: -30, right: -30, width: 160, height: 160, borderRadius: "50%", background: todTheme.blob1 + "18", pointerEvents: "none" }} />
                <div style={{ position: "absolute", bottom: -20, left: 60, width: 100, height: 100, borderRadius: "50%", background: todTheme.blob2 + "14", pointerEvents: "none" }} />
                {/* Floating time-of-day icons */}
                {todTheme.icons.map((ic, i) => (
                    <div key={i} style={{ position: "absolute", top: ic.t !== undefined ? ic.t : "auto", bottom: ic.b !== undefined ? ic.b : "auto", right: ic.r, fontSize: ic.s, opacity: ic.o, pointerEvents: "none", userSelect: "none", lineHeight: 1 }}>{ic.e}</div>
                ))}

                <div style={{ padding: mobile ? "20px 18px" : "28px 32px", position: "relative" }}>
                    <div style={{ display: "flex", flexDirection: mobile ? "column" : "row", alignItems: mobile ? "flex-start" : "center", gap: mobile ? 16 : 0 }}>
                        {/* Left: greeting */}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: C.accent, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>{greeting} ✦</div>
                            <h1 style={{ margin: "0 0 6px", fontWeight: 900, fontSize: mobile ? 26 : 34, color: C.white, lineHeight: 1.1 }}>{user.name.split(" ")[0]} <span style={{ color: C.accent }}>👋</span></h1>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ color: C.muted, fontSize: 13 }}>{staff?.designation}</span>
                                <span style={{ color: C.border }}>•</span>
                                <span style={{ color: C.muted, fontSize: 13 }}>{fmtDate(today)}</span>
                                {isModStaff && <span style={{ background: C.green + "33", color: C.green, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>🛡 Staff Mod</span>}
                                {isModFreelancer && <span style={{ background: C.purple + "33", color: C.purple, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>🟣 FL Mod</span>}
                            </div>
                        </div>
                        {/* Right: live clock */}
                        <div style={{ textAlign: mobile ? "left" : "right" }}>
                            <div style={{ fontSize: mobile ? 40 : 54, fontWeight: 900, color: C.white, letterSpacing: -3, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{timeStr}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap", justifyContent: mobile ? "flex-start" : "flex-end" }}>
                                <span>Deadline <strong style={{ color: C.yellow }}>11 AM</strong></span>
                                <span>Min stay <strong style={{ color: C.green }}>10 min</strong></span>
                                <span>Auto-out <strong style={{ color: C.red }}>6 PM</strong></span>
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ height: 1, background: `linear-gradient(90deg, ${C.accent}44, transparent)`, margin: "18px 0 16px" }} />

                    {/* Clock-in / status row */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            {/* Status dot */}
                            {(isHoliday || isSat) && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.yellow + "22", border: `1px solid ${C.yellow}33`, borderRadius: 12, padding: "8px 16px" }}>
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.yellow }} />
                                    <span style={{ color: C.yellow, fontWeight: 700, fontSize: 13 }}>{isHoliday ? "🎉 Holiday — Auto Present" : "📅 Saturday — Auto Present"}</span>
                                </div>
                            )}
                            {!isHoliday && !isSat && rec && rec.clock_out && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.green + "18", border: `1px solid ${C.green}33`, borderRadius: 12, padding: "8px 16px" }}>
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green }} />
                                    <span style={{ color: C.green, fontWeight: 700, fontSize: 13 }}>✅ Done — {rec.clock_in} → {rec.clock_out}</span>
                                </div>
                            )}
                            {!isHoliday && !isSat && rec && !rec.clock_out && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.green + "18", border: `1px solid ${C.green}33`, borderRadius: 12, padding: "8px 16px" }}>
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, animation: "pulse 1.5s infinite" }} />
                                    <span style={{ color: C.green, fontWeight: 700, fontSize: 13 }}>🟢 Working since {rec.clock_in}</span>
                                    {cooldownLeft > 0 && <span style={{ color: C.yellow, fontSize: 12 }}>— clock-out in {cooldownLeft} min</span>}
                                </div>
                            )}
                            {!isHoliday && !isSat && tooLate && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.red + "18", border: `1px solid ${C.red}33`, borderRadius: 12, padding: "8px 16px" }}>
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.red }} />
                                    <span style={{ color: C.red, fontWeight: 700, fontSize: 13 }}>⛔ Clock-in closed — Marked Absent</span>
                                </div>
                            )}
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            {canClockIn && (
                                <button onClick={clockIn} style={{ background: `linear-gradient(135deg, ${C.green}, #059669)`, color: "#fff", border: "none", borderRadius: 12, padding: mobile ? "11px 24px" : "12px 32px", fontWeight: 800, fontSize: mobile ? 14 : 15, cursor: "pointer", boxShadow: `0 4px 20px ${C.green}44`, display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ fontSize: 16 }}>🟢</span> Clock In
                                </button>
                            )}
                            {canClockOut && (
                                <button onClick={clockOut} style={{ background: `linear-gradient(135deg, ${C.red}, #dc2626)`, color: "#fff", border: "none", borderRadius: 12, padding: mobile ? "11px 24px" : "12px 32px", fontWeight: 800, fontSize: mobile ? 14 : 15, cursor: "pointer", boxShadow: `0 4px 20px ${C.red}44`, display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ fontSize: 16 }}>🔴</span> Clock Out
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── IP + LOCATION CARD ── */}
            {(() => {
                const allowedIps = (_appSettings.office_ip || "").split(",").map(s => s.trim()).filter(Boolean);
                const isOfficeIp = ipInfo.ip && allowedIps.some(a => a.toLowerCase() === ipInfo.ip.toLowerCase());
                return (
                    <div style={{ display: "flex", gap: mobile ? 12 : 20, flexWrap: "wrap", alignItems: "stretch", marginBottom: 16 }}>
                        {/* IP card */}
                        <div style={{ flex: 1, minWidth: 180, background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: (isOfficeIp ? C.green : C.accent) + "22", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Wifi size={18} color={isOfficeIp ? C.green : C.accent} />
                            </div>
                            <div>
                                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 }}>Your IP Address</div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: C.white, fontVariantNumeric: "tabular-nums" }}>
                                    {ipInfo.loading ? <span style={{ color: C.muted }}>Detecting…</span> : ipInfo.ip}
                                </div>
                                {!ipInfo.loading && ipInfo.ip && ipInfo.ip !== "Unavailable" && (
                                    <div style={{ fontSize: 11, marginTop: 3, color: isOfficeIp ? C.green : C.yellow, fontWeight: 600 }}>
                                        {isOfficeIp ? "✅ Office Network" : allowedIps.length > 0 ? "📡 Remote / External" : "📡 External"}
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Location card */}
                        <div style={{ flex: 1, minWidth: 180, background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: (locationInfo.coords ? C.green : C.muted) + "22", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <MapPin size={18} color={locationInfo.coords ? C.green : C.muted} />
                            </div>
                            <div>
                                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 }}>Your Location</div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: locationInfo.coords ? C.white : C.muted }}>
                                    {locationInfo.loading ? <span style={{ color: C.muted }}>Detecting…</span>
                                        : locationInfo.denied ? "Not enabled"
                                        : locationInfo.label || "Unknown"}
                                </div>
                                {locationInfo.coords && (
                                    <div style={{ fontSize: 11, marginTop: 3, color: C.muted }}>
                                        {locationInfo.coords.lat.toFixed(4)}, {locationInfo.coords.lon.toFixed(4)}
                                    </div>
                                )}
                                {locationInfo.denied && (
                                    <div style={{ fontSize: 11, marginTop: 3, color: C.muted }}>Enable location to see this</div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {summary && (
                <>
                    {/* ── SALARY + STATS ROW ── */}
                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1.4fr 1fr", gap: mobile ? 12 : 16, marginBottom: 16 }}>
                        {/* Salary card */}
                        <div style={{ background: `linear-gradient(135deg, #0f172a, #1a1040)`, border: `1px solid ${C.accent}33`, borderRadius: 20, padding: mobile ? "18px 16px" : "22px 26px", position: "relative", overflow: "hidden" }}>
                            <div style={{ position: "absolute", top: -20, right: -20, width: 120, height: 120, borderRadius: "50%", background: C.yellow + "0d", pointerEvents: "none" }} />
                            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
                                💰 {now.toLocaleString("default", { month: "long" })} Salary
                            </div>
                            <div style={{ fontSize: mobile ? 34 : 44, fontWeight: 900, color: C.white, letterSpacing: -1, lineHeight: 1 }}>
                                Rs.<span style={{ color: C.yellow }}>{summary.netSalary.toLocaleString()}</span>
                            </div>
                            <div style={{ fontSize: 13, color: C.muted, marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap" }}>
                                <span>Gross <strong style={{ color: C.white }}>Rs.{summary.grossSalary.toLocaleString()}</strong></span>
                                {summary.deduction > 0 && <span>Deduction <strong style={{ color: C.red }}>-Rs.{summary.deduction.toLocaleString()}</strong></span>}
                                <span style={{ color: C.muted }}>Rs.{summary.perDaySalary}/day</span>
                            </div>
                            {/* Month progress bar */}
                            <div style={{ marginTop: 16 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                    <span style={{ fontSize: 11, color: C.muted }}>Month progress</span>
                                    <span style={{ fontSize: 11, color: C.accent, fontWeight: 700 }}>{monthProgress}% ({daysPassed}/{monthDays} days)</span>
                                </div>
                                <div style={{ height: 6, borderRadius: 4, background: C.border, overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: `${monthProgress}%`, background: `linear-gradient(90deg, ${C.accent}, ${C.purple})`, borderRadius: 4, transition: "width 0.6s ease" }} />
                                </div>
                            </div>
                            {(() => { const p = _salaryPayments.filter(r => r.staff_id === user.id && r.status === "pending"); return p.length > 0 ? <div style={{ marginTop: 12, padding: "7px 14px", background: C.yellow + "18", border: `1px solid ${C.yellow}33`, borderRadius: 10, fontSize: 12, color: C.yellow, fontWeight: 700, display: "inline-block" }}>⏳ {p.length} salary payment(s) pending</div> : null; })()}
                        </div>

                        {/* Attendance breakdown */}
                        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mobile ? "18px 16px" : "22px 24px" }}>
                            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 16 }}>📅 Attendance</div>
                            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                                <div style={{ flex: 1, background: C.green + "15", border: `1px solid ${C.green}33`, borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
                                    <div style={{ fontSize: 28, fontWeight: 900, color: C.green }}>{summary.present}</div>
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Present</div>
                                </div>
                                <div style={{ flex: 1, background: C.red + "15", border: `1px solid ${C.red}33`, borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
                                    <div style={{ fontSize: 28, fontWeight: 900, color: C.red }}>{summary.absent}</div>
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Absent</div>
                                </div>
                                <div style={{ flex: 1, background: C.accent + "15", border: `1px solid ${C.accent}33`, borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
                                    <div style={{ fontSize: 28, fontWeight: 900, color: C.accent }}>{summary.workingDays}</div>
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Days</div>
                                </div>
                            </div>
                            {/* Attendance rate bar */}
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                    <span style={{ fontSize: 11, color: C.muted }}>Attendance rate</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: attendancePct >= 80 ? C.green : attendancePct >= 60 ? C.yellow : C.red }}>{attendancePct}%</span>
                                </div>
                                <div style={{ height: 6, borderRadius: 4, background: C.border, overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: `${attendancePct}%`, background: attendancePct >= 80 ? `linear-gradient(90deg, ${C.green}, #059669)` : attendancePct >= 60 ? `linear-gradient(90deg, ${C.yellow}, #d97706)` : `linear-gradient(90deg, ${C.red}, #dc2626)`, borderRadius: 4, transition: "width 0.6s ease" }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ── MOOD CHECK-IN ── */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: mobile ? "18px 16px" : "22px 24px", marginBottom: 0 }}>
                <MoodCheckin user={user} reload={reload} />
            </div>

            {/* Staff of the Month banner */}
            <StaffOfMonth isAdmin={false} reload={reload} />

            {/* Pinned Announcements */}
            {_announcements.length > 0 && (
                <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>📢 ANNOUNCEMENTS</div>
                    <AnnouncementsPanel user={user} isAdmin={false} reload={reload} />
                </div>
            )}

            {/* ── ID Card ── */}
            {(() => { const s = _staff.find(x => x.id === user.id); if (!s) return null; return (
                <div style={{ ...styles.card, marginTop: 20 }}><IdCardWithDownload staff={s} /></div>
            ); })()}

            {/* Birthday today banner */}
            <BirthdayBanner />
        </div>
    );
}

// ─── Staff: My Attendance ──────────────────────────────────────────────────────────
function StaffAttendance({ user }) {
    const mobile = useMobile();
    const [filterType, setFilterType] = useState("this_month");
    const [customStart, setCustomStart] = useState(todayStr());
    const [customEnd, setCustomEnd] = useState(todayStr());
    const now = new Date();

    const getDateRange = () => {
        if (filterType === "this_month") { const s = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`; const e = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate())}`; return [s, e]; }
        if (filterType === "last_month") { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); const s = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; const e = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate())}`; return [s, e]; }
        return [customStart, customEnd];
    };
    const [startDate, endDate] = getDateRange();
    const today = todayStr();
    const effectiveEnd = endDate > today ? today : endDate;
    const days = daysInRange(startDate, effectiveEnd);
    const staff = _staff.find(s => s.id === user.id);

    let present = 0, absent = 0;
    const rows = days.map(d => {
        const st = getStatusForDate(user.id, d);
        const rec = getAttendanceRecord(user.id, d);
        if (st === "present" || st === "saturday" || st === "holiday") present++;
        else if (st === "absent" || st === "auto_absent") absent++;
        return { date: d, status: st, clockIn: rec?.clock_in || "—", clockOut: rec?.clock_out || "—" };
    });

    const exportCSV = () => {
        const csvRows = [["Date", "Day", "Clock In", "Clock Out", "Status"]];
        rows.forEach(r => csvRows.push([r.date, ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date(r.date).getDay()], r.clockIn, r.clockOut, r.status]));
        const csv = csvRows.map(r => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `my_attendance_${startDate}_${effectiveEnd}.csv`; a.click();
    };

    const deduction = staff ? Math.round(rows.reduce((a, r) => (r.status === "absent" || r.status === "auto_absent" ? a + absenceDeduction(staff.id, r.date) : a), 0)) : 0;
    const monthSalary = staff ? currentSalaryOf(staff.id, startDate) : 0;

    return (
        <div>
            <div style={{ display: "flex", alignItems: mobile ? "flex-start" : "center", flexDirection: mobile ? "column" : "row", justifyContent: "space-between", marginBottom: 24, gap: 12 }}>
                <h2 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>My Attendance</h2>
                <button style={styles.btn(C.green)} onClick={exportCSV}>⬇ Export CSV</button>
            </div>
            <div style={{ ...styles.card, marginBottom: 20 }}>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>FILTER</label>
                        <select style={styles.input} value={filterType} onChange={e => setFilterType(e.target.value)}>
                            <option value="this_month">This Month</option><option value="last_month">Last Month</option><option value="custom">Custom Range</option>
                        </select></div>
                    {filterType === "custom" && <>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>FROM</label><input style={styles.input} type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} /></div>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>TO</label><input style={styles.input} type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} /></div>
                    </>}
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
                <StatCard icon="✅" label="Present" value={present} color={C.green} />
                <StatCard icon="❌" label="Absent" value={absent} color={C.red} />
                <StatCard icon="💸" label="Deduction" value={`Rs.${deduction.toLocaleString()}`} color={C.red} />
                <StatCard icon="💰" label="Net Salary" value={`Rs.${(monthSalary - deduction).toLocaleString()}`} color={C.green} />
            </div>
            <div style={styles.card}>
                {mobile ? (
                    <div>
                        {rows.map(r => (
                            <div key={r.date} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                                <div>
                                    <div style={{ fontWeight: 700, color: C.white, fontSize: 13 }}>{fmtDate(r.date)} <span style={{ color: new Date(r.date).getDay() === 0 ? C.purple : C.muted, fontWeight: 400 }}>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date(r.date).getDay()]}</span></div>
                                    {r.clockIn !== "—" && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{r.clockIn} → {r.clockOut}</div>}
                                </div>
                                <StatusBadge status={r.status} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}><table style={{ ...styles.table, minWidth: 480 }}>
                        <thead><tr>
                            <th style={styles.th}>Date</th><th style={styles.th}>Day</th>
                            <th style={styles.th}>Clock In</th><th style={styles.th}>Clock Out</th><th style={styles.th}>Status</th>
                        </tr></thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.date}>
                                    <td style={styles.td}>{fmtDate(r.date)}</td>
                                    <td style={{ ...styles.td, color: new Date(r.date).getDay() === 0 ? C.purple : C.muted }}>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date(r.date).getDay()]}</td>
                                    <td style={styles.td}>{r.clockIn}</td>
                                    <td style={styles.td}>{r.clockOut}</td>
                                    <td style={styles.td}><StatusBadge status={r.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table></div>
                )}
            </div>
        </div>
    );
}

// ─── Root App ─────────────────────────────────────────────────────────────────────

// Salary pages (admin + staff) live in src/finance/SalaryPages.js

// ─── Admin Commissions Page ───────────────────────────────────────────────────
function AdminCommissionsPage({ reload }) {
    const mobile = useMobile();
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const emptyForm = { staff_id: _staff[0]?.id || "", project_name: "", project_value: "", commission_percent: "", description: "", date: new Date().toISOString().slice(0, 10) };
    const [tab, setTab] = useState("outstanding");
    const [form, setForm] = useState(emptyForm);
    const [editId, setEditId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [filterStaff, setFilterStaff] = useState("all");
    const [filterYear, setFilterYear] = useState("all");
    const [filterMonth, setFilterMonth] = useState("all");
    const [payModal, setPayModal] = useState(null); // commission record being paid
    const [payForm, setPayForm] = useState({ txn_number: "", payment_method: "bank_transfer" });
    const [paying, setPaying] = useState(false);

    const commissionAmount = form.project_value && form.commission_percent
        ? Math.round(Number(form.project_value) * Number(form.commission_percent) / 100) : 0;

    const outstanding = _commissions.filter(c => c.status !== "paid");
    const paid = _commissions.filter(c => c.status === "paid");
    const list = tab === "outstanding" ? outstanding : paid;

    const totalOutstanding = outstanding.reduce((a, c) => a + Number(c.commission_amount), 0);
    const totalPaid = paid.reduce((a, c) => a + Number(c.commission_amount), 0);
    const thisMonth = new Date().getMonth() + 1;
    const thisYear = new Date().getFullYear();
    const thisMonthTotal = _commissions.filter(c => c.month === thisMonth && c.year === thisYear).reduce((a, c) => a + Number(c.commission_amount), 0);

    const commissionYears = [...new Set(_commissions.map(c => c.year))].sort((a, b) => b - a);
    const hasFilters = filterStaff !== "all" || filterYear !== "all" || filterMonth !== "all" || search !== "";

    const filtered = list.filter(c =>
        (filterStaff === "all" || c.staff_id === filterStaff) &&
        (filterYear === "all" || c.year === Number(filterYear)) &&
        (filterMonth === "all" || c.month === Number(filterMonth)) &&
        (search === "" || getStaffName(c.staff_id).toLowerCase().includes(search.toLowerCase()) || c.project_name.toLowerCase().includes(search.toLowerCase()))
    );

    const openAdd = () => { setForm(emptyForm); setEditId(null); setShowForm(true); };
    const openEdit = (c) => {
        setForm({ staff_id: c.staff_id, project_name: c.project_name, project_value: c.project_value, commission_percent: c.commission_percent, description: c.description || "", date: c.date });
        setEditId(c.id); setShowForm(true);
    };

    const save = async () => {
        if (!form.staff_id || !form.project_name || !form.project_value || !form.commission_percent || !form.date) return;
        setSaving(true);
        try {
            const d = new Date(form.date);
            const payload = { staff_id: form.staff_id, project_name: form.project_name, project_value: Number(form.project_value), commission_percent: Number(form.commission_percent), commission_amount: commissionAmount, date: form.date, month: d.getMonth() + 1, year: d.getFullYear(), description: form.description };
            if (editId) {
                await db.updateCommission(editId, payload);
                setShowForm(false); setEditId(null);
                await reload();
                showToast("Commission updated successfully!");
            } else {
                const id = "com" + Date.now();
                await db.addCommission({ ...payload, id, status: "outstanding", created_at: new Date().toISOString() });
                await db.addNotification({ id: "notif_com_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6), recipient_id: form.staff_id, type: "commission", title: "Commission Received! 🏆", message: `You've earned a commission of Rs.${commissionAmount.toLocaleString()} for "${form.project_name}" (${form.commission_percent}% of Rs.${Number(form.project_value).toLocaleString()}).`, read: false, created_at: new Date().toISOString() });
                setShowForm(false); setForm(emptyForm);
                await reload();
                showToast(`Commission of Rs.${commissionAmount.toLocaleString()} added for ${_staff.find(s => s.id === form.staff_id)?.name || "staff"}!`);
            }
        } catch (e) {
            showToast("Failed to save: " + (e?.message || "Unknown error"), C.red);
        } finally { setSaving(false); }
    };

    const markPaid = async () => {
        if (!payModal) return;
        setPaying(true);
        try {
            await db.updateCommission(payModal.id, { status: "paid", paid_at: new Date().toISOString(), txn_number: payForm.txn_number, payment_method: payForm.payment_method });
            setPayModal(null); setPayForm({ txn_number: "", payment_method: "bank_transfer" });
            await reload();
            showToast("Commission marked as paid!");
        } catch (e) {
            showToast("Failed to update: " + (e?.message || "Unknown error"), C.red);
        } finally { setPaying(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this commission?")) return;
        await db.deleteCommission(id); await reload();
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                <div>
                    <h2 style={{ margin: 0, fontWeight: 900, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><DollarSign size={22} /> Commissions</h2>
                    <p style={{ margin: "4px 0 0", color: C.muted, fontSize: 13 }}>Manage and release staff commission payments</p>
                </div>
                <button style={styles.btn(C.accent)} onClick={openAdd}>+ Add Commission</button>
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: mobile ? 10 : 16, marginBottom: 24 }}>
                <div style={{ ...styles.card, border: `1px solid ${C.yellow}44`, background: C.yellow + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>OUTSTANDING</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.yellow }}>Rs.{totalOutstanding.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{outstanding.length} record(s)</div>
                </div>
                <div style={{ ...styles.card, border: `1px solid ${C.green}44`, background: C.green + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>PAID</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.green }}>Rs.{totalPaid.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{paid.length} record(s)</div>
                </div>
                <div style={{ ...styles.card, border: `1px solid #a855f744`, background: "#a855f708" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>THIS MONTH</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: "#a855f7" }}>Rs.{thisMonthTotal.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{monthNames[thisMonth - 1]} {thisYear}</div>
                </div>
                <div style={{ ...styles.card, border: `1px solid ${C.accent}44`, background: C.accent + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>STAFF REWARDED</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.accent }}>{new Set(_commissions.map(c => c.staff_id)).size}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>unique members</div>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: `2px solid ${C.border}` }}>
                {[["outstanding", "⏳ Outstanding", outstanding.length], ["paid", "✅ Paid", paid.length]].map(([id, label, count]) => (
                    <button key={id} onClick={() => setTab(id)} style={{ padding: "10px 24px", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", background: "transparent", borderBottom: tab === id ? `3px solid ${C.accent}` : "3px solid transparent", color: tab === id ? C.accent : C.muted, marginBottom: -2 }}>
                        {label} <span style={{ background: tab === id ? C.accent + "33" : C.card2, color: tab === id ? C.accent : C.muted, borderRadius: 20, padding: "1px 8px", fontSize: 12, marginLeft: 4 }}>{count}</span>
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <input style={{ ...styles.input, minWidth: 200, flex: 1, maxWidth: 280 }} placeholder="Search staff or project..." value={search} onChange={e => setSearch(e.target.value)} />
                <select style={{ ...styles.input, minWidth: 160 }} value={filterStaff} onChange={e => setFilterStaff(e.target.value)}>
                    <option value="all">All Staff</option>
                    {_staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select style={{ ...styles.input, minWidth: 100 }} value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                    <option value="all">All Years</option>
                    {commissionYears.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select style={{ ...styles.input, minWidth: 120 }} value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                    <option value="all">All Months</option>
                    {monthNames.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
                {hasFilters && <button style={{ ...styles.btn(C.card2), fontSize: 12, padding: "8px 14px" }} onClick={() => { setFilterStaff("all"); setFilterYear("all"); setFilterMonth("all"); setSearch(""); }}>✕ Clear</button>}
            </div>

            {filtered.length === 0 ? (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>{hasFilters ? "No results for selected filters." : `No ${tab} commissions.`}</div>
            ) : (
                <div style={styles.card}>
                    {filtered.map(c => (
                        <div key={c.id} style={{ padding: "14px 0", borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, color: C.white, fontSize: 15 }}>{getStaffName(c.staff_id)}</div>
                                    <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>📁 {c.project_name}</div>
                                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{c.commission_percent}% of Rs.{Number(c.project_value).toLocaleString()} &nbsp;•&nbsp; {monthNames[c.month - 1]} {c.year}</div>
                                    {c.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>📝 {c.description}</div>}
                                    {c.txn_number && <div style={{ fontSize: 12, color: C.accent, marginTop: 3 }}>🔖 Txn: {c.txn_number}</div>}
                                    {c.paid_at && <div style={{ fontSize: 11, color: C.green, marginTop: 3 }}>Paid on {new Date(c.paid_at).toLocaleDateString()}</div>}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                    <span style={{ fontWeight: 900, fontSize: 18, color: "#a855f7" }}>Rs.{Number(c.commission_amount).toLocaleString()}</span>
                                    {tab === "outstanding" && <button style={{ ...styles.btn(C.green), padding: "5px 12px", fontSize: 12 }} onClick={() => { setPayModal(c); setPayForm({ txn_number: "" }); }}>✅ Mark Paid</button>}
                                    <button style={{ ...styles.btn(C.accent2), padding: "5px 12px", fontSize: 12 }} onClick={() => openEdit(c)}>Edit</button>
                                    <button style={{ ...styles.btn(C.red), padding: "5px 12px", fontSize: 12 }} onClick={() => remove(c.id)}>🗑</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Mark Commission Paid Modal */}
            {payModal && (
                <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                    <div style={{ ...styles.card, width: "100%", maxWidth: 420 }}>
                        <h3 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 18 }}>Mark Commission Paid</h3>
                        <p style={{ margin: "0 0 16px", color: C.muted, fontSize: 13 }}>{getStaffName(payModal.staff_id)} — {payModal.project_name}</p>
                        <div style={{ padding: "12px 16px", background: "#a855f711", borderRadius: 10, border: "1px solid #a855f733", textAlign: "center", marginBottom: 16 }}>
                            <div style={{ fontSize: 12, color: "#a855f7", fontWeight: 600 }}>Commission Amount</div>
                            <div style={{ fontSize: 28, fontWeight: 900, color: "#a855f7" }}>Rs.{Number(payModal.commission_amount).toLocaleString()}</div>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PAYMENT METHOD *</label>
                            <select style={styles.input} value={payForm.payment_method} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}>
                                <option value="esewa">eSewa</option>
                                <option value="khalti">Khalti</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="cash">Cash</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>TRANSACTION NUMBER (optional)</label>
                            <input style={styles.input} placeholder="e.g. TXN-20240408-001" value={payForm.txn_number} onChange={e => setPayForm(f => ({ ...f, txn_number: e.target.value }))} />
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            <button style={{ ...styles.btn(C.green), flex: 1 }} onClick={markPaid} disabled={paying}>{paying ? "Saving..." : "✅ Confirm Paid"}</button>
                            <button style={{ ...styles.btn(C.card2), flex: 1 }} onClick={() => setPayModal(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Commission Modal */}
            {showForm && (
                <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                    <div style={{ ...styles.card, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                            <h3 style={{ margin: 0, fontWeight: 800, fontSize: 18 }}>{editId ? "Edit Commission" : "Add Commission"}</h3>
                            <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 20 }}>×</button>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            <div>
                                <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>STAFF MEMBER</label>
                                <select style={styles.input} value={form.staff_id} onChange={e => setForm(f => ({ ...f, staff_id: e.target.value }))}>
                                    {_staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.employment_type === "freelancer" ? "Freelancer" : s.employment_type === "part_time" ? "Part Time" : "Full Time"})</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PROJECT NAME</label>
                                <input style={styles.input} placeholder="e.g. Website Redesign Q1" value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div>
                                    <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>PROJECT VALUE (Rs.)</label>
                                    <input style={styles.input} type="number" placeholder="e.g. 100000" value={form.project_value} onChange={e => setForm(f => ({ ...f, project_value: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>COMMISSION %</label>
                                    <input style={styles.input} type="number" placeholder="e.g. 10" value={form.commission_percent} onChange={e => setForm(f => ({ ...f, commission_percent: e.target.value }))} />
                                </div>
                            </div>
                            {commissionAmount > 0 && (
                                <div style={{ padding: "12px 16px", background: "#a855f711", borderRadius: 10, border: "1px solid #a855f733", textAlign: "center" }}>
                                    <div style={{ fontSize: 12, color: "#a855f7", fontWeight: 600 }}>Commission Amount</div>
                                    <div style={{ fontSize: 28, fontWeight: 900, color: "#a855f7" }}>Rs.{commissionAmount.toLocaleString()}</div>
                                </div>
                            )}
                            <div>
                                <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>DATE</label>
                                <input style={styles.input} type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>DESCRIPTION (optional)</label>
                                <textarea style={{ ...styles.input, minHeight: 70, resize: "vertical" }} placeholder="Any notes..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                                <button style={{ ...styles.btn(C.accent), flex: 1 }} onClick={save} disabled={saving || !form.staff_id || !form.project_name || !form.project_value || !form.commission_percent}>
                                    {saving ? "Saving..." : editId ? "Update" : "Add Commission"}
                                </button>
                                <button style={{ ...styles.btn(C.card2), flex: 1 }} onClick={() => setShowForm(false)}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── My Commissions Page (Staff) ──────────────────────────────────────────────
function MyCommissionsPage({ user }) {
    const mobile = useMobile();
    const [tab, setTab] = useState("outstanding");
    const myCommissions = _commissions.filter(c => c.staff_id === user.id);
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    const downloadCommissionSlip = async (c) => {
        const staff = _staff.find(s => s.id === user.id);
        const logo = await loadLogoBase64();
        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const W = 210, mL = 20, mR = 20, cW = W - mL - mR;
        let y = 20;
        pdf.setFillColor(17, 24, 39);
        pdf.rect(0, 0, W, 42, "F");
        if (logo) { try { pdf.addImage(logo, "PNG", mL, 8, 22, 22); } catch (_) {} }
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18); pdf.setFont("helvetica", "bold");
        pdf.text("COMMISSION SLIP", mL + 26, 18);
        pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
        pdf.text("Foxbeep Technology Pvt. Ltd.", mL + 26, 26);
        pdf.text(`${monthNames[c.month - 1]} ${c.year}`, W - mR, 18, { align: "right" });
        pdf.text(c.status === "paid" ? "Status: PAID" : "Status: OUTSTANDING", W - mR, 26, { align: "right" });
        y = 52;
        pdf.setTextColor(30, 30, 30);
        pdf.setFontSize(11); pdf.setFont("helvetica", "bold");
        pdf.text("Employee Details", mL, y); y += 7;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
        pdf.text("Name:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(staff?.name || user.name, mL + 35, y); y += 6;
        pdf.setFont("helvetica", "normal");
        pdf.text("Designation:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(staff?.designation || "—", mL + 35, y); y += 6;
        pdf.setFont("helvetica", "normal");
        pdf.text("Period:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(`${monthNames[c.month - 1]} ${c.year}`, mL + 35, y); y += 12;
        pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 8;
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(30, 30, 30);
        pdf.text("Project Details", mL, y); y += 7;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
        pdf.text("Project:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(c.project_name || "—", mL + 35, y); y += 6;
        pdf.setFont("helvetica", "normal");
        pdf.text("Project Value:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(`Rs. ${Number(c.project_value || 0).toLocaleString()}`, mL + 35, y); y += 6;
        pdf.setFont("helvetica", "normal");
        pdf.text("Commission Rate:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(`${c.commission_percent}%`, mL + 35, y); y += 6;
        pdf.setFont("helvetica", "normal");
        if (c.description) { pdf.text("Note:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(c.description, mL + 35, y); pdf.setFont("helvetica", "normal"); y += 6; }
        y += 6;
        pdf.setDrawColor(220, 220, 220); pdf.line(mL, y, W - mR, y); y += 6;
        pdf.setFillColor(245, 255, 245);
        pdf.roundedRect(mL, y, cW, 16, 2, 2, "FD");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(20, 120, 60);
        pdf.text("COMMISSION AMOUNT", mL + 6, y + 10);
        pdf.text(`Rs. ${Number(c.commission_amount).toLocaleString()}`, W - mR - 6, y + 10, { align: "right" });
        y += 24;
        if (c.status === "paid" && (c.payment_method || c.txn_number || c.paid_at)) {
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(30, 30, 30);
            pdf.text("Payment Details", mL, y); y += 7;
            pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
            if (c.payment_method) { pdf.text("Method:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(c.payment_method.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase()), mL + 35, y); pdf.setFont("helvetica", "normal"); y += 6; }
            if (c.txn_number) { pdf.text("Txn #:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(c.txn_number, mL + 35, y); pdf.setFont("helvetica", "normal"); y += 6; }
            if (c.paid_at) { pdf.text("Paid on:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(new Date(c.paid_at).toLocaleDateString(), mL + 35, y); pdf.setFont("helvetica", "normal"); y += 6; }
            y += 4;
        }
        // Company footer
        y += 4;
        pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 5;
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(60, 60, 60);
        pdf.text("Foxbeep Technology Pvt. Ltd.", mL, y); y += 5;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(120, 120, 120);
        pdf.text("Madhyapur Thimi, Bhaktapur, Nepal  \u2022  Ph: +977-9705516131", mL, y); y += 4;
        pdf.text("Foxbeeptech@gmail.com  \u2022  Support@foxbeep.com  \u2022  www.foxbeep.com", mL, y); y += 6;
        pdf.setFont("helvetica", "italic"); pdf.setFontSize(8);
        pdf.text((c.status === "paid" ? "Payment released. " : "Payment pending. ") + "Computer-generated document. Generated: " + new Date().toLocaleDateString(), mL, y);
        pdf.save(`commission_slip_${c.project_name?.replace(/\s+/g, "_") || "project"}_${monthNames[c.month - 1]}_${c.year}.pdf`);
    };

    const outstanding = myCommissions.filter(c => c.status !== "paid");
    const paid = myCommissions.filter(c => c.status === "paid");
    const list = tab === "outstanding" ? outstanding : paid;

    const totalEarned = myCommissions.reduce((a, c) => a + Number(c.commission_amount), 0);
    const totalOutstanding = outstanding.reduce((a, c) => a + Number(c.commission_amount), 0);
    const totalPaid = paid.reduce((a, c) => a + Number(c.commission_amount), 0);
    const thisMonth = new Date().getMonth() + 1;
    const thisYear = new Date().getFullYear();
    const thisMonthEarned = myCommissions.filter(c => c.month === thisMonth && c.year === thisYear).reduce((a, c) => a + Number(c.commission_amount), 0);

    return (
        <div>
            <h2 style={{ margin: "0 0 4px", fontWeight: 900, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><DollarSign size={22} /> My Commission</h2>
            <p style={{ margin: "0 0 8px", color: C.muted, fontSize: 13 }}>Your commission earnings from projects</p>
            <div style={{ margin: "0 0 24px", padding: "10px 16px", background: "#f59e0b11", border: "1px solid #f59e0b44", borderRadius: 10, fontSize: 13, color: "#f59e0b", fontWeight: 600 }}>
                💡 Refer a project to Foxbeep and earn 5% of the project value as a cash reward. Good Luck!
            </div>

            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: mobile ? 10 : 16, marginBottom: 24 }}>
                <div style={{ ...styles.card, border: `1px solid ${C.yellow}44`, background: C.yellow + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>OUTSTANDING</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.yellow }}>Rs.{totalOutstanding.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{outstanding.length} pending</div>
                </div>
                <div style={{ ...styles.card, border: `1px solid ${C.green}44`, background: C.green + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>PAID</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.green }}>Rs.{totalPaid.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{paid.length} released</div>
                </div>
                <div style={{ ...styles.card, border: `1px solid #a855f744`, background: "#a855f708" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>TOTAL EARNED</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: "#a855f7" }}>Rs.{totalEarned.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{myCommissions.length} total</div>
                </div>
                <div style={{ ...styles.card, border: `1px solid ${C.accent}44`, background: C.accent + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>THIS MONTH</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.accent }}>Rs.{thisMonthEarned.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{monthNames[thisMonth - 1]} {thisYear}</div>
                </div>
            </div>

            <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: `2px solid ${C.border}` }}>
                {[["outstanding", "⏳ Outstanding", outstanding.length], ["paid", "✅ Paid", paid.length]].map(([id, label, count]) => (
                    <button key={id} onClick={() => setTab(id)} style={{ padding: "10px 24px", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", background: "transparent", borderBottom: tab === id ? `3px solid ${C.accent}` : "3px solid transparent", color: tab === id ? C.accent : C.muted, marginBottom: -2 }}>
                        {label} <span style={{ background: tab === id ? C.accent + "33" : C.card2, color: tab === id ? C.accent : C.muted, borderRadius: 20, padding: "1px 8px", fontSize: 12, marginLeft: 4 }}>{count}</span>
                    </button>
                ))}
            </div>

            {list.length === 0 ? (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>🏆</div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>No {tab} commissions yet</div>
                </div>
            ) : (
                <div style={styles.card}>
                    {list.map(c => (
                        <div key={c.id} style={{ padding: "16px 0", borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, color: C.white, fontSize: 15 }}>📁 {c.project_name}</div>
                                    <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{c.commission_percent}% of Rs.{Number(c.project_value).toLocaleString()} &nbsp;•&nbsp; {monthNames[c.month - 1]} {c.year}</div>
                                    {c.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>📝 {c.description}</div>}
                                    {c.payment_method && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>💳 {c.payment_method.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}</div>}
                                    {c.txn_number && <div style={{ fontSize: 12, color: C.accent, marginTop: 3 }}>🔖 Txn: {c.txn_number}</div>}
                                    {c.paid_at && <div style={{ fontSize: 11, color: C.green, marginTop: 3 }}>Released on {new Date(c.paid_at).toLocaleDateString()}</div>}
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: c.status === "paid" ? C.green : "#a855f7" }}>Rs.{Number(c.commission_amount).toLocaleString()}</div>
                                    <span style={styles.badge(c.status === "paid" ? C.green : C.yellow)}>{c.status === "paid" ? "✅ Paid" : "⏳ Outstanding"}</span>
                                    <button onClick={() => downloadCommissionSlip(c)} style={{ ...styles.btn(C.accent), padding: "5px 10px", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                                        <Download size={12} /> PDF
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Admin ID Cards Page ───────────────────────────────────────────────────────
function AdminIdCardsPage() {
    const mobile = useMobile();
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");

    const filtered = _staff.filter(s => {
        const q = search.toLowerCase();
        return (s.name.toLowerCase().includes(q) || (s.designation || "").toLowerCase().includes(q)) &&
            (typeFilter === "all" || s.employment_type === typeFilter);
    }).sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div>
            <div style={{ display: "flex", alignItems: mobile ? "flex-start" : "center", flexDirection: mobile ? "column" : "row", justifyContent: "space-between", marginBottom: 24, gap: 12 }}>
                <div>
                    <h2 style={{ margin: "0 0 4px", fontWeight: 900, fontSize: 22 }}>🪪 Staff ID Cards</h2>
                    <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Preview & download identity cards — 86 × 54mm credit card size</p>
                </div>
                <span style={styles.badge(C.accent)}>{_staff.length} staff</span>
            </div>

            {/* Filters */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }}>
                <input placeholder="Search by name or role…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...styles.input, flex: 1, minWidth: 180, maxWidth: 280 }} />
                {[["all", "All"], ["full_time", "Full Time"], ["part_time", "Part Time"], ["freelancer", "Freelancer"]].map(([v, l]) => (
                    <button key={v} onClick={() => setTypeFilter(v)} style={{ padding: "7px 13px", borderRadius: 8, border: `1px solid ${typeFilter === v ? C.accent : C.border}`, background: typeFilter === v ? C.accent + "22" : C.card2, color: typeFilter === v ? C.accent : C.muted, fontWeight: typeFilter === v ? 700 : 500, fontSize: 12, cursor: "pointer" }}>{l}</button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div style={{ ...styles.card, textAlign: "center", padding: 48, color: C.muted }}>No staff found</div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(auto-fill, minmax(360px, 1fr))", gap: 20 }}>
                    {filtered.map(staff => (
                        <div key={staff.id} style={styles.card}>
                            <IdCardWithDownload staff={staff} scale={mobile ? 2.8 : 3.4} compact />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Bonus pages (admin + staff) live in src/finance/BonusPages.js

// ─── Freelancer Withdrawal History ───────────────────────────────────────────
function FreelancerWithdrawalHistory({ user }) {
    const mobile = useMobile();
    const now = new Date();
    const [fromDate, setFromDate] = useState(() => { const d = new Date(now.getFullYear(), now.getMonth() - 2, 1); return d.toISOString().slice(0, 10); });
    const [toDate, setToDate] = useState(() => now.toISOString().slice(0, 10));

    const allRequests = _withdrawRequests.filter(r => r.freelancer_id === user.id);
    const filtered = allRequests.filter(r => {
        const d = new Date(r.created_at);
        return d >= new Date(fromDate) && d <= new Date(toDate + "T23:59:59");
    }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const totalCleared = filtered.filter(r => r.status === "cleared").reduce((a, r) => a + Number(r.amount), 0);
    const totalPending = filtered.filter(r => r.status === "pending").reduce((a, r) => a + Number(r.amount), 0);

    const downloadWithdrawalSlip = async (r) => {
        const staff = _staff.find(s => s.id === user.id);
        const logo = await loadLogoBase64();
        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const W = 210, mL = 20, mR = 20, cW = W - mL - mR;
        let y = 20;
        pdf.setFillColor(17, 24, 39);
        pdf.rect(0, 0, W, 42, "F");
        if (logo) { try { pdf.addImage(logo, "PNG", mL, 8, 22, 22); } catch (_) {} }
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18); pdf.setFont("helvetica", "bold");
        pdf.text("WITHDRAWAL SLIP", mL + 26, 18);
        pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
        pdf.text("Foxbeep Technology Pvt. Ltd.", mL + 26, 26);
        const slipDate = r.cleared_at ? new Date(r.cleared_at) : new Date(r.created_at);
        pdf.text(`${slipDate.toLocaleDateString()}`, W - mR, 18, { align: "right" });
        pdf.text(r.status === "cleared" ? "Status: CLEARED" : "Status: PENDING", W - mR, 26, { align: "right" });
        y = 52;
        pdf.setTextColor(30, 30, 30);
        pdf.setFontSize(11); pdf.setFont("helvetica", "bold");
        pdf.text("Freelancer Details", mL, y); y += 7;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
        pdf.text("Name:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(staff?.name || user.name, mL + 40, y); y += 6;
        pdf.setFont("helvetica", "normal");
        pdf.text("Designation:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(staff?.designation || "Freelancer", mL + 40, y); y += 6;
        pdf.setFont("helvetica", "normal");
        if (staff?.bank_name) { pdf.text("Bank:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(staff.bank_name, mL + 40, y); pdf.setFont("helvetica", "normal"); y += 6; }
        if (staff?.bank_account) { pdf.text("Account #:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(staff.bank_account, mL + 40, y); pdf.setFont("helvetica", "normal"); y += 6; }
        if (staff?.esewa_number) { pdf.text("eSewa:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(staff.esewa_number, mL + 40, y); pdf.setFont("helvetica", "normal"); y += 6; }
        y += 4;
        pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 8;
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(11);
        pdf.text("Withdrawal Details", mL, y); y += 7;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
        pdf.text("Requested on:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(new Date(r.created_at).toLocaleDateString(), mL + 40, y); pdf.setFont("helvetica", "normal"); y += 6;
        if (r.note) { pdf.text("Note:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(r.note, mL + 40, y); pdf.setFont("helvetica", "normal"); y += 6; }
        y += 6;
        pdf.setDrawColor(220, 220, 220); pdf.line(mL, y, W - mR, y); y += 6;
        const boxColor = r.status === "cleared" ? [245, 255, 245] : [255, 251, 235];
        const textColor = r.status === "cleared" ? [20, 120, 60] : [180, 120, 0];
        pdf.setFillColor(...boxColor);
        pdf.roundedRect(mL, y, cW, 16, 2, 2, "FD");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(...textColor);
        pdf.text("WITHDRAWAL AMOUNT", mL + 6, y + 10);
        pdf.text(`Rs. ${Number(r.amount).toLocaleString()}`, W - mR - 6, y + 10, { align: "right" });
        y += 24;
        if (r.status === "cleared") {
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(30, 30, 30);
            pdf.text("Payment Details", mL, y); y += 7;
            pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
            if (r.admin_payment_method) { pdf.text("Method:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(r.admin_payment_method.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase()), mL + 40, y); pdf.setFont("helvetica", "normal"); y += 6; }
            if (r.txn_number) { pdf.text("Txn #:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(r.txn_number, mL + 40, y); pdf.setFont("helvetica", "normal"); y += 6; }
            if (r.cleared_at) { pdf.text("Cleared on:", mL, y); pdf.setFont("helvetica", "bold"); pdf.text(new Date(r.cleared_at).toLocaleDateString(), mL + 40, y); pdf.setFont("helvetica", "normal"); y += 6; }
            y += 4;
        }
        // Company footer
        y += 4;
        pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 5;
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(60, 60, 60);
        pdf.text("Foxbeep Technology Pvt. Ltd.", mL, y); y += 5;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(120, 120, 120);
        pdf.text("Madhyapur Thimi, Bhaktapur, Nepal  \u2022  Ph: +977-9705516131", mL, y); y += 4;
        pdf.text("Foxbeeptech@gmail.com  \u2022  Support@foxbeep.com  \u2022  www.foxbeep.com", mL, y); y += 6;
        pdf.setFont("helvetica", "italic"); pdf.setFontSize(8);
        pdf.text((r.status === "cleared" ? "Payment released. " : "Payment pending approval. ") + "Computer-generated document. Generated: " + new Date().toLocaleDateString(), mL, y);
        const slipLabel = r.status === "cleared" ? `cleared_${new Date(r.cleared_at).toISOString().slice(0,10)}` : `pending_${new Date(r.created_at).toISOString().slice(0,10)}`;
        pdf.save(`withdrawal_slip_${slipLabel}.pdf`);
    };

    return (
        <div>
            <h2 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><Receipt size={22} /> Withdrawal History</h2>
            <p style={{ margin: "0 0 24px", color: C.muted, fontSize: 13 }}>Your withdrawal requests and payment slips</p>

            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 14px" }}>
                    <span style={{ fontSize: 12, color: C.muted }}>From</span>
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ ...styles.input, width: 130, padding: "4px 8px", fontSize: 12 }} />
                    <span style={{ fontSize: 12, color: C.muted }}>To</span>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ ...styles.input, width: 130, padding: "4px 8px", fontSize: 12 }} />
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: mobile ? 10 : 16, marginBottom: 24 }}>
                <div style={{ ...styles.card, border: `1px solid ${C.green}44`, background: C.green + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>CLEARED IN PERIOD</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.green }}>Rs.{totalCleared.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{filtered.filter(r => r.status === "cleared").length} payment(s)</div>
                </div>
                <div style={{ ...styles.card, border: `1px solid ${C.yellow}44`, background: C.yellow + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>PENDING IN PERIOD</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.yellow }}>Rs.{totalPending.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{filtered.filter(r => r.status === "pending").length} request(s)</div>
                </div>
                <div style={{ ...styles.card, border: `1px solid ${C.accent}44`, background: C.accent + "08" }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>ALL TIME CLEARED</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.accent }}>Rs.{allRequests.filter(r => r.status === "cleared").reduce((a, r) => a + Number(r.amount), 0).toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{allRequests.filter(r => r.status === "cleared").length} total</div>
                </div>
            </div>

            {filtered.length === 0 ? (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>No withdrawal requests in this period</div>
                </div>
            ) : (
                <div style={styles.card}>
                    {filtered.map(r => (
                        <div key={r.id} style={{ padding: "16px 0", borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, color: C.white, fontSize: 16 }}>Rs.{Number(r.amount).toLocaleString()}</div>
                                    <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>Requested: {new Date(r.created_at).toLocaleDateString()}</div>
                                    {r.note && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>📝 {r.note}</div>}
                                    {r.admin_payment_method && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>💳 {r.admin_payment_method.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}</div>}
                                    {r.txn_number && <div style={{ fontSize: 12, color: C.accent, marginTop: 2 }}>🔖 Txn: {r.txn_number}</div>}
                                    {r.cleared_at && <div style={{ fontSize: 11, color: C.green, marginTop: 2 }}>✅ Cleared: {new Date(r.cleared_at).toLocaleDateString()}</div>}
                                    {r.payment_screenshot && <a {...proofLinkProps(r.payment_screenshot)} style={{ fontSize: 12, color: C.accent, marginTop: 2, display: "inline-block" }}>🖼 View Payment Proof</a>}
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                                    <span style={styles.badge(r.status === "cleared" ? C.green : C.yellow)}>{r.status === "cleared" ? "✅ Cleared" : "⏳ Pending"}</span>
                                    <button onClick={() => downloadWithdrawalSlip(r)} style={{ ...styles.btn(C.accent), padding: "5px 10px", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                                        <Download size={12} /> Slip PDF
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Team Profiles Directory ──────────────────────────────────────────────────
function TeamProfiles({ setPage, setProfileId }) {
    const mobile = useMobile();
    const [search, setSearch] = useState("");
    const [filterType, setFilterType] = useState("all");

    const filtered = _staff.filter(s => {
        const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) ||
            (s.designation || "").toLowerCase().includes(search.toLowerCase());
        const matchType = filterType === "all" || s.employment_type === filterType;
        return matchSearch && matchType;
    });

    const openProfile = (id) => { setProfileId(id); setPage("profile"); };

    const etColor = (t) => t === "freelancer" ? C.purple : t === "part_time" ? C.yellow : C.green;
    const etLabel = (t) => t === "freelancer" ? "Freelancer" : t === "part_time" ? "Part Time" : "Full Time";

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 22 }}>🪪 Team Profiles</h2>
                <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>View your colleagues' profiles and info</p>
            </div>

            {/* Search + filter */}
            <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
                <input
                    style={{ ...styles.input, flex: 1, minWidth: 180 }}
                    placeholder="🔍  Search by name or role..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <div style={{ display: "flex", gap: 6 }}>
                    {[["all", "All"], ["full_time", "Full Time"], ["part_time", "Part Time"], ["freelancer", "Freelancer"]].map(([id, label]) => (
                        <button key={id} onClick={() => setFilterType(id)} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${filterType === id ? C.accent : C.border}`, background: filterType === id ? C.accent + "22" : C.card2, color: filterType === id ? C.accent : C.muted, fontWeight: filterType === id ? 700 : 500, fontSize: 12, cursor: "pointer" }}>{label}</button>
                    ))}
                </div>
            </div>

            {/* Stats row */}
            <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
                {[
                    ["👥", _staff.length, "Total Staff", C.accent],
                    ["🟢", _staff.filter(s => s.employment_type === "full_time").length, "Full Time", C.green],
                    ["🟡", _staff.filter(s => s.employment_type === "part_time").length, "Part Time", C.yellow],
                    ["🟣", _staff.filter(s => s.employment_type === "freelancer").length, "Freelancers", C.purple],
                ].map(([icon, val, label, color]) => (
                    <div key={label} style={{ ...styles.card, display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", flex: "1 1 120px" }}>
                        <div style={{ fontSize: 20 }}>{icon}</div>
                        <div>
                            <div style={{ fontWeight: 900, fontSize: 20, color, lineHeight: 1 }}>{val}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Grid */}
            {filtered.length === 0 ? (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 48 }}>No staff found.</div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: mobile ? 10 : 16 }}>
                    {filtered.map(s => (
                        <div key={s.id}
                            onClick={() => openProfile(s.id)}
                            style={{ ...styles.card, cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s", padding: mobile ? "16px 12px" : 20 }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 8px 32px ${C.accent}22`; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                        >
                            {/* Avatar */}
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                                <div style={{ width: mobile ? 56 : 72, height: mobile ? 56 : 72, borderRadius: "50%", background: etColor(s.employment_type) + "22", border: `2px solid ${etColor(s.employment_type)}44`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: mobile ? 24 : 30, marginBottom: 12, flexShrink: 0 }}>
                                    {s.profile_image ? <img src={s.profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
                                </div>
                                <div style={{ fontWeight: 800, fontSize: mobile ? 13 : 15, color: C.white, marginBottom: 2, lineHeight: 1.2 }}>{s.name}</div>
                                <div style={{ fontSize: mobile ? 11 : 12, color: C.muted, marginBottom: 8 }}>{s.designation || "—"}</div>
                                <span style={{ ...styles.badge(etColor(s.employment_type)), fontSize: 11 }}>{etLabel(s.employment_type)}</span>
                                {s.bio && (
                                    <div style={{ marginTop: 10, fontSize: 11, color: C.muted, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", textOverflow: "ellipsis" }}>{s.bio}</div>
                                )}
                                <div style={{ marginTop: 10, fontSize: 11, color: C.accent, fontWeight: 600 }}>View Profile →</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Individual Staff Profile ─────────────────────────────────────────────────

// ─── Admin Grant Points Inline (used on Staff Profile) ───────────────────────
function AdminGrantPointsInline({ staffId, staffName }) {
    const [pts, setPts] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    const grant = async () => {
        if (!pts || !reason) return;
        setSaving(true);
        try {
            await awardPoints(staffId, Number(pts), reason, null);
            // refresh ledger
            const fresh = await db.getPointsLedger();
            _pointsLedger = fresh || [];
            setMsg(`✅ ${pts} pts granted to ${staffName}`);
            setPts(""); setReason("");
            setTimeout(() => setMsg(""), 3000);
        } finally { setSaving(false); }
    };

    return (
        <div style={{ ...styles.card, marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.white, marginBottom: 4 }}>⭐ Grant Extra Points</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Award bonus points to {staffName}</div>
            {msg && <div style={{ marginBottom: 10, padding: "8px 12px", background: C.green + "22", borderRadius: 8, color: C.green, fontSize: 13, fontWeight: 600 }}>{msg}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input style={styles.input} type="number" value={pts} onChange={e => setPts(e.target.value)} placeholder="Points (e.g. 10)" />
                <input style={styles.input} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (e.g. Great performance)" />
                <button style={styles.btn(C.yellow)} onClick={grant} disabled={saving}>{saving ? "Granting..." : "⭐ Grant Points"}</button>
            </div>
        </div>
    );
}

function StaffProfile({ staffId, setPage, isAdmin = false }) {
    const mobile = useMobile();
    const [deleting, setDeleting] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [finYear, setFinYear] = useState(new Date().getFullYear());
    const [finMonth, setFinMonth] = useState(0); // 0 = all months
    const s = _staff.find(x => x.id === staffId);
    if (!s) return <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 48 }}>Staff not found.</div>;

    const deletePicture = async () => {
        if (!window.confirm(`Remove profile picture for ${s.name}?`)) return;
        setDeleting(true);
        try {
            await db.updateStaff(s.id, { profile_image: null });
            s.profile_image = null; // optimistic update
            setDeleted(true);
        } finally { setDeleting(false); }
    };

    const etColor = s.employment_type === "freelancer" ? C.purple : s.employment_type === "part_time" ? C.yellow : C.green;
    const etLabel = s.employment_type === "freelancer" ? "Freelancer" : s.employment_type === "part_time" ? "Part Time" : "Full Time";

    const now = new Date();
    const joinDate = s.join_date ? new Date(s.join_date) : null;
    const tenureMonths = joinDate ? Math.floor((now - joinDate) / (1000 * 60 * 60 * 24 * 30)) : null;
    const tenureStr = tenureMonths === null ? null : tenureMonths < 1 ? "< 1 month" : tenureMonths < 12 ? `${tenureMonths} month(s)` : `${Math.floor(tenureMonths / 12)} yr ${tenureMonths % 12} mo`;

    // Attendance stats (this month)
    const summary = s.employment_type !== "freelancer" ? computeSalarySummary(s.id, now.getMonth() + 1, now.getFullYear()) : null;

    // Freelancer stats
    const myProjects = _projects.filter(p => p.freelancer_id === s.id);
    const completed = myProjects.filter(p => p.status === "cleared");
    const totalEarned = completed.reduce((a, p) => a + Number(p.price), 0);

    const InfoRow = ({ icon, label, value, color }) => (
        value ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: (color || C.accent) + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>{icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 14, color: color || C.white, fontWeight: 600, wordBreak: "break-word" }}>{value}</div>
                </div>
            </div>
        ) : null
    );

    return (
        <div style={{ maxWidth: 720 }}>
            {/* Back button */}
            <button onClick={() => setPage("team")} style={{ ...styles.btnOut, marginBottom: 20, display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>← Back to Team</button>

            {/* Hero card */}
            <div style={{ ...styles.card, marginBottom: 20, background: `linear-gradient(135deg, ${C.card} 0%, ${etColor}11 100%)`, border: `1px solid ${etColor}33` }}>
                <div style={{ display: "flex", flexDirection: mobile ? "column" : "row", alignItems: mobile ? "center" : "flex-start", gap: 24, textAlign: mobile ? "center" : "left" }}>
                    {/* Big avatar */}
                    <div style={{ position: "relative", flexShrink: 0 }}>
                        <div style={{ width: mobile ? 90 : 110, height: mobile ? 90 : 110, borderRadius: "50%", background: etColor + "22", border: `3px solid ${etColor}66`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: mobile ? 40 : 50 }}>
                            {(!deleted && s.profile_image) ? <img src={s.profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
                        </div>
                        {isAdmin && !deleted && s.profile_image && (
                            <button onClick={deletePicture} disabled={deleting} title="Remove profile picture" style={{ position: "absolute", bottom: 0, right: 0, width: 28, height: 28, borderRadius: "50%", background: C.red, border: `2px solid ${C.bg}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13 }}>
                                {deleting ? "…" : "🗑"}
                            </button>
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                            <div style={{ fontSize: mobile ? 22 : 28, fontWeight: 900, color: C.white, lineHeight: 1.1 }}>{s.name}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f59e0b22", border: "1px solid #f59e0b44", borderRadius: 20, padding: "5px 12px", flexShrink: 0 }}>
                                <span style={{ fontSize: 16 }}>⭐</span>
                                <span style={{ fontWeight: 900, fontSize: 14, color: "#f59e0b" }}>{getStaffPoints(s.id).balance}</span>
                                <span style={{ fontSize: 11, color: C.muted }}>pts</span>
                            </div>
                        </div>
                        <div style={{ fontSize: 15, color: C.muted, marginTop: 4 }}>{s.designation || "—"}</div>
                        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", justifyContent: mobile ? "center" : "flex-start" }}>
                            <span style={styles.badge(etColor)}>{etLabel}</span>
                            {tenureStr && <span style={styles.badge(C.accent)}>🗓 {tenureStr}</span>}
                            {s.join_date && <span style={styles.badge(C.accent2)}>Joined {fmtDate(s.join_date)}</span>}
                        </div>
                        {s.bio && <div style={{ marginTop: 14, fontSize: 13, color: C.muted, lineHeight: 1.7, fontStyle: "italic", maxWidth: 480 }}>"{s.bio}"</div>}
                    </div>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                {/* Personal Info */}
                <div style={styles.card}>
                    <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: C.white }}>👤 Personal Info</div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Contact & personal details</div>
                    <InfoRow icon="📧" label="EMAIL" value={s.email} />
                    <InfoRow icon="📱" label="PHONE" value={s.phone} color={C.accent} />
                    <InfoRow icon="🎂" label="AGE" value={s.age ? `${s.age} years old` : null} />
                    <InfoRow icon="🗓" label="JOIN DATE" value={s.join_date ? fmtDate(s.join_date) : null} />
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: C.accent + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>🕐</div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 2 }}>LAST LOGIN</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600 }}>
                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: isOnline(s.last_login) ? C.green : C.red, flexShrink: 0, boxShadow: isOnline(s.last_login) ? `0 0 6px ${C.green}` : "none" }} />
                                <span style={{ color: s.last_login ? C.white : C.red }}>{s.last_login ? timeAgoLocal(s.last_login) : "Never"}</span>
                                {isOnline(s.last_login) && <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>● Online</span>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div style={styles.card}>
                    <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: C.white }}>📊 This Month</div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Performance snapshot</div>
                    {summary ? (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            {[
                                ["✅", summary.present, "Present", C.green],
                                ["❌", summary.absent, "Absent", C.red],
                                ["📅", summary.workingDays, "Calendar Days", C.accent],
                                ["💰", `Rs.${summary.netSalary.toLocaleString()}`, "Net Salary", C.yellow],
                            ].map(([icon, val, label, color]) => (
                                <div key={label} style={{ background: color + "11", borderRadius: 10, padding: "12px 10px", textAlign: "center", border: `1px solid ${color}22` }}>
                                    <div style={{ fontSize: 16 }}>{icon}</div>
                                    <div style={{ fontSize: 18, fontWeight: 900, color, marginTop: 2 }}>{val}</div>
                                    <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{label}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            {[
                                ["📁", myProjects.length, "Total Projects", C.accent],
                                ["✅", completed.length, "Completed", C.green],
                                ["💰", `Rs.${totalEarned.toLocaleString()}`, "Total Earned", C.yellow],
                                ["⏳", myProjects.filter(p => p.status === "pending").length, "Pending", C.yellow],
                            ].map(([icon, val, label, color]) => (
                                <div key={label} style={{ background: color + "11", borderRadius: 10, padding: "12px 10px", textAlign: "center", border: `1px solid ${color}22` }}>
                                    <div style={{ fontSize: 16 }}>{icon}</div>
                                    <div style={{ fontSize: 18, fontWeight: 900, color, marginTop: 2 }}>{val}</div>
                                    <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{label}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Bank Details */}
                {isAdmin && <AdminGrantPointsInline staffId={s.id} staffName={s.name} />}
                {(isAdmin || s.bank_name || s.bank_account || s.esewa_number) && (
                    <div style={styles.card}>
                        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: C.white }}>🏦 Bank & Payment</div>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Payment details on file</div>
                        {(s.bank_name || s.bank_account || s.bank_branch || s.esewa_number) ? <>
                            <InfoRow icon="🏛" label="BANK NAME" value={s.bank_name} />
                            <InfoRow icon="🔢" label="ACCOUNT NUMBER" value={s.bank_account} color={C.accent} />
                            <InfoRow icon="🌿" label="BRANCH" value={s.bank_branch} />
                            <InfoRow icon="📲" label="ESEWA" value={s.esewa_number} color={C.green} />
                        </> : (
                            <div style={{ padding: "16px 0", color: C.muted, fontSize: 13, textAlign: "center" }}>⚠️ No bank details added yet. Staff can add these in Settings.</div>
                        )}
                    </div>
                )}

                {/* ── Financial Summary for full-time staff (admin only) ── */}
                {isAdmin && s.employment_type !== "freelancer" && (() => {
                    const staffSalaries = _salaryPayments.filter(r => r.staff_id === s.id);
                    const staffComms = _commissions.filter(c => c.staff_id === s.id);
                    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

                    const filteredSalaries = staffSalaries.filter(r =>
                        r.year === finYear && (finMonth === 0 || r.month === finMonth)
                    );
                    const filteredComms = staffComms.filter(c =>
                        c.year === finYear && (finMonth === 0 || c.month === finMonth)
                    );

                    const totalOutstanding = staffSalaries.filter(r => r.status === "pending").reduce((a, r) => a + Number(r.amount), 0);
                    const standaloneCommTotal = staffComms.filter(c => !staffSalaries.find(r => r.month === c.month && r.year === c.year)).reduce((a, c) => a + Number(c.commission_amount), 0);
                    const totalPaid = staffSalaries.filter(r => r.status === "paid").reduce((a, r) => a + Number(r.amount), 0);
                    const totalComms = staffComms.reduce((a, c) => a + Number(c.commission_amount), 0);

                    const years = [...new Set([...staffSalaries.map(r => r.year), ...staffComms.map(c => c.year), new Date().getFullYear()])].sort((a, b) => b - a);

                    return (
                        <div style={{ ...styles.card, gridColumn: mobile ? "1" : "1 / -1" }}>
                            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: C.white }}>💰 Financial Overview</div>
                            <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Salary & commission history</div>

                            {/* Summary stats */}
                            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
                                {[
                                    ["⏳", `Rs.${(totalOutstanding + standaloneCommTotal).toLocaleString()}`, "Outstanding", C.yellow],
                                    ["✅", `Rs.${totalPaid.toLocaleString()}`, "Paid Salary", C.green],
                                    ["🏆", `Rs.${totalComms.toLocaleString()}`, "Commissions", "#a855f7"],
                                ].map(([icon, val, label, color]) => (
                                    <div key={label} style={{ background: color + "11", borderRadius: 10, padding: "12px 10px", textAlign: "center", border: `1px solid ${color}22` }}>
                                        <div style={{ fontSize: 16 }}>{icon}</div>
                                        <div style={{ fontSize: 16, fontWeight: 900, color, marginTop: 2 }}>{val}</div>
                                        <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Date filter */}
                            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                                <select style={{ ...styles.input, width: "auto", padding: "6px 12px", fontSize: 13 }} value={finYear} onChange={e => setFinYear(Number(e.target.value))}>
                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                                <select style={{ ...styles.input, width: "auto", padding: "6px 12px", fontSize: 13 }} value={finMonth} onChange={e => setFinMonth(Number(e.target.value))}>
                                    <option value={0}>All Months</option>
                                    {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                </select>
                            </div>

                            {/* Records list */}
                            {filteredSalaries.length === 0 && filteredComms.length === 0 ? (
                                <div style={{ textAlign: "center", color: C.muted, padding: "20px 0", fontSize: 13 }}>No records for this period.</div>
                            ) : (
                                <div>
                                    {filteredSalaries.map(r => {
                                        const monthComms = filteredComms.filter(c => c.month === r.month && c.year === r.year);
                                        const commTotal = monthComms.reduce((a, c) => a + Number(c.commission_amount), 0);
                                        return (
                                            <div key={r.id} style={{ padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 700, color: C.white, fontSize: 14 }}>{monthNames[r.month - 1]} {r.year}</div>
                                                        <div style={{ fontSize: 16, fontWeight: 900, color: r.status === "paid" ? C.green : C.yellow, marginTop: 2 }}>
                                                            Rs.{Number(r.amount).toLocaleString()} <span style={{ fontSize: 11, fontWeight: 500, color: C.muted }}>base</span>
                                                        </div>
                                                        {commTotal > 0 && (
                                                            <div style={{ marginTop: 4, padding: "4px 8px", background: "#a855f711", borderRadius: 6, border: "1px solid #a855f733" }}>
                                                                {monthComms.map(c => (
                                                                    <div key={c.id} style={{ fontSize: 12, color: C.muted, display: "flex", justifyContent: "space-between" }}>
                                                                        <span>🏆 {c.project_name} ({c.commission_percent}%)</span>
                                                                        <span style={{ color: "#a855f7", fontWeight: 700 }}>+Rs.{Number(c.commission_amount).toLocaleString()}</span>
                                                                    </div>
                                                                ))}
                                                                <div style={{ fontSize: 12, fontWeight: 800, color: "#a855f7", marginTop: 2 }}>
                                                                    Total: Rs.{(Number(r.amount) + commTotal).toLocaleString()}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {r.note && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>📝 {r.note}</div>}
                                                    </div>
                                                    <span style={styles.badge(r.status === "paid" ? C.green : C.yellow)}>{r.status === "paid" ? "✅ Paid" : "⏳ Pending"}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {/* Standalone commissions (no salary record that month) */}
                                    {filteredComms.filter(c => !filteredSalaries.find(r => r.month === c.month && r.year === c.year)).map(c => (
                                        <div key={c.id} style={{ padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <div>
                                                    <div style={{ fontWeight: 700, color: "#a855f7", fontSize: 14 }}>🏆 Commission — {c.project_name}</div>
                                                    <div style={{ fontSize: 11, color: C.muted }}>{monthNames[c.month - 1]} {c.year} • {c.commission_percent}% of Rs.{Number(c.project_value).toLocaleString()}</div>
                                                    <div style={{ fontSize: 16, fontWeight: 900, color: "#a855f7", marginTop: 2 }}>+Rs.{Number(c.commission_amount).toLocaleString()}</div>
                                                    {c.description && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>📝 {c.description}</div>}
                                                </div>
                                                <span style={styles.badge("#a855f7")}>⏳ Outstanding</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Recent projects for freelancer */}
                {s.employment_type === "freelancer" && myProjects.length > 0 && (
                    <div style={styles.card}>
                        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: C.white }}>💼 Recent Projects</div>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Latest {Math.min(myProjects.length, 5)} projects</div>
                        {myProjects.slice(0, 5).map(p => (
                            <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, color: C.white, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                                    <div style={{ fontSize: 11, color: C.muted }}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}</div>
                                </div>
                                <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: p.status === "cleared" ? C.green : C.yellow }}>Rs.{Number(p.price).toLocaleString()}</div>
                                    <span style={{ ...styles.badge(p.status === "cleared" ? C.green : C.yellow), fontSize: 10 }}>{p.status === "cleared" ? "✅" : "⏳"}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


// ─── Points Badge (shown top-right on dashboards) ─────────────────────────────
function PointsBadge({ userId, onClick }) {
    const { balance } = getStaffPoints(userId);
    return (
        <div onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f59e0b22", border: "1px solid #f59e0b44", borderRadius: 20, padding: "5px 12px", cursor: "pointer", userSelect: "none" }}>
            <span style={{ fontSize: 16 }}>⭐</span>
            <span style={{ fontWeight: 900, fontSize: 14, color: "#f59e0b" }}>{balance}</span>
            <span style={{ fontSize: 11, color: C.muted }}>pts</span>
        </div>
    );
}

// ─── Points History + Redeem Modal ────────────────────────────────────────────
function PointsModal({ user, onClose, reload }) {
    const mobile = useMobile();
    const [tab, setTab] = useState("history");
    const [redeemId, setRedeemId] = useState("");
    const [redeeming, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    const { balance, earned, redeemed } = getStaffPoints(user.id);
    const myLedger = _pointsLedger.filter(p => p.staff_id === user.id);
    const myRedemptions = _redemptions.filter(r => r.staff_id === user.id);
    const availableRewards = _rewards.filter(r => r.active !== false || r.expired === true);

    const doRedeem = async () => {
        const reward = _rewards.find(r => r.id === redeemId);
        if (!reward) return;
        if (reward.expired) { setMsg("This reward has expired."); return; }
        if (balance < reward.points_required) { setMsg("Not enough points!"); return; }
        setSaving(true);
        try {
            await db.addRedemption({ id: "rd" + Date.now(), staff_id: user.id, staff_name: user.name, reward_id: reward.id, reward_name: reward.name, points_used: reward.points_required, status: "pending", created_at: new Date().toISOString() });
            await reload();
            setMsg("✅ Redemption request submitted!");
            setRedeemId("");
        } finally { setSaving(false); }
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: mobile ? "flex-end" : "center", justifyContent: "center" }} onClick={onClose}>
            <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: mobile ? "20px 20px 0 0" : 16, width: mobile ? "100%" : 480, maxHeight: mobile ? "85vh" : "80vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <div style={{ padding: "20px 20px 0", borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div>
                            <div style={{ fontWeight: 900, fontSize: 18, color: C.white }}>⭐ My Points</div>
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Earn, track and redeem your rewards</div>
                        </div>
                        <button onClick={onClose} style={{ background: C.card2, border: "none", color: C.muted, fontSize: 20, cursor: "pointer", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                    </div>
                    {/* Stats row */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                        {[["⭐ Balance", balance, C.yellow], ["💰 Earned", earned, C.green], ["🎁 Redeemed", redeemed, C.purple]].map(([l, v, c]) => (
                            <div key={l} style={{ background: c + "11", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                                <div style={{ fontSize: 18, fontWeight: 900, color: c }}>{v}</div>
                                <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{l}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "flex", gap: 0 }}>
                        {[["history", "📋 History"], ["redeem", "🎁 Redeem"]].map(([id, label]) => (
                            <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "8px", border: "none", background: "transparent", borderBottom: tab === id ? `2px solid ${C.accent}` : "2px solid transparent", color: tab === id ? C.accent : C.muted, fontWeight: tab === id ? 700 : 500, fontSize: 13, cursor: "pointer" }}>{label}</button>
                        ))}
                    </div>
                </div>
                {/* Body */}
                <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
                    {tab === "history" && (
                        <>
                            {myLedger.length === 0 ? <div style={{ textAlign: "center", color: C.muted, padding: 32 }}>No points earned yet.</div> : myLedger.map(p => (
                                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                                    <div>
                                        <div style={{ fontSize: 13, color: C.white, fontWeight: 600 }}>{p.reason}</div>
                                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{p.created_at ? fmtTZ(p.created_at) : ""}</div>
                                    </div>
                                    <span style={{ fontWeight: 900, fontSize: 16, color: C.yellow }}>+{p.points} ⭐</span>
                                </div>
                            ))}
                            {myRedemptions.length > 0 && <>
                                <div style={{ fontWeight: 700, color: C.muted, fontSize: 12, margin: "16px 0 8px" }}>REDEMPTION HISTORY</div>
                                {myRedemptions.map(r => (
                                    <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                                        <div>
                                            <div style={{ fontSize: 13, color: C.white, fontWeight: 600 }}>🎁 {r.reward_name}</div>
                                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{r.created_at ? fmtTZ(r.created_at) : ""}</div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ fontWeight: 900, color: C.red, fontSize: 14 }}>-{r.points_used} ⭐</div>
                                            <span style={styles.badge(r.status === "approved" ? C.green : r.status === "rejected" ? C.red : C.yellow)}>{r.status}</span>
                                        </div>
                                    </div>
                                ))}
                            </>}
                        </>
                    )}
                    {tab === "redeem" && (
                        <>
                            {msg && <div style={{ marginBottom: 12, padding: "10px 14px", background: msg.startsWith("✅") ? C.green + "22" : C.red + "22", borderRadius: 10, color: msg.startsWith("✅") ? C.green : C.red, fontSize: 13, fontWeight: 600 }}>{msg}</div>}
                            {availableRewards.length === 0 ? (
                                <div style={{ textAlign: "center", color: C.muted, padding: 40 }}>
                                    <div style={{ fontSize: 36, marginBottom: 12 }}>🎁</div>
                                    <div style={{ fontWeight: 700, marginBottom: 4 }}>No rewards available</div>
                                    <div style={{ fontSize: 12 }}>Admin hasn't set up any rewards yet.</div>
                                </div>
                            ) : (
                                <>
                                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Your balance: <strong style={{ color: C.yellow }}>{balance} ⭐</strong></div>
                                    {availableRewards.map(r => {
                                        const isExpired = r.expired === true;
                                        const canRedeem = !isExpired && balance >= r.points_required;
                                        return (
                                            <div key={r.id} onClick={() => canRedeem && setRedeemId(redeemId === r.id ? "" : r.id)} style={{ padding: 14, borderRadius: 12, border: `2px solid ${isExpired ? C.red + "44" : redeemId === r.id ? C.accent : canRedeem ? C.border : C.border + "66"}`, background: isExpired ? C.red + "08" : redeemId === r.id ? C.accent + "11" : C.card2, cursor: canRedeem ? "pointer" : "not-allowed", marginBottom: 10, opacity: isExpired ? 0.6 : canRedeem ? 1 : 0.5, transition: "all 0.15s" }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <div>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                            <span style={{ fontWeight: 700, color: isExpired ? C.muted : C.white, fontSize: 14, textDecoration: isExpired ? "line-through" : "none" }}>{r.name}</span>
                                                            {isExpired && <span style={{ fontSize: 10, fontWeight: 700, color: C.red, background: C.red + "22", padding: "2px 7px", borderRadius: 20 }}>EXPIRED</span>}
                                                        </div>
                                                        {r.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{r.description}</div>}
                                                    </div>
                                                    <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                                                        <div style={{ fontWeight: 900, color: isExpired ? C.muted : C.yellow, fontSize: 15 }}>{r.points_required} ⭐</div>
                                                        {!isExpired && !canRedeem && <div style={{ fontSize: 10, color: C.red }}>Need {r.points_required - balance} more</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {redeemId && (
                                        <button style={{ ...styles.btn(C.accent), width: "100%", padding: 13, fontSize: 15, marginTop: 8 }} onClick={doRedeem} disabled={redeeming}>
                                            {redeeming ? "Submitting..." : `🎁 Redeem "${_rewards.find(r => r.id === redeemId)?.name}"`}
                                        </button>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Admin: Redeemed Points ───────────────────────────────────────────────────
function AdminRedeemedPoints({ reload }) {
    const mobile = useMobile();
    const [tab, setTab] = useState("pending");
    const pending = _redemptions.filter(r => r.status === "pending");
    const approved = _redemptions.filter(r => r.status === "approved");
    const rejected = _redemptions.filter(r => r.status === "rejected");

    const updateStatus = async (id, status) => {
        await db.updateRedemption(id, { status, reviewed_at: new Date().toISOString() });
        await reload();
        showToast(status === "approved" ? "Redemption approved!" : "Redemption rejected.", status === "approved" ? "#16a34a" : "#ef4444");
    };

    const RedemptionCard = ({ r, showActions }) => (
        mobile ? (
            <div style={{ padding: "14px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div>
                        <div style={{ fontWeight: 700, color: C.white }}>{r.staff_name}</div>
                        <div style={{ fontSize: 13, color: C.accent, marginTop: 2 }}>🎁 {r.reward_name}</div>
                        <div style={{ fontSize: 12, color: C.yellow, marginTop: 2 }}>-{r.points_used} ⭐</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{r.created_at ? fmtTZ(r.created_at) : ""}</div>
                    </div>
                    <span style={styles.badge(r.status === "approved" ? C.green : r.status === "rejected" ? C.red : C.yellow)}>{r.status}</span>
                </div>
                {showActions && (
                    <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...styles.btn(C.green), flex: 1, padding: "8px", fontSize: 13 }} onClick={() => updateStatus(r.id, "approved")}>✅ Approve</button>
                        <button style={{ ...styles.btn(C.red), flex: 1, padding: "8px", fontSize: 13 }} onClick={() => updateStatus(r.id, "rejected")}>❌ Reject</button>
                    </div>
                )}
            </div>
        ) : (
            <tr key={r.id}>
                <td style={styles.td}><strong style={{ color: C.white }}>{r.staff_name}</strong></td>
                <td style={styles.td}><span style={{ color: C.accent }}>🎁 {r.reward_name}</span></td>
                <td style={styles.td}><strong style={{ color: C.yellow }}>{r.points_used} ⭐</strong></td>
                <td style={{ ...styles.td, fontSize: 12, color: C.muted }}>{r.created_at ? fmtTZ(r.created_at) : "—"}</td>
                <td style={styles.td}><span style={styles.badge(r.status === "approved" ? C.green : r.status === "rejected" ? C.red : C.yellow)}>{r.status}</span></td>
                {showActions && (
                    <td style={styles.td}>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button style={{ ...styles.btn(C.green), padding: "5px 10px", fontSize: 12 }} onClick={() => updateStatus(r.id, "approved")}>✅ Approve</button>
                            <button style={{ ...styles.btn(C.red), padding: "5px 10px", fontSize: 12 }} onClick={() => updateStatus(r.id, "rejected")}>❌ Reject</button>
                        </div>
                    </td>
                )}
            </tr>
        )
    );

    return (
        <div>
            <h2 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 22 }}>🎁 Redeemed Points</h2>
            <p style={{ margin: "0 0 24px", color: C.muted, fontSize: 13 }}>Review and approve staff reward redemption requests</p>
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: mobile ? 10 : 16, marginBottom: 24 }}>
                <StatCard icon="⏳" label="Pending" value={pending.length} color={C.yellow} />
                <StatCard icon="✅" label="Approved" value={approved.length} color={C.green} />
                <StatCard icon="❌" label="Rejected" value={rejected.length} color={C.red} />
            </div>
            <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: `2px solid ${C.border}` }}>
                {[["pending", "⏳ Pending", pending.length], ["approved", "✅ Approved", approved.length], ["rejected", "❌ Rejected", rejected.length]].map(([id, label, count]) => (
                    <button key={id} onClick={() => setTab(id)} style={{ padding: "10px 20px", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", background: "transparent", borderBottom: tab === id ? `3px solid ${C.accent}` : "3px solid transparent", color: tab === id ? C.accent : C.muted, marginBottom: -2 }}>
                        {label} <span style={{ background: tab === id ? C.accent + "33" : C.card2, color: tab === id ? C.accent : C.muted, borderRadius: 20, padding: "1px 8px", fontSize: 12, marginLeft: 4 }}>{count}</span>
                    </button>
                ))}
            </div>
            {(() => {
                const list = tab === "pending" ? pending : tab === "approved" ? approved : rejected;
                if (list.length === 0) return <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>No {tab} redemptions.</div>;
                return (
                    <div style={styles.card}>
                        {mobile ? list.map(r => <RedemptionCard key={r.id} r={r} showActions={tab === "pending"} />) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ ...styles.table, minWidth: 500 }}>
                                    <thead><tr>
                                        <th style={styles.th}>Staff</th><th style={styles.th}>Reward</th><th style={styles.th}>Points</th>
                                        <th style={styles.th}>Requested</th><th style={styles.th}>Status</th>
                                        {tab === "pending" && <th style={styles.th}>Actions</th>}
                                    </tr></thead>
                                    <tbody>{list.map(r => <RedemptionCard key={r.id} r={r} showActions={tab === "pending"} />)}</tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
}

// ─── Admin: Rewards Settings Panel ───────────────────────────────────────────
function RewardsSettingsPanel({ reload }) {
    const mobile = useMobile();
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ name: "", description: "", points_required: "" });
    const [saving, setSaving] = useState(false);
    const [grantStaff, setGrantStaff] = useState(_staff[0]?.id || "");
    const [grantPts, setGrantPts] = useState("");
    const [grantReason, setGrantReason] = useState("");
    const [granting, setGranting] = useState(false);

    const saveReward = async () => {
        if (!form.name || !form.points_required) return;
        setSaving(true);
        try {
            await db.addReward({ id: "rw" + Date.now(), name: form.name, description: form.description, points_required: Number(form.points_required), active: true, created_at: new Date().toISOString() });
            await reload(); setShowAdd(false); setForm({ name: "", description: "", points_required: "" });
            showToast("Reward added!");
        } finally { setSaving(false); }
    };

    const toggleReward = async (r) => { await db.updateReward(r.id, { active: !r.active }); await reload(); showToast(r.active ? "Reward deactivated." : "Reward activated!"); };
    const expireReward = async (r) => { await db.updateReward(r.id, { expired: true, active: false }); await reload(); showToast("Reward expired.", "#f59e0b"); };
    const unexpireReward = async (r) => { await db.updateReward(r.id, { expired: false, active: true }); await reload(); showToast("Reward restored!"); };
    const deleteReward = async (id) => { await db.deleteReward(id); await reload(); showToast("Reward deleted.", "#ef4444"); };

    const grantExtra = async () => {
        if (!grantStaff || !grantPts || !grantReason) return;
        setGranting(true);
        try {
            await awardPoints(grantStaff, Number(grantPts), grantReason, reload);
            setGrantPts(""); setGrantReason("");
            showToast(`${grantPts} points granted to ${_staff.find(s => s.id === grantStaff)?.name || "staff"}!`);
        } finally { setGranting(false); }
    };

    return (
        <div>
            {/* Grant Extra Points */}
            <div style={{ ...styles.card, marginBottom: 20 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: C.white, marginBottom: 4 }}>⭐ Grant Extra Points</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Manually award bonus points to any staff member</div>
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>STAFF MEMBER</label>
                        <select style={styles.input} value={grantStaff} onChange={e => setGrantStaff(e.target.value)}>
                            {_staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>POINTS</label>
                        <input style={styles.input} type="number" value={grantPts} onChange={e => setGrantPts(e.target.value)} placeholder="e.g. 10" />
                    </div>
                    <div style={{ gridColumn: mobile ? "auto" : "1 / -1" }}>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>REASON</label>
                        <input style={styles.input} value={grantReason} onChange={e => setGrantReason(e.target.value)} placeholder="e.g. Outstanding performance" />
                    </div>
                </div>
                <button style={styles.btn(C.yellow)} onClick={grantExtra} disabled={granting}>{granting ? "Granting..." : "⭐ Grant Points"}</button>
            </div>

            {/* Rewards List */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: C.white }}>🎁 Reward Catalogue</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Define rewards staff can redeem with their points</div>
                </div>
                <button style={styles.btn()} onClick={() => setShowAdd(v => !v)}>+ Add Reward</button>
            </div>
            {showAdd && (
                <div style={{ ...styles.card, marginBottom: 16 }}>
                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>REWARD NAME</label>
                            <input style={styles.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Gift Voucher Rs.500" />
                        </div>
                        <div>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>POINTS REQUIRED</label>
                            <input style={styles.input} type="number" value={form.points_required} onChange={e => setForm(f => ({ ...f, points_required: e.target.value }))} placeholder="e.g. 100" />
                        </div>
                        <div style={{ gridColumn: mobile ? "auto" : "1 / -1" }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>DESCRIPTION (optional)</label>
                            <input style={styles.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What does this reward include?" />
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button style={styles.btn(C.green)} onClick={saveReward} disabled={saving}>{saving ? "Saving..." : "Save Reward"}</button>
                        <button style={styles.btnOut} onClick={() => setShowAdd(false)}>Cancel</button>
                    </div>
                </div>
            )}
            {_rewards.length === 0 ? (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>
                    <div style={{ fontSize: 36, marginBottom: 12 }}>🎁</div>
                    No rewards added yet. Add your first reward above.
                </div>
            ) : (
                <div style={styles.card}>
                    {_rewards.map(r => {
                        const isExpired = r.expired === true;
                        return (
                        <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${C.border}`, opacity: isExpired || r.active === false ? 0.6 : 1 }}>
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ fontWeight: 700, color: C.white, fontSize: 14 }}>{r.name}</span>
                                    {isExpired && <span style={{ fontSize: 10, fontWeight: 700, color: C.red, background: C.red + "22", padding: "2px 7px", borderRadius: 20 }}>EXPIRED</span>}
                                </div>
                                {r.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{r.description}</div>}
                                <div style={{ fontSize: 12, color: isExpired ? C.muted : C.yellow, marginTop: 2, fontWeight: 700 }}>{r.points_required} ⭐ required</div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 12 }}>
                                {isExpired ? (
                                    <button style={{ ...styles.btn(C.green), padding: "5px 10px", fontSize: 12 }} onClick={() => unexpireReward(r)}>Re-enable</button>
                                ) : (
                                    <>
                                        <button style={{ ...styles.btn(r.active === false ? C.green : C.yellow), padding: "5px 10px", fontSize: 12 }} onClick={() => toggleReward(r)}>{r.active === false ? "Enable" : "Disable"}</button>
                                        <button style={{ ...styles.btn(C.red + "cc"), padding: "5px 10px", fontSize: 12 }} onClick={() => expireReward(r)}>Expire</button>
                                    </>
                                )}
                                <button style={{ ...styles.btn(C.red), padding: "5px 10px", fontSize: 12 }} onClick={() => deleteReward(r.id)}>🗑</button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// ─── Explore Page ─────────────────────────────────────────────────────────────
function ExplorePage({ user, reload }) {
    const mobile = useMobile();
    const [text, setText] = useState("");
    const [posting, setPosting] = useState(false);
    const [postErr, setPostErr] = useState("");
    const [filter, setFilter] = useState("all");
    const [celebration, setCelebration] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showShoutout, setShowShoutout] = useState(false);
    const [expandedReplies, setExpandedReplies] = useState({});
    const [replyText, setReplyText] = useState({});
    const [replyPosting, setReplyPosting] = useState({});
    const [linkPreview, setLinkPreview] = useState(null);
    const [linkLoading, setLinkLoading] = useState(false);
    const [visibleCount, setVisibleCount] = useState(10);
    const sentinelRef = useRef(null);

    // Auto-detect URL in text and fetch OG preview via microlink.io
    useEffect(() => {
        const match = text.match(/https?:\/\/[^\s]+/i);
        if (!match) { setLinkPreview(null); return; }
        const url = match[0];
        if (linkPreview?.url === url) return;
        const timer = setTimeout(async () => {
            setLinkLoading(true);
            try {
                const res = await fetch(`https://api.microlink.io?url=${encodeURIComponent(url)}`);
                const data = await res.json();
                if (data.status === "success") {
                    setLinkPreview({
                        url,
                        title: data.data.title || "",
                        description: data.data.description || "",
                        image: data.data.image?.url || "",
                        domain: (() => { try { return new URL(url).hostname; } catch { return url; } })(),
                    });
                } else { setLinkPreview(null); }
            } catch { setLinkPreview(null); }
            finally { setLinkLoading(false); }
        }, 800);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);
    // Reset visible count when filter changes
    useEffect(() => { setVisibleCount(10); }, [filter]);

    // Infinite scroll — attach observer after posts render
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) setVisibleCount(c => c + 10);
        }, { threshold: 0.1 });
        obs.observe(el);
        return () => obs.disconnect();
    }, [visibleCount, filter]);

    const MAX_CHARS = 280;
    const POST_POINTS = Number(_appSettings.post_points) || 2;

    const me = _staff.find(s => s.id === user.id) || { name: user.name, designation: "", employment_type: "full_time" };
    const isAdminUser = user.role === "admin";

    // Count my posts this month — admin has no limit
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const myMonthlyPosts = _posts.filter(p => p.author_id === user.id && p.created_at >= monthStart).length;
    const MAX_MONTHLY = 30;
    const postsLeft = isAdminUser ? Infinity : MAX_MONTHLY - myMonthlyPosts;

    const submit = async () => {
        if (!text.trim()) return;
        if (text.length > MAX_CHARS) { setPostErr(`Max ${MAX_CHARS} characters`); return; }
        if (!isAdminUser && postsLeft <= 0) { setPostErr("Monthly post limit (30) reached."); return; }
        setPosting(true); setPostErr("");
        try {
            const postId = "post_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);
            await db.addPost({
                id: postId,
                author_id: user.id,
                author_name: me.name,
                author_designation: me.designation || "",
                author_type: me.employment_type || "full_time",
                author_role: isAdminUser ? "admin" : "staff",
                content: text.trim(),
                likes: [],
                created_at: new Date().toISOString(),
                link_url: linkPreview?.url || null,
                link_title: linkPreview?.title || null,
                link_description: linkPreview?.description || null,
                link_image: linkPreview?.image || null,
                link_domain: linkPreview?.domain || null,
            });
            setText("");
            setLinkPreview(null);
            if (!isAdminUser && POST_POINTS > 0) {
                await claimPoints("claim_post_points", { p_post_id: postId }, null);
            }
            await reload();
            if (!isAdminUser) { setCelebration(true); setTimeout(() => setCelebration(false), 3500); }
        } finally { setPosting(false); }
    };

    const toggleReply = (postId) => setExpandedReplies(prev => ({ ...prev, [postId]: !prev[postId] }));

    const submitReply = async (postId) => {
        const txt = (replyText[postId] || "").trim();
        if (!txt) return;
        setReplyPosting(prev => ({ ...prev, [postId]: true }));
        const me = _staff.find(s => s.id === user.id) || { name: user.name, employment_type: "full_time", designation: "" };
        try {
            await db.addReply({
                id: "rep_" + Date.now() + "_" + Math.random().toString(36).slice(2, 5),
                post_id: postId,
                author_id: user.id,
                author_name: isAdminUser ? user.name : me.name,
                author_type: isAdminUser ? "admin" : (me.employment_type || "full_time"),
                author_role: isAdminUser ? "admin" : "staff",
                content: txt,
                created_at: new Date().toISOString(),
            });
            // Notify post author (and previous repliers) — exclude self
            const post = _posts.find(p => p.id === postId);
            const toNotify = new Set();
            if (post && post.author_id !== user.id) toNotify.add(post.author_id);
            _replies.filter(r => r.post_id === postId && r.author_id !== user.id).forEach(r => toNotify.add(r.author_id));
            const senderName = isAdminUser ? user.name : me.name;
            const notifPromises = [...toNotify].map(recipientId =>
                db.addNotification({
                    id: "notif_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6),
                    recipient_id: recipientId,
                    type: "reply",
                    actor_name: senderName,
                    post_id: postId,
                    post_preview: post ? post.content.slice(0, 60) : "",
                    message: `${senderName} replied to a post you're in`,
                    read: false,
                    created_at: new Date().toISOString(),
                })
            );
            await Promise.all(notifPromises);
            setReplyText(prev => ({ ...prev, [postId]: "" }));
            await reload();
        } finally {
            setReplyPosting(prev => ({ ...prev, [postId]: false }));
        }
    };

    const deleteReply = async (replyId) => {
        await db.deleteReply(replyId);
        await reload();
    };

    const toggleLike = async (post) => {
        await db.likePost(post.id);
        await reload();
    };

    const deletePost = async (id) => {
        await db.deletePost(id);
        await reload();
    };

    // timeAgo handled by shared timeAgoLocal()

    const filtered = _posts.filter(p => {
        if (filter === "mine") return p.author_id === user.id;
        if (filter === "verified") return p.author_type === "full_time";
        return true;
    });

    const AvatarCircle = ({ name, type, size = 40 }) => {
        const staff = _staff.find(s => s.name === name);
        const bg = type === "freelancer" ? C.purple : type === "part_time" ? C.yellow : C.accent;
        return (
            <div style={{ width: size, height: size, borderRadius: "50%", background: bg + "33", border: `2px solid ${bg}55`, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.4 }}>
                {staff?.profile_image
                    ? <img src={staff.profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : name?.charAt(0)?.toUpperCase() || "?"}
            </div>
        );
    };

    const VerifiedBadge = () => (
        <span title="Full-time staff" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", background: C.accent, marginLeft: 4, flexShrink: 0 }}>
            <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </span>
    );

    return (
        <div style={{ maxWidth: 620, margin: "0 auto" }}>
            {showShoutout && <ShoutoutModal user={user} onClose={() => setShowShoutout(false)} reload={reload} />}
            {/* Announcements pinned banner */}
            {_announcements.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    {_announcements.slice(0, 2).map(a => {
                        const pc = a.priority === "urgent" ? C.red : a.priority === "important" ? C.yellow : C.accent;
                        return (
                            <div key={a.id} style={{ padding: "10px 14px", background: pc + "11", border: `1px solid ${pc}33`, borderRadius: 10, marginBottom: 8, display: "flex", gap: 10, alignItems: "flex-start" }}>
                                <span>{a.priority === "urgent" ? "🚨" : a.priority === "important" ? "⚠️" : "📌"}</span>
                                <div style={{ flex: 1, fontSize: 13, color: C.white }}>{a.content}</div>
                                <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{timeAgoLocal(a.created_at)}</span>
                            </div>
                        );
                    })}
                </div>
            )}
            {/* Celebration overlay */}
            {celebration && (
                <div style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ textAlign: "center", animation: "popIn 0.3s ease" }}>
                        <div style={{ fontSize: 72, marginBottom: 8 }}>🎉</div>
                        <div style={{ background: "linear-gradient(135deg,#f59e0b,#f97316)", color: "#fff", borderRadius: 16, padding: "14px 28px", fontWeight: 900, fontSize: 20, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
                            +{POST_POINTS} ⭐ Points Earned!
                        </div>
                        <div style={{ color: C.muted, fontSize: 13, marginTop: 8 }}>Keep sharing with your team 🚀</div>
                    </div>
                </div>
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <div>
                    <h2 style={{ margin: 0, fontWeight: 900, fontSize: 22 }}>🌐 Feeds</h2>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>What's happening at Foxbeep</div>
                </div>
                {!isAdminUser && (
                    <div style={{ fontSize: 12, color: postsLeft <= 5 ? C.red : C.muted, fontWeight: postsLeft <= 5 ? 700 : 400 }}>
                        {postsLeft} / {MAX_MONTHLY} posts this month
                    </div>
                )}
                {isAdminUser && <span style={{ fontSize: 12, color: C.muted }}>Unlimited posts</span>}
            </div>

            {/* Compose box */}
            <div style={{ ...styles.card, marginBottom: 20, border: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", gap: mobile ? 10 : 12, alignItems: "flex-start" }}>
                    <AvatarCircle name={me.name} type={me.employment_type} size={42} />
                    <div style={{ flex: 1 }}>
                        <textarea
                            value={text}
                            onChange={e => { setText(e.target.value); setPostErr(""); }}
                            placeholder="What's on your mind?"
                            rows={3}
                            style={{ width: "100%", background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", color: C.white, fontSize: 15, resize: "none", fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                        />
                        {linkLoading && (
                            <div style={{ marginTop: 8, padding: "10px 12px", background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.muted, display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⏳</span> Fetching link preview...
                            </div>
                        )}
                        {linkPreview && !linkLoading && (
                            <div style={{ marginTop: 8, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", position: "relative", background: C.card2 }}>
                                <button onClick={() => setLinkPreview(null)} style={{ position: "absolute", top: 6, right: 8, background: C.card, border: "none", color: C.muted, cursor: "pointer", fontSize: 14, borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>✕</button>
                                {linkPreview.image && <img src={linkPreview.image} alt="" style={{ width: "100%", maxHeight: 180, objectFit: "cover", display: "block" }} onError={e => e.target.style.display = "none"} />}
                                <div style={{ padding: "10px 12px" }}>
                                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>{linkPreview.domain}</div>
                                    {linkPreview.title && <div style={{ fontWeight: 700, fontSize: 14, color: C.white, marginBottom: 3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{linkPreview.title}</div>}
                                    {linkPreview.description && <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{linkPreview.description}</div>}
                                </div>
                            </div>
                        )}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                            <span style={{ fontSize: 12, color: text.length > MAX_CHARS ? C.red : C.muted }}>{text.length}/{MAX_CHARS}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                {postErr && <span style={{ fontSize: 12, color: C.red }}>{postErr}</span>}
                                {!isAdminUser && <button style={{ ...styles.btn(C.purple), padding: "8px 16px", fontSize: 13 }} onClick={() => setShowShoutout(true)}>👏 Shoutout</button>}
                                <button
                                    style={{ ...styles.btn(C.accent), padding: "8px 20px", fontSize: 14, opacity: (!text.trim() || (!isAdminUser && postsLeft <= 0)) ? 0.5 : 1 }}
                                    onClick={submit}
                                    disabled={posting || !text.trim() || (!isAdminUser && postsLeft <= 0)}
                                >{posting ? "Posting..." : "Post"}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter tabs */}
            <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: `1px solid ${C.border}`, alignItems: "center" }}>
                {[["all", "🌐 All"], ["verified", "✅ Full-time"], ["mine", "👤 Mine"]].map(([id, label]) => (
                    <button key={id} onClick={() => setFilter(id)} style={{ padding: "8px 16px", border: "none", background: "transparent", borderBottom: filter === id ? `2px solid ${C.accent}` : "2px solid transparent", color: filter === id ? C.accent : C.muted, fontWeight: filter === id ? 700 : 500, fontSize: 13, cursor: "pointer", marginBottom: -1 }}>{label}</button>
                ))}
                <span style={{ marginLeft: "auto", fontSize: 12, color: C.muted, alignSelf: "center" }}>{filtered.length} posts</span>
                <button
                    onClick={async () => { setRefreshing(true); await reload(); setTimeout(() => setRefreshing(false), 600); }}
                    title="Refresh feed"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 10px", color: refreshing ? C.accent : C.muted, fontSize: 17, marginLeft: 4, display: "flex", alignItems: "center" }}
                >
                    <span style={{ display: "inline-block", transition: "transform 0.6s linear", transform: refreshing ? "rotate(180deg)" : "rotate(0deg)" }}>🔄</span>
                </button>
            </div>

            {/* Posts */}
            {filtered.length === 0 && (
                <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 48 }}>
                    <div style={{ fontSize: 36, marginBottom: 12 }}>🌐</div>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>No posts yet</div>
                    <div style={{ fontSize: 12 }}>Be the first to share something!</div>
                </div>
            )}

            {filtered.slice(0, visibleCount).map(post => {
                const likes = Array.isArray(post.likes) ? post.likes : [];
                const liked = likes.includes(user.id);
                const canDelete = post.author_id === user.id || isAdminUser;
                const isPostVerified = post.author_type === "full_time" || post.author_role === "admin";

                return (
                    <div key={post.id} style={{ ...styles.card, marginBottom: 12, border: `1px solid ${C.border}`, transition: "border-color 0.15s" }}>
                        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                            <AvatarCircle name={post.author_name} type={post.author_type} size={42} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Header */}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap", gap: 4 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                        <span style={{ fontWeight: 800, fontSize: 14, color: C.white }}>{post.author_name}</span>
                                        {isPostVerified && <VerifiedBadge />}
                                        {(() => { const author = _staff.find(s => s.id === post.author_id); const online = author ? isOnline(author.last_login) : false; return <span style={{ width: 8, height: 8, borderRadius: "50%", background: online ? C.green : C.red, flexShrink: 0, boxShadow: online ? `0 0 5px ${C.green}` : "none", display: "inline-block" }} title={online ? "Online" : "Offline"} />; })()}
                                        <span style={{ fontSize: 12, color: C.muted }}>·</span>
                                        <span style={{ fontSize: 12, color: C.muted }}>{post.author_designation || "Staff"}</span>
                                        <span style={{ fontSize: 12, color: C.muted }}>·</span>
                                        <span style={{ fontSize: 12, color: C.muted }}>{timeAgoLocal(post.created_at)}</span>
                                        {post.author_type === "freelancer" && <span style={{ ...styles.badge(C.purple), fontSize: 10 }}>Freelancer</span>}
                                        {post.author_type === "part_time" && <span style={{ ...styles.badge(C.yellow), fontSize: 10 }}>Part Time</span>}
                                    </div>
                                    {canDelete && (
                                        <button onClick={() => { if (window.confirm("Delete this post?")) deletePost(post.id); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 15, padding: "2px 6px", borderRadius: 6, lineHeight: 1 }} title="Delete post">🗑</button>
                                    )}
                                </div>
                                {/* Content */}
                                <div style={{ fontSize: 15, color: C.white, lineHeight: 1.6, marginBottom: post.link_url ? 10 : 12, wordBreak: "break-word" }}>
                                    {post.content.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                                        /^https?:\/\//.test(part)
                                            ? <a key={i} href={part} target="_blank" rel="noreferrer" style={{ color: C.accent, textDecoration: "underline", wordBreak: "break-all" }}>{part}</a>
                                            : part
                                    )}
                                </div>
                                {/* Link Preview Card */}
                                {post.link_url && (
                                    <a href={post.link_url} target="_blank" rel="noreferrer" style={{ display: "block", marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", textDecoration: "none", background: C.card2, transition: "border-color 0.15s" }}
                                        onMouseEnter={e => e.currentTarget.style.borderColor = C.accent + "88"}
                                        onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                                        {post.link_image && <img src={post.link_image} alt="" style={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block" }} onError={e => e.target.style.display = "none"} />}
                                        <div style={{ padding: "10px 14px" }}>
                                            <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>{post.link_domain}</div>
                                            {post.link_title && <div style={{ fontWeight: 700, fontSize: 14, color: C.white, marginBottom: 3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{post.link_title}</div>}
                                            {post.link_description && <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{post.link_description}</div>}
                                        </div>
                                    </a>
                                )}
                                {/* Actions */}
                                {(() => {
                                    const postReplies = _replies.filter(r => r.post_id === post.id);
                                    const isExpanded = expandedReplies[post.id];
                                    return (
                                        <>
                                            <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: isExpanded ? 12 : 0 }}>
                                                <button
                                                    onClick={() => toggleLike(post)}
                                                    style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, color: liked ? "#e11d48" : C.muted, fontSize: 13, fontWeight: liked ? 700 : 400, padding: 0, transition: "color 0.15s" }}
                                                >
                                                    <span style={{ fontSize: 16 }}>{liked ? "❤️" : "🤍"}</span>
                                                    {likes.length > 0 && <span>{likes.length}</span>}
                                                </button>
                                                <button
                                                    onClick={() => toggleReply(post.id)}
                                                    style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, color: isExpanded ? C.accent : C.muted, fontSize: 13, padding: 0, transition: "color 0.15s", fontWeight: isExpanded ? 700 : 400 }}
                                                >
                                                    <span style={{ fontSize: 15 }}>💬</span>
                                                    <span>{postReplies.length > 0 ? postReplies.length : ""} {postReplies.length === 1 ? "Reply" : postReplies.length > 1 ? "Replies" : "Reply"}</span>
                                                </button>
                                            </div>
                                            {isExpanded && (
                                                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 4 }}>
                                                    {/* Existing replies */}
                                                    {postReplies.map(rep => {
                                                        const repAuthor = _staff.find(s => s.id === rep.author_id);
                                                        const repOnline = repAuthor ? isOnline(repAuthor.last_login) : false;
                                                        const repVerified = rep.author_type === "full_time" || rep.author_role === "admin";
                                                        const canDelRep = rep.author_id === user.id || isAdminUser;
                                                        return (
                                                            <div key={rep.id} style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                                                                <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.border, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0, fontWeight: 700 }}>
                                                                    {repAuthor?.profile_image ? <img src={repAuthor.profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (rep.author_name?.charAt(0) || "?")}
                                                                </div>
                                                                <div style={{ flex: 1, background: C.card2, borderRadius: 10, padding: "8px 12px" }}>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                                                                        <span style={{ fontWeight: 700, fontSize: 13, color: C.white }}>{rep.author_name}</span>
                                                                        {repVerified && <VerifiedBadge />}
                                                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: repOnline ? C.green : C.red, display: "inline-block", boxShadow: repOnline ? `0 0 4px ${C.green}` : "none" }} />
                                                                        <span style={{ fontSize: 11, color: C.muted }}>{timeAgoLocal(rep.created_at)}</span>
                                                                        {canDelRep && (
                                                                            <button onClick={() => deleteReply(rep.id)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12, padding: "0 4px", marginLeft: "auto" }} title="Delete">🗑</button>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ fontSize: 13, color: C.white, lineHeight: 1.5, wordBreak: "break-word" }}>{rep.content}</div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {/* Reply compose */}
                                                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 4 }}>
                                                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.border, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>
                                                            {_staff.find(s => s.id === user.id)?.profile_image
                                                                ? <img src={_staff.find(s => s.id === user.id).profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                                : (user.name?.charAt(0) || "?")}
                                                        </div>
                                                        <div style={{ flex: 1, display: "flex", gap: 8, alignItems: "flex-end" }}>
                                                            <textarea
                                                                value={replyText[post.id] || ""}
                                                                onChange={e => setReplyText(prev => ({ ...prev, [post.id]: e.target.value }))}
                                                                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitReply(post.id); } }}
                                                                placeholder="Write a reply... (Enter to send)"
                                                                rows={1}
                                                                style={{ flex: 1, background: C.card2, border: `1px solid ${C.border}`, borderRadius: 20, padding: "8px 14px", color: C.white, fontSize: 13, resize: "none", fontFamily: "inherit", outline: "none", lineHeight: 1.4 }}
                                                            />
                                                            <button
                                                                onClick={() => submitReply(post.id)}
                                                                disabled={replyPosting[post.id] || !(replyText[post.id] || "").trim()}
                                                                style={{ ...styles.btn(C.accent), padding: "7px 14px", fontSize: 13, opacity: !(replyText[post.id] || "").trim() ? 0.5 : 1, flexShrink: 0 }}
                                                            >{replyPosting[post.id] ? "..." : "↩"}</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                );
            })}

            {/* Infinite scroll sentinel */}
            {visibleCount < filtered.length ? (
                <div ref={sentinelRef} style={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 13, color: C.muted }}>Loading more...</span>
                </div>
            ) : (
                filtered.length > 0 && (
                    <div style={{ textAlign: "center", padding: "24px 0 8px", fontSize: 12, color: C.muted }}>
                        — You've seen all {filtered.length} posts —
                    </div>
                )
            )}
        </div>
    );
}


// ─── Badge Definitions ────────────────────────────────────────────────────────
const BADGE_DEFS = [
    { id: "first_post", icon: "✍️", name: "First Post", desc: "Published first Feeds post" },
    { id: "perfect_month", icon: "💯", name: "Perfect Month", desc: "No absents in a full month" },
    { id: "streak_7", icon: "🔥", name: "7-Day Streak", desc: "7 consecutive attendance days" },
    { id: "streak_30", icon: "⚡", name: "30-Day Streak", desc: "30 consecutive attendance days" },
    { id: "top_poster", icon: "📣", name: "Top Poster", desc: "Posted 10+ times on Feeds" },
    { id: "liked_10", icon: "❤️", name: "Fan Favourite", desc: "Received 10+ likes on posts" },
    { id: "point_100", icon: "⭐", name: "Century", desc: "Earned 100+ points" },
    { id: "point_500", icon: "🏆", name: "Legend", desc: "Earned 500+ points" },
    { id: "shoutout", icon: "👏", name: "Shoutout Star", desc: "Received a shoutout" },
    { id: "sotm", icon: "🥇", name: "Staff of Month", desc: "Won Staff of the Month" },
];

function computeBadges(staffId) {
    const myPosts = _posts.filter(p => p.author_id === staffId);
    const totalLikes = myPosts.reduce((a, p) => a + (Array.isArray(p.likes) ? p.likes.length : 0), 0);
    const pts = getStaffPoints(staffId).earned;
    const shoutouts = _posts.filter(p => p.post_type === "shoutout" && p.tagged_id === staffId);
    const isSotm = _sotm.some(s => s.staff_id === staffId);
    const earned = [];
    if (myPosts.length >= 1) earned.push("first_post");
    if (myPosts.length >= 10) earned.push("top_poster");
    if (totalLikes >= 10) earned.push("liked_10");
    if (pts >= 100) earned.push("point_100");
    if (pts >= 500) earned.push("point_500");
    if (shoutouts.length > 0) earned.push("shoutout");
    if (isSotm) earned.push("sotm");
    // streak & perfect month from attendance
    const today = new Date();
    let streak = 0;
    for (let i = 0; i < 60; i++) {
        const d = new Date(today); d.setDate(d.getDate() - i);
        const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const st = getStatusForDate(staffId, ds);
        if (st === "present" || st === "saturday" || st === "holiday") streak++;
        else break;
    }
    if (streak >= 7) earned.push("streak_7");
    if (streak >= 30) earned.push("streak_30");
    // perfect month last month
    const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const daysInLm = new Date(lm.getFullYear(), lm.getMonth() + 1, 0).getDate();
    const lmDays = Array.from({ length: daysInLm }, (_, i) => { const d = new Date(lm); d.setDate(i + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; });
    const noAbsent = lmDays.every(d => { const st = getStatusForDate(staffId, d); return st !== "absent" && st !== "auto_absent" && st !== "not_marked"; });
    if (noAbsent && lmDays.length > 0) earned.push("perfect_month");
    return BADGE_DEFS.filter(b => earned.includes(b.id));
}

// ─── Mood Check-in (embedded in StaffDashboard clock-in area) ─────────────────
function MoodCheckin({ user, reload }) {
    const today = todayStr();
    const myMood = _moods.find(m => m.staff_id === user.id && m.date === today);
    const MOODS = [["😊", "Happy"], ["😴", "Tired"], ["🔥", "Energized"], ["😤", "Stressed"], ["🤒", "Unwell"], ["😎", "Confident"], ["🤔", "Focused"]];
    const [saving, setSaving] = useState(false);

    const pickMood = async (emoji) => {
        setSaving(true);
        try {
            await db.upsertMood({ id: `${user.id}_${today}`, staff_id: user.id, date: today, mood: emoji, created_at: new Date().toISOString() });
            await reload();
        } finally { setSaving(false); }
    };

    return (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 8 }}>HOW ARE YOU FEELING TODAY?</div>
            {myMood ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 28 }}>{myMood.mood}</span>
                    <span style={{ fontSize: 13, color: C.muted }}>{MOODS.find(m => m[0] === myMood.mood)?.[1] || ""}  · <span style={{ color: C.accent, cursor: "pointer" }} onClick={() => pickMood("")}>Change</span></span>
                </div>
            ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {MOODS.map(([emoji, label]) => (
                        <button key={emoji} onClick={() => pickMood(emoji)} disabled={saving} title={label}
                            style={{ fontSize: 22, background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "6px 10px", cursor: "pointer", transition: "transform 0.1s" }}>
                            {emoji}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Staff of the Month ───────────────────────────────────────────────────────
function StaffOfMonth({ isAdmin, reload }) {
    const mobile = useMobile();
    const now = new Date();
    const current = _sotm.find(s => s.month === now.getMonth() + 1 && s.year === now.getFullYear());
    const winner = current ? _staff.find(s => s.id === current.staff_id) : null;
    const [sel, setSel] = useState(_staff[0]?.id || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const s = _staff.find(st => st.id === sel);
        try {
            await db.setSotm(sel, now.getFullYear(), now.getMonth() + 1); // also awards the +50 points (once)
            await reload();
            showToast(`${s?.name || "Staff"} awarded Staff of the Month! 🏆`);
        } finally { setSaving(false); }
    };

    return (
        <div style={{ ...styles.card, background: `linear-gradient(135deg, ${C.card} 0%, #f59e0b11 100%)`, border: `1px solid #f59e0b33`, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: mobile ? "flex-start" : "center", justifyContent: "space-between", flexDirection: mobile ? "column" : "row", gap: 12 }}>
                <div>
                    <div style={{ fontSize: 12, color: "#f59e0b", fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>🥇 STAFF OF THE MONTH</div>
                    {winner ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f59e0b22", border: "2px solid #f59e0b", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
                                {winner.profile_image ? <img src={winner.profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : winner.name?.charAt(0)}
                            </div>
                            <div>
                                <div style={{ fontWeight: 900, fontSize: 18, color: C.white }}>{winner.name}</div>
                                <div style={{ fontSize: 12, color: C.muted }}>{winner.designation} · {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][now.getMonth()]} {now.getFullYear()}</div>
                            </div>
                        </div>
                    ) : <div style={{ color: C.muted, fontSize: 13 }}>No winner selected yet for this month.</div>}
                </div>
                {isAdmin && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <select style={{ ...styles.input, width: "auto" }} value={sel} onChange={e => setSel(e.target.value)}>
                            {_staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <button style={styles.btn("#f59e0b")} onClick={save} disabled={saving}>{saving ? "..." : "🥇 Set Winner"}</button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Shoutout Button (on Explore compose) ─────────────────────────────────────
function ShoutoutModal({ user, onClose, reload }) {
    const mobile = useMobile();
    const [sel, setSel] = useState("");
    const [msg, setMsg] = useState("");
    const [saving, setSaving] = useState(false);
    const others = _staff.filter(s => s.id !== user.id);

    const send = async () => {
        if (!sel || !msg.trim()) return;
        setSaving(true);
        const target = _staff.find(s => s.id === sel);
        const me = _staff.find(s => s.id === user.id) || { name: user.name, designation: "", employment_type: "full_time" };
        try {
            const shoutId = "shout_" + Date.now();
            await db.addPost({
                id: shoutId,
                author_id: user.id, author_name: me.name, author_designation: me.designation || "",
                author_type: me.employment_type || "full_time", author_role: "staff",
                content: `👏 Shoutout to **${target?.name}**: ${msg.trim()}`,
                post_type: "shoutout", tagged_id: sel, tagged_name: target?.name || "",
                likes: [], created_at: new Date().toISOString(),
            });
            await claimPoints("claim_shoutout_points", { p_post_id: shoutId }, null);
            await reload(); onClose();
        } finally { setSaving(false); }
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: mobile ? "flex-end" : "center", justifyContent: "center" }} onClick={onClose}>
            <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: mobile ? "20px 20px 0 0" : 16, width: mobile ? "100%" : 420, padding: 24 }}>
                <div style={{ fontWeight: 900, fontSize: 17, color: C.white, marginBottom: 4 }}>👏 Give a Shoutout</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Celebrate a colleague — they earn +5 ⭐ points!</div>
                <select style={{ ...styles.input, marginBottom: 12 }} value={sel} onChange={e => setSel(e.target.value)}>
                    <option value="">Select colleague...</option>
                    {others.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder="What did they do awesome?" rows={3}
                    style={{ width: "100%", background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", color: C.white, fontSize: 14, resize: "none", fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 12 }} />
                <div style={{ display: "flex", gap: 10 }}>
                    <button style={{ ...styles.btn(C.green), flex: 1 }} onClick={send} disabled={saving || !sel || !msg.trim()}>{saving ? "Sending..." : "👏 Send Shoutout"}</button>
                    <button style={styles.btnOut} onClick={onClose}>Cancel</button>
                </div>
            </div>
        </div>
    );
}

// ─── Work Log ─────────────────────────────────────────────────────────────────
function WorkLogPage({ user, isAdmin, reload }) {
    const [text, setText] = useState("");
    const [saving, setSaving] = useState(false);
    const [viewStaff, setViewStaff] = useState("all");
    const today = todayStr();
    const myTodayLog = _workLogs.find(w => w.staff_id === user.id && w.date === today);

    const submit = async () => {
        if (!text.trim()) return;
        setSaving(true);
        const me = _staff.find(s => s.id === user.id) || { name: user.name };
        try {
            await db.addWorkLog({ id: "wl_" + Date.now(), staff_id: user.id, staff_name: me.name, date: today, content: text.trim(), created_at: new Date().toISOString() });
            setText(""); await reload();
            showToast("Work log submitted!");
        } finally { setSaving(false); }
    };

    const logs = isAdmin
        ? (viewStaff === "all" ? _workLogs : _workLogs.filter(w => w.staff_id === viewStaff))
        : _workLogs.filter(w => w.staff_id === user.id);

    return (
        <div style={{ maxWidth: 680 }}>
            <h2 style={{ margin: "0 0 4px", fontWeight: 900, fontSize: 22 }}>📝 Work Log</h2>
            <p style={{ margin: "0 0 20px", color: C.muted, fontSize: 13 }}>Daily end-of-day summary</p>

            {!isAdmin && (
                <div style={{ ...styles.card, marginBottom: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.white, marginBottom: 8 }}>Today's Log — {today}</div>
                    {myTodayLog ? (
                        <div>
                            <div style={{ fontSize: 14, color: C.white, lineHeight: 1.7, background: C.card2, borderRadius: 10, padding: "10px 14px" }}>{myTodayLog.content}</div>
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>✅ Log submitted for today</div>
                        </div>
                    ) : (
                        <>
                            <textarea value={text} onChange={e => setText(e.target.value)} placeholder="What did you work on today? Any blockers? Plans for tomorrow?" rows={4}
                                style={{ width: "100%", background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", color: C.white, fontSize: 14, resize: "none", fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
                            <button style={styles.btn(C.accent)} onClick={submit} disabled={saving || !text.trim()}>{saving ? "Saving..." : "Submit Log"}</button>
                        </>
                    )}
                </div>
            )}

            {isAdmin && (
                <div style={{ marginBottom: 16 }}>
                    <select style={{ ...styles.input, width: "auto" }} value={viewStaff} onChange={e => setViewStaff(e.target.value)}>
                        <option value="all">All Staff</option>
                        {_staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
            )}

            <div>
                {logs.length === 0 && <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>No work logs yet.</div>}
                {logs.map(w => (
                    <div key={w.id} style={{ ...styles.card, marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                            <div style={{ fontWeight: 700, color: C.white, fontSize: 14 }}>{isAdmin ? w.staff_name : "My Log"} <span style={{ color: C.muted, fontWeight: 400, fontSize: 12 }}>· {w.date}</span></div>
                            {(isAdmin || w.staff_id === user.id) && <button onClick={() => { db.deleteWorkLog(w.id); reload(); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14 }}>🗑</button>}
                        </div>
                        <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>{w.content}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Poll / Voting ────────────────────────────────────────────────────────────
function PollsPage({ user, isAdmin, reload }) {
    const [showAdd, setShowAdd] = useState(false);
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState(["", ""]);
    const [saving, setSaving] = useState(false);

    const addPoll = async () => {
        const opts = options.filter(o => o.trim());
        if (!question.trim() || opts.length < 2) return;
        setSaving(true);
        try {
            const votes = {};
            opts.forEach((_, i) => votes[i] = []);
            await db.addPoll({ id: "poll_" + Date.now(), question: question.trim(), options: opts, votes, created_by: user.id, created_at: new Date().toISOString(), active: true });
            setQuestion(""); setOptions(["", ""]); setShowAdd(false); await reload();
            showToast("Poll created!");
        } finally { setSaving(false); }
    };

    const vote = async (poll, optIdx) => {
        await db.votePoll(poll.id, optIdx);
        await reload();
        showToast("Vote recorded!");
    };

    return (
        <div style={{ maxWidth: 600 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <div><h2 style={{ margin: 0, fontWeight: 900, fontSize: 22 }}>🗳️ Polls</h2><p style={{ margin: "4px 0 0", color: C.muted, fontSize: 13 }}>Vote on team decisions</p></div>
                {isAdmin && <button style={styles.btn()} onClick={() => setShowAdd(v => !v)}>+ New Poll</button>}
            </div>

            {showAdd && (
                <div style={{ ...styles.card, marginBottom: 20 }}>
                    <input style={{ ...styles.input, marginBottom: 10 }} value={question} onChange={e => setQuestion(e.target.value)} placeholder="Poll question..." />
                    {options.map((opt, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                            <input style={{ ...styles.input, flex: 1 }} value={opt} onChange={e => { const o = [...options]; o[i] = e.target.value; setOptions(o); }} placeholder={`Option ${i + 1}`} />
                            {options.length > 2 && <button onClick={() => setOptions(options.filter((_, j) => j !== i))} style={{ ...styles.btnOut, padding: "8px 12px" }}>✕</button>}
                        </div>
                    ))}
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <button style={styles.btnOut} onClick={() => setOptions([...options, ""])}>+ Add Option</button>
                        <button style={styles.btn(C.green)} onClick={addPoll} disabled={saving}>{saving ? "..." : "Create Poll"}</button>
                        <button style={styles.btnOut} onClick={() => setShowAdd(false)}>Cancel</button>
                    </div>
                </div>
            )}

            {_polls.length === 0 && <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>No polls yet.</div>}
            {_polls.map(poll => {
                const totalVotes = Object.values(poll.votes || {}).reduce((a, v) => a + (Array.isArray(v) ? v.length : 0), 0);
                const myVote = Object.keys(poll.votes || {}).find(k => (poll.votes[k] || []).includes(user.id));
                return (
                    <div key={poll.id} style={{ ...styles.card, marginBottom: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                            <div style={{ fontWeight: 800, fontSize: 15, color: C.white }}>{poll.question}</div>
                            {isAdmin && <button onClick={() => { db.deletePoll(poll.id); reload(); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer" }}>🗑</button>}
                        </div>
                        {(poll.options || []).map((opt, i) => {
                            const count = (poll.votes?.[i] || []).length;
                            const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                            const isMyVote = String(myVote) === String(i);
                            return (
                                <div key={i} onClick={() => !isMyVote && vote(poll, i)} style={{ marginBottom: 8, cursor: isMyVote ? "default" : "pointer" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4, color: isMyVote ? C.accent : C.white }}>
                                        <span>{isMyVote ? "✓ " : ""}{opt}</span>
                                        <span style={{ color: C.muted }}>{count} vote{count !== 1 ? "s" : ""} ({pct}%)</span>
                                    </div>
                                    <div style={{ height: 6, background: C.card2, borderRadius: 3, overflow: "hidden" }}>
                                        <div style={{ height: "100%", width: `${pct}%`, background: isMyVote ? C.accent : C.border, borderRadius: 3, transition: "width 0.4s" }} />
                                    </div>
                                </div>
                            );
                        })}
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{totalVotes} total vote{totalVotes !== 1 ? "s" : ""} · {timeAgoLocal(poll.created_at)}</div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Admin Announcements ──────────────────────────────────────────────────────
function AnnouncementsPanel({ user, isAdmin, reload }) {
    const [text, setText] = useState("");
    const [priority, setPriority] = useState("normal");
    const [saving, setSaving] = useState(false);

    const post = async () => {
        if (!text.trim()) return;
        setSaving(true);
        try {
            await db.addAnnouncement({ id: "ann_" + Date.now(), content: text.trim(), priority, author: user.name, created_at: new Date().toISOString() });
            setText(""); await reload();
            showToast("Announcement posted!");
        } finally { setSaving(false); }
    };

    const priorityColor = (p) => p === "urgent" ? C.red : p === "important" ? C.yellow : C.accent;

    return (
        <div>
            {isAdmin && (
                <div style={{ ...styles.card, marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.white, marginBottom: 10 }}>📢 Post Announcement</div>
                    <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Write an announcement for all staff..." rows={3}
                        style={{ width: "100%", background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", color: C.white, fontSize: 14, resize: "none", fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <select style={{ ...styles.input, width: "auto" }} value={priority} onChange={e => setPriority(e.target.value)}>
                            <option value="normal">📌 Normal</option>
                            <option value="important">⚠️ Important</option>
                            <option value="urgent">🚨 Urgent</option>
                        </select>
                        <button style={styles.btn(C.accent)} onClick={post} disabled={saving || !text.trim()}>{saving ? "Posting..." : "📢 Announce"}</button>
                    </div>
                </div>
            )}
            {_announcements.length === 0 && <div style={{ textAlign: "center", color: C.muted, padding: 20, fontSize: 13 }}>No announcements.</div>}
            {_announcements.map(a => (
                <div key={a.id} style={{ ...styles.card, marginBottom: 10, border: `1px solid ${priorityColor(a.priority)}44`, background: `linear-gradient(135deg, ${C.card} 0%, ${priorityColor(a.priority)}08 100%)` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span style={styles.badge(priorityColor(a.priority))}>{a.priority === "urgent" ? "🚨 Urgent" : a.priority === "important" ? "⚠️ Important" : "📌 Notice"}</span>
                                <span style={{ fontSize: 11, color: C.muted }}>{a.author} · {timeAgoLocal(a.created_at)}</span>
                            </div>
                            <div style={{ fontSize: 14, color: C.white, lineHeight: 1.7 }}>{a.content}</div>
                        </div>
                        {isAdmin && <button onClick={() => { db.deleteAnnouncement(a.id); reload(); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", marginLeft: 8 }}>🗑</button>}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── Leave Requests ───────────────────────────────────────────────────────────
function LeaveRequestsPage({ user, isAdmin, reload }) {
    const mobile = useMobile();
    const [tab, setTab] = useState("pending");
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ from: todayStr(), to: todayStr(), reason: "", type: "annual" });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!form.reason.trim()) return;
        setSaving(true);
        const me = _staff.find(s => s.id === user.id) || { name: user.name };
        try {
            await db.addLeaveRequest({ id: "lv_" + Date.now(), staff_id: user.id, staff_name: me.name, from_date: form.from, to_date: form.to, reason: form.reason, type: form.type, status: "pending", created_at: new Date().toISOString() });
            setShowForm(false); await reload();
            showToast("Leave request submitted!");
        } finally { setSaving(false); }
    };

    const updateStatus = async (id, status) => {
        await db.updateLeaveRequest(id, { status, reviewed_at: new Date().toISOString() });
        await reload();
        showToast(status === "approved" ? "Leave approved!" : "Leave rejected.", status === "approved" ? "#16a34a" : "#ef4444");
    };

    const typeColor = (t) => t === "sick" ? C.red : t === "emergency" ? C.yellow : C.accent;
    const statusColor = (s) => s === "approved" ? C.green : s === "rejected" ? C.red : C.yellow;

    const myLeaves = _leaveRequests.filter(l => l.staff_id === user.id);
    const allLeaves = _leaveRequests;
    const pending = allLeaves.filter(l => l.status === "pending");
    const list = isAdmin
        ? (tab === "pending" ? pending : tab === "approved" ? allLeaves.filter(l => l.status === "approved") : allLeaves.filter(l => l.status === "rejected"))
        : myLeaves;

    return (
        <div style={{ maxWidth: 700 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                <div>
                    <h2 style={{ margin: 0, fontWeight: 900, fontSize: 22 }}>🏖️ Leave Requests</h2>
                    <p style={{ margin: "4px 0 0", color: C.muted, fontSize: 13 }}>{isAdmin ? "Manage staff leave applications" : "Apply for leave"}</p>
                </div>
                {!isAdmin && <button style={styles.btn(C.accent)} onClick={() => setShowForm(v => !v)}>+ Request Leave</button>}
                {isAdmin && pending.length > 0 && <span style={styles.badge(C.red)}>{pending.length} pending</span>}
            </div>

            {showForm && !isAdmin && (
                <div style={{ ...styles.card, marginBottom: 20 }}>
                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>FROM</label><input type="date" style={styles.input} value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))} /></div>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>TO</label><input type="date" style={styles.input} value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))} /></div>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>TYPE</label>
                            <select style={styles.input} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                                <option value="annual">🏖️ Annual Leave</option>
                                <option value="sick">🤒 Sick Leave</option>
                                <option value="emergency">🚨 Emergency</option>
                                <option value="other">📋 Other</option>
                            </select>
                        </div>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>REASON</label><input style={styles.input} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Brief reason..." /></div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button style={styles.btn(C.green)} onClick={submit} disabled={saving}>{saving ? "Submitting..." : "Submit Request"}</button>
                        <button style={styles.btnOut} onClick={() => setShowForm(false)}>Cancel</button>
                    </div>
                </div>
            )}

            {isAdmin && (
                <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: `2px solid ${C.border}` }}>
                    {[["pending", "⏳ Pending"], ["approved", "✅ Approved"], ["rejected", "❌ Rejected"]].map(([id, label]) => (
                        <button key={id} onClick={() => setTab(id)} style={{ padding: "8px 20px", border: "none", background: "transparent", borderBottom: tab === id ? `3px solid ${C.accent}` : "3px solid transparent", color: tab === id ? C.accent : C.muted, fontWeight: tab === id ? 700 : 500, fontSize: 13, cursor: "pointer", marginBottom: -2 }}>{label}</button>
                    ))}
                </div>
            )}

            {list.length === 0 && <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>No leave requests.</div>}
            {list.map(l => (
                <div key={l.id} style={{ ...styles.card, marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                        <div>
                            {isAdmin && <div style={{ fontWeight: 700, color: C.white, fontSize: 14, marginBottom: 4 }}>{l.staff_name}</div>}
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                                <span style={styles.badge(typeColor(l.type))}>{l.type === "sick" ? "🤒 Sick" : l.type === "emergency" ? "🚨 Emergency" : l.type === "annual" ? "🏖️ Annual" : "📋 Other"}</span>
                                <span style={styles.badge(statusColor(l.status))}>{l.status}</span>
                                <span style={{ fontSize: 12, color: C.muted }}>{l.from_date} → {l.to_date}</span>
                            </div>
                            <div style={{ fontSize: 13, color: C.muted }}>{l.reason}</div>
                        </div>
                        {isAdmin && l.status === "pending" && (
                            <div style={{ display: "flex", gap: 8 }}>
                                <button style={{ ...styles.btn(C.green), padding: "6px 12px", fontSize: 12 }} onClick={() => updateStatus(l.id, "approved")}>✅ Approve</button>
                                <button style={{ ...styles.btn(C.red), padding: "6px 12px", fontSize: 12 }} onClick={() => updateStatus(l.id, "rejected")}>❌ Reject</button>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── Meeting Rooms ────────────────────────────────────────────────────────────
function MeetingRoomsPage({ user, isAdmin, reload }) {
    const mobile = useMobile();
    const [showAdd, setShowAdd] = useState(false);
    const [roomForm, setRoomForm] = useState({ name: "", capacity: "", description: "" });
    const [saving, setSaving] = useState(false);
    const [bookForm, setBookForm] = useState({ room_id: "", date: todayStr(), from_time: "09:00", to_time: "10:00", purpose: "" });
    const [booking, setBooking] = useState(false);
    const [selRoom, setSelRoom] = useState(null);

    const addRoom = async () => {
        if (!roomForm.name) return;
        setSaving(true);
        try {
            await db.addMeetingRoom({ id: "room_" + Date.now(), name: roomForm.name, capacity: Number(roomForm.capacity) || 0, description: roomForm.description, created_at: new Date().toISOString() });
            setRoomForm({ name: "", capacity: "", description: "" }); setShowAdd(false); await reload();
            showToast("Meeting room added!");
        } finally { setSaving(false); }
    };

    const bookRoom = async () => {
        if (!bookForm.room_id || !bookForm.purpose) return;
        const me = _staff.find(s => s.id === user.id) || { name: user.name };
        setBooking(true);
        try {
            await db.addBooking({ id: "bk_" + Date.now(), room_id: bookForm.room_id, staff_id: user.id, staff_name: me.name, date: bookForm.date, from_time: bookForm.from_time, to_time: bookForm.to_time, purpose: bookForm.purpose, created_at: new Date().toISOString() });
            setSelRoom(null); await reload();
            showToast("Room booked successfully!");
        } finally { setBooking(false); }
    };

    const today = todayStr();

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                <div><h2 style={{ margin: 0, fontWeight: 900, fontSize: 22 }}>🏢 Meeting Rooms</h2><p style={{ margin: "4px 0 0", color: C.muted, fontSize: 13 }}>Book a room for your meeting</p></div>
                {isAdmin && <button style={styles.btn()} onClick={() => setShowAdd(v => !v)}>+ Add Room</button>}
            </div>

            {showAdd && isAdmin && (
                <div style={{ ...styles.card, marginBottom: 20 }}>
                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>ROOM NAME</label><input style={styles.input} value={roomForm.name} onChange={e => setRoomForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Board Room" /></div>
                        <div><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>CAPACITY</label><input style={styles.input} type="number" value={roomForm.capacity} onChange={e => setRoomForm(f => ({ ...f, capacity: e.target.value }))} placeholder="Max people" /></div>
                        <div style={{ gridColumn: mobile ? "auto" : "1 / -1" }}><label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "block", marginBottom: 6 }}>DESCRIPTION</label><input style={styles.input} value={roomForm.description} onChange={e => setRoomForm(f => ({ ...f, description: e.target.value }))} placeholder="Facilities, equipment..." /></div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button style={styles.btn(C.green)} onClick={addRoom} disabled={saving}>{saving ? "..." : "Add Room"}</button>
                        <button style={styles.btnOut} onClick={() => setShowAdd(false)}>Cancel</button>
                    </div>
                </div>
            )}

            {_meetingRooms.length === 0 && <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 40 }}>No rooms added yet. {isAdmin ? "Add your first room above." : "Ask admin to add rooms."}</div>}

            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 24 }}>
                {_meetingRooms.map(room => {
                    const todayBookings = _bookings.filter(b => b.room_id === room.id && b.date === today);
                    const isBusy = todayBookings.length > 0;
                    return (
                        <div key={room.id} style={{ ...styles.card, border: `1px solid ${isBusy ? C.red + "44" : C.green + "44"}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: 15, color: C.white }}>{room.name}</div>
                                    {room.capacity > 0 && <div style={{ fontSize: 12, color: C.muted }}>👥 Up to {room.capacity} people</div>}
                                    {room.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{room.description}</div>}
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                                    <span style={styles.badge(isBusy ? C.red : C.green)}>{isBusy ? "🔴 Busy" : "🟢 Free"}</span>
                                    {isAdmin && <button onClick={() => { db.deleteMeetingRoom(room.id); reload(); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12 }}>🗑 Remove</button>}
                                </div>
                            </div>
                            {todayBookings.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                    {todayBookings.map(b => (
                                        <div key={b.id} style={{ fontSize: 12, color: C.muted, padding: "4px 8px", background: C.card2, borderRadius: 6, marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                                            <span>{b.from_time}–{b.to_time} · {b.staff_name}</span>
                                            {(b.staff_id === user.id || isAdmin) && <button onClick={() => { db.deleteBooking(b.id); reload(); }} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 11 }}>✕</button>}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <button style={{ ...styles.btn(C.accent), width: "100%", padding: "8px" }} onClick={() => setSelRoom(selRoom === room.id ? null : room.id)}>📅 Book This Room</button>
                            {selRoom === room.id && (
                                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                                        <div><label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>DATE</label><input type="date" style={{ ...styles.input, fontSize: 12, padding: "6px 8px" }} value={bookForm.date} onChange={e => setBookForm(f => ({ ...f, date: e.target.value, room_id: room.id }))} /></div>
                                        <div><label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>FROM</label><input type="time" style={{ ...styles.input, fontSize: 12, padding: "6px 8px" }} value={bookForm.from_time} onChange={e => setBookForm(f => ({ ...f, from_time: e.target.value, room_id: room.id }))} /></div>
                                        <div><label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>TO</label><input type="time" style={{ ...styles.input, fontSize: 12, padding: "6px 8px" }} value={bookForm.to_time} onChange={e => setBookForm(f => ({ ...f, to_time: e.target.value, room_id: room.id }))} /></div>
                                        <div><label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>PURPOSE</label><input style={{ ...styles.input, fontSize: 12, padding: "6px 8px" }} value={bookForm.purpose} onChange={e => setBookForm(f => ({ ...f, purpose: e.target.value, room_id: room.id }))} placeholder="Meeting topic" /></div>
                                    </div>
                                    <button style={{ ...styles.btn(C.green), width: "100%", padding: "8px" }} onClick={bookRoom} disabled={booking}>{booking ? "Booking..." : "Confirm Booking"}</button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <h3 style={{ fontWeight: 700, fontSize: 16, margin: "0 0 12px" }}>All Bookings</h3>
            {_bookings.length === 0 && <div style={{ ...styles.card, textAlign: "center", color: C.muted, padding: 24 }}>No bookings yet.</div>}
            {_bookings.map(b => {
                const room = _meetingRooms.find(r => r.id === b.room_id);
                return (
                    <div key={b.id} style={{ ...styles.card, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <div>
                            <div style={{ fontWeight: 700, color: C.white, fontSize: 13 }}>{room?.name || "Room"} · {b.date}</div>
                            <div style={{ fontSize: 12, color: C.muted }}>{b.from_time}–{b.to_time} · {b.staff_name} · {b.purpose}</div>
                        </div>
                        {(b.staff_id === user.id || isAdmin) && <button onClick={() => { db.deleteBooking(b.id); reload(); }} style={{ ...styles.btn(C.red), padding: "5px 10px", fontSize: 12 }}>Cancel</button>}
                    </div>
                );
            })}
        </div>
    );
}

// ─── Badges Page ──────────────────────────────────────────────────────────────
function BadgesPage({ user, isAdmin }) {
    const mobile = useMobile();
    const staffList = isAdmin ? _staff : [_staff.find(s => s.id === user.id)].filter(Boolean);

    return (
        <div>
            <h2 style={{ margin: "0 0 4px", fontWeight: 900, fontSize: 22 }}>🏅 Badges & Achievements</h2>
            <p style={{ margin: "0 0 20px", color: C.muted, fontSize: 13 }}>{isAdmin ? "All staff achievements" : "Your earned achievements"}</p>
            {staffList.map(s => {
                const earned = computeBadges(s.id);
                const unearned = BADGE_DEFS.filter(b => !earned.find(e => e.id === b.id));
                return (
                    <div key={s.id} style={{ ...styles.card, marginBottom: 16 }}>
                        {isAdmin && <div style={{ fontWeight: 700, color: C.white, fontSize: 14, marginBottom: 12 }}>{s.name} <span style={{ color: C.muted, fontWeight: 400 }}>· {earned.length}/{BADGE_DEFS.length} badges</span></div>}
                        {!isAdmin && <div style={{ fontWeight: 700, color: C.white, fontSize: 14, marginBottom: 12 }}>Your Badges — {earned.length}/{BADGE_DEFS.length} earned</div>}
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: earned.length > 0 && unearned.length > 0 ? 16 : 0 }}>
                            {earned.map(b => (
                                <div key={b.id} style={{ background: C.accent + "22", border: `1px solid ${C.accent}44`, borderRadius: 12, padding: "12px 10px", textAlign: "center" }}>
                                    <div style={{ fontSize: 28, marginBottom: 4 }}>{b.icon}</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: C.white }}>{b.name}</div>
                                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{b.desc}</div>
                                </div>
                            ))}
                        </div>
                        {unearned.length > 0 && (
                            <>
                                <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>LOCKED</div>
                                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10 }}>
                                    {unearned.map(b => (
                                        <div key={b.id} style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 10px", textAlign: "center", opacity: 0.4 }}>
                                            <div style={{ fontSize: 28, marginBottom: 4, filter: "grayscale(1)" }}>{b.icon}</div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>{b.name}</div>
                                            <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{b.desc}</div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ─── Freelancer Analytics ─────────────────────────────────────────────────────
function FreelancerAnalytics({ user }) {
    const mobile = useMobile();
    const now = new Date();
    const [filter, setFilter] = useState("this_month");
    const [customStart, setCustomStart] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); });
    const [customEnd, setCustomEnd] = useState(() => new Date().toISOString().slice(0, 10));

    const getRange = () => {
        if (filter === "this_month") {
            const y = now.getFullYear(), m = now.getMonth();
            return [new Date(y, m, 1), new Date(y, m + 1, 0, 23, 59, 59)];
        }
        if (filter === "last_month") {
            const y = now.getFullYear(), m = now.getMonth() - 1;
            return [new Date(y, m, 1), new Date(y, m + 1, 0, 23, 59, 59)];
        }
        if (filter === "last_3") {
            const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
            return [start, new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)];
        }
        if (filter === "this_year") {
            return [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31, 23, 59, 59)];
        }
        return [new Date(customStart), new Date(customEnd + "T23:59:59")];
    };

    const [startDate, endDate] = getRange();

    const myProjects = _projects.filter(p => p.freelancer_id === user.id);
    const myWithdraws = _withdrawRequests.filter(r => r.freelancer_id === user.id);
    const myBonuses = _bonuses.filter(b => b.staff_id === user.id);

    const inRange = (dateStr) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        return d >= startDate && d <= endDate;
    };

    const filteredProjects = myProjects.filter(p => inRange(p.created_at));
    const filteredCompleted = filteredProjects.filter(p => p.status === "cleared");
    const filteredPending = filteredProjects.filter(p => p.status === "pending");
    const filteredWithdraws = myWithdraws.filter(r => inRange(r.created_at));
    const filteredCleared = filteredWithdraws.filter(r => r.status === "cleared");
    const filteredBonuses = myBonuses.filter(b => inRange(b.created_at));
    const filteredPaidBonuses = filteredBonuses.filter(b => b.status === "paid");

    // Build daily/weekly buckets between startDate and endDate
    const diffDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const useBuckets = diffDays <= 31 ? "daily" : "monthly";

    const buildBuckets = (items, dateField, valueField) => {
        const buckets = {};
        if (useBuckets === "daily") {
            let cur = new Date(startDate);
            while (cur <= endDate) {
                const key = cur.toISOString().slice(0, 10);
                buckets[key] = 0;
                cur = new Date(cur); cur.setDate(cur.getDate() + 1);
            }
            items.forEach(item => {
                const key = item[dateField]?.slice(0, 10);
                if (key && buckets[key] !== undefined) buckets[key] += valueField ? Number(item[valueField]) : 1;
            });
        } else {
            // monthly
            let cur = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
            while (cur <= endDate) {
                const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`;
                buckets[key] = 0;
                cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
            }
            items.forEach(item => {
                const key = item[dateField]?.slice(0, 7);
                if (key && buckets[key] !== undefined) buckets[key] += valueField ? Number(item[valueField]) : 1;
            });
        }
        return Object.entries(buckets).map(([label, value]) => ({ label, value }));
    };

    const projectCountData = buildBuckets(filteredProjects, "created_at", null);
    const earningData = buildBuckets(filteredCompleted, "created_at", "price");
    const withdrawData = buildBuckets(filteredCleared, "cleared_at", "amount");

    const fmtLabel = (l) => {
        if (useBuckets === "daily") {
            const d = new Date(l); return `${d.getDate()} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()]}`;
        }
        const [y, m] = l.split("-");
        return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(m) - 1]} ${y.slice(2)}`;
    };

    // SVG Bar Chart
    const BarChart = ({ data, color, prefix = "", height = 160 }) => {
        const max = Math.max(...data.map(d => d.value), 1);
        const W = 100, H = height;
        const BAR_W = Math.max(4, Math.floor((W / data.length) * 0.55));
        const GAP = W / data.length;
        const showEvery = data.length > 20 ? Math.ceil(data.length / 10) : data.length > 10 ? 2 : 1;
        const [hovered, setHovered] = useState(null);
        return (
            <div style={{ position: "relative" }}>
                <svg viewBox={`0 0 ${W * (mobile ? 4 : 6)} ${H + 40}`} style={{ width: "100%", overflow: "visible" }}>
                    {/* Grid lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
                        <g key={i}>
                            <line x1={0} y1={H - pct * H} x2={W * (mobile ? 4 : 6)} y2={H - pct * H} stroke={C.border} strokeWidth="0.3" strokeDasharray="2,2" />
                            <text x={-2} y={H - pct * H + 3} textAnchor="end" fontSize="4" fill={C.muted}>{prefix}{Math.round(max * pct).toLocaleString()}</text>
                        </g>
                    ))}
                    {data.map((d, i) => {
                        const barH = (d.value / max) * H;
                        const x = i * GAP * (mobile ? 4 : 6) / data.length * data.length + (GAP * (mobile ? 4 : 6) / data.length - BAR_W * (mobile ? 2.2 : 3)) / 2;
                        const bw = BAR_W * (mobile ? 2.2 : 3);
                        const isHov = hovered === i;
                        return (
                            <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
                                {/* Bar background */}
                                <rect x={x} y={0} width={bw} height={H} fill={color + "11"} rx="2" />
                                {/* Bar */}
                                <rect x={x} y={H - barH} width={bw} height={barH} fill={isHov ? color : color + "cc"} rx="2" style={{ transition: "all 0.2s" }} />
                                {/* Label */}
                                {(i % showEvery === 0) && (
                                    <text x={x + bw / 2} y={H + 12} textAnchor="middle" fontSize="3.5" fill={C.muted} transform={data.length > 15 ? `rotate(-35, ${x + bw / 2}, ${H + 12})` : ""}>{fmtLabel(d.label)}</text>
                                )}
                                {/* Tooltip */}
                                {isHov && d.value > 0 && (
                                    <g>
                                        <rect x={x + bw / 2 - 18} y={H - barH - 16} width={36} height={12} fill={C.card} rx="3" stroke={color} strokeWidth="0.5" />
                                        <text x={x + bw / 2} y={H - barH - 8} textAnchor="middle" fontSize="4" fill={color} fontWeight="bold">{prefix}{d.value.toLocaleString()}</text>
                                    </g>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        );
    };

    // Line Chart for earnings trend
    const LineChart = ({ data, color, prefix = "" }) => {
        const max = Math.max(...data.map(d => d.value), 1);
        const W = mobile ? 400 : 600;
        const H = 140;
        const padL = 40, padR = 10, padT = 20, padB = 30;
        const plotW = W - padL - padR;
        const plotH = H - padT - padB;
        const pts = data.map((d, i) => ({ x: padL + (i / Math.max(data.length - 1, 1)) * plotW, y: padT + plotH - (d.value / max) * plotH }));
        const polyline = pts.map(p => `${p.x},${p.y}`).join(" ");
        const area = [`${pts[0]?.x},${padT + plotH}`, ...pts.map(p => `${p.x},${p.y}`), `${pts[pts.length - 1]?.x},${padT + plotH}`].join(" ");
        const showEvery = data.length > 20 ? Math.ceil(data.length / 10) : data.length > 10 ? 2 : 1;
        const [hovered, setHovered] = useState(null);
        return (
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", overflow: "visible" }}>
                {/* Grid */}
                {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
                    <g key={i}>
                        <line x1={padL} y1={padT + plotH - pct * plotH} x2={W - padR} y2={padT + plotH - pct * plotH} stroke={C.border} strokeWidth="0.5" strokeDasharray="3,3" />
                        <text x={padL - 3} y={padT + plotH - pct * plotH + 3} textAnchor="end" fontSize="5" fill={C.muted}>{prefix}{Math.round(max * pct / (prefix === "Rs." ? 1000 : 1)).toLocaleString()}{prefix === "Rs." ? "k" : ""}</text>
                    </g>
                ))}
                {/* Area fill */}
                {pts.length > 1 && <polygon points={area} fill={color + "18"} />}
                {/* Line */}
                {pts.length > 1 && <polyline points={polyline} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />}
                {/* Points + hover */}
                {pts.map((p, i) => (
                    <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
                        <circle cx={p.x} cy={p.y} r={hovered === i ? 4 : 2.5} fill={hovered === i ? color : C.bg} stroke={color} strokeWidth="1.5" style={{ transition: "r 0.15s" }} />
                        {(i % showEvery === 0) && <text x={p.x} y={H - 4} textAnchor="middle" fontSize="5" fill={C.muted} transform={data.length > 15 ? `rotate(-35, ${p.x}, ${H - 4})` : ""}>{fmtLabel(data[i].label)}</text>}
                        {hovered === i && data[i].value > 0 && (
                            <g>
                                <rect x={p.x - 22} y={p.y - 18} width={44} height={13} fill={C.card} rx="3" stroke={color} strokeWidth="0.5" />
                                <text x={p.x} y={p.y - 9} textAnchor="middle" fontSize="5" fill={color} fontWeight="bold">{prefix}{data[i].value.toLocaleString()}</text>
                            </g>
                        )}
                    </g>
                ))}
            </svg>
        );
    };

    const totalEarned = filteredCompleted.reduce((a, p) => a + Number(p.price), 0);
    const totalWithdrawn = filteredCleared.reduce((a, r) => a + Number(r.amount), 0);
    const totalBonuses = filteredPaidBonuses.reduce((a, b) => a + Number(b.amount), 0);
    const completionRate = filteredProjects.length > 0 ? Math.round((filteredCompleted.length / filteredProjects.length) * 100) : 0;

    const periodLabel = filter === "this_month" ? `This Month (${now.toLocaleString("default", { month: "long" })} ${now.getFullYear()})`
        : filter === "last_month" ? (() => { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); return `Last Month (${d.toLocaleString("default", { month: "long" })} ${d.getFullYear()})`; })()
        : filter === "last_3" ? "Last 3 Months"
        : filter === "this_year" ? `This Year (${now.getFullYear()})`
        : `${customStart} to ${customEnd}`;

    const downloadAnalyticsPDF = async () => {
        const logo = await loadLogoBase64();
        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const W = 210, mL = 18, mR = 18, cW = W - mL - mR;
        let y = 0;
        const staff = _staff.find(s => s.id === user.id);

        // Header
        pdf.setFillColor(17, 24, 39);
        pdf.rect(0, 0, W, 44, "F");
        if (logo) { try { pdf.addImage(logo, "PNG", mL, 9, 22, 22); } catch (_) {} }
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18); pdf.setFont("helvetica", "bold");
        pdf.text("ANALYTICS REPORT", mL + 26, 20);
        pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
        pdf.setTextColor(180, 180, 180);
        pdf.text(`${user.name}  •  ${staff?.designation || ""}`, mL + 26, 28);
        pdf.text(`Period: ${periodLabel}`, mL + 26, 35);
        y = 54;

        // KPI Summary
        pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(200, 200, 200);
        pdf.text("SUMMARY", mL, y); y += 7;
        const kpis = [
            ["Total Projects", filteredProjects.length.toString(), "#6366f1"],
            ["Completed", filteredCompleted.length.toString(), "#22c55e"],
            ["Pending", filteredPending.length.toString(), "#f59e0b"],
            ["Completion Rate", `${completionRate}%`, completionRate >= 70 ? "#22c55e" : completionRate >= 40 ? "#f59e0b" : "#ef4444"],
            ["Total Earned", `Rs.${totalEarned.toLocaleString()}`, "#22c55e"],
            ["Total Withdrawn", `Rs.${totalWithdrawn.toLocaleString()}`, "#a855f7"],
            ["Bonuses Received", `Rs.${totalBonuses.toLocaleString()}`, "#f59e0b"],
        ];
        const colW = cW / 3;
        kpis.forEach((k, i) => {
            const col = i % 3;
            const row = Math.floor(i / 3);
            const bx = mL + col * colW, by = y + row * 22;
            pdf.setFillColor(30, 40, 55); pdf.roundedRect(bx, by, colW - 3, 18, 3, 3, "F");
            pdf.setFontSize(8); pdf.setFont("helvetica", "normal"); pdf.setTextColor(150, 150, 150);
            pdf.text(k[0].toUpperCase(), bx + 4, by + 6);
            pdf.setFontSize(13); pdf.setFont("helvetica", "bold");
            const rgb = k[2].replace("#", "");
            pdf.setTextColor(parseInt(rgb.slice(0,2),16), parseInt(rgb.slice(2,4),16), parseInt(rgb.slice(4,6),16));
            pdf.text(k[1], bx + 4, by + 14);
        });
        y += Math.ceil(kpis.length / 3) * 22 + 10;

        // Projects Table
        pdf.setDrawColor(60, 60, 80); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 6;
        pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(200, 200, 200);
        pdf.text("PROJECTS", mL, y); y += 7;

        if (filteredProjects.length === 0) {
            pdf.setFontSize(10); pdf.setFont("helvetica", "italic"); pdf.setTextColor(130, 130, 130);
            pdf.text("No projects in this period.", mL, y); y += 10;
        } else {
            const pCols = [["Project", 70], ["Date", 28], ["Status", 26], ["Amount", 32]];
            let px = mL;
            pdf.setFillColor(30, 40, 55); pdf.rect(mL, y - 5, cW, 9, "F");
            pdf.setFontSize(8); pdf.setFont("helvetica", "bold"); pdf.setTextColor(150, 150, 150);
            pCols.forEach(([label, w]) => { pdf.text(label, px + 2, y); px += w; });
            y += 5;
            filteredProjects.forEach((p, idx) => {
                if (y > 265) { pdf.addPage(); y = 20; }
                if (idx % 2 === 0) { pdf.setFillColor(22, 30, 42); pdf.rect(mL, y - 4, cW, 8, "F"); }
                px = mL;
                pdf.setFontSize(9); pdf.setFont("helvetica", "normal"); pdf.setTextColor(220, 220, 220);
                const title = p.title.length > 38 ? p.title.slice(0, 36) + "…" : p.title;
                pdf.text(title, px + 2, y); px += 70;
                pdf.text(p.created_at ? new Date(p.created_at).toLocaleDateString() : "—", px + 2, y); px += 28;
                if (p.status === "cleared") { pdf.setTextColor(34, 197, 94); pdf.text("Completed", px + 2, y); }
                else { pdf.setTextColor(245, 158, 11); pdf.text("Ongoing", px + 2, y); }
                px += 26;
                pdf.setTextColor(34, 197, 94); pdf.text(`Rs.${Number(p.price).toLocaleString()}`, px + 2, y);
                y += 8;
            });
            pdf.setTextColor(34, 197, 94); pdf.setFont("helvetica", "bold"); pdf.setFontSize(9);
            pdf.text(`Total Earned: Rs.${totalEarned.toLocaleString()}`, mL, y + 4); y += 12;
        }

        // Withdrawals Table
        if (y > 245) { pdf.addPage(); y = 20; }
        pdf.setDrawColor(60, 60, 80); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 6;
        pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(200, 200, 200);
        pdf.text("WITHDRAWALS", mL, y); y += 7;

        if (filteredWithdraws.length === 0) {
            pdf.setFontSize(10); pdf.setFont("helvetica", "italic"); pdf.setTextColor(130, 130, 130);
            pdf.text("No withdrawals in this period.", mL, y); y += 10;
        } else {
            const wCols = [["Amount", 36], ["Method", 30], ["Account", 42], ["Requested", 32], ["Status", 26]];
            let wx = mL;
            pdf.setFillColor(30, 40, 55); pdf.rect(mL, y - 5, cW, 9, "F");
            pdf.setFontSize(8); pdf.setFont("helvetica", "bold"); pdf.setTextColor(150, 150, 150);
            wCols.forEach(([label, w]) => { pdf.text(label, wx + 2, y); wx += w; });
            y += 5;
            filteredWithdraws.forEach((r, idx) => {
                if (y > 265) { pdf.addPage(); y = 20; }
                if (idx % 2 === 0) { pdf.setFillColor(22, 30, 42); pdf.rect(mL, y - 4, cW, 8, "F"); }
                wx = mL;
                pdf.setFontSize(9); pdf.setFont("helvetica", "normal");
                pdf.setTextColor(r.status === "cleared" ? 34 : 245, r.status === "cleared" ? 197 : 158, r.status === "cleared" ? 94 : 11);
                pdf.text(`Rs.${Number(r.amount).toLocaleString()}`, wx + 2, y); wx += 36;
                pdf.setTextColor(220, 220, 220); pdf.text(r.payment_method || "—", wx + 2, y); wx += 30;
                pdf.text(r.account_number || "—", wx + 2, y); wx += 42;
                pdf.text(r.created_at ? new Date(r.created_at).toLocaleDateString() : "—", wx + 2, y); wx += 32;
                if (r.status === "cleared") { pdf.setTextColor(34, 197, 94); pdf.text("Cleared", wx + 2, y); }
                else { pdf.setTextColor(245, 158, 11); pdf.text("Pending", wx + 2, y); }
                y += 8;
            });
            pdf.setTextColor(168, 85, 247); pdf.setFont("helvetica", "bold"); pdf.setFontSize(9);
            pdf.text(`Total Withdrawn: Rs.${totalWithdrawn.toLocaleString()}`, mL, y + 4); y += 12;
        }

        // Bonuses Table
        if (y > 245) { pdf.addPage(); y = 20; }
        pdf.setDrawColor(60, 60, 80); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 6;
        pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(200, 200, 200);
        pdf.text("BONUSES", mL, y); y += 7;

        if (filteredBonuses.length === 0) {
            pdf.setFontSize(10); pdf.setFont("helvetica", "italic"); pdf.setTextColor(130, 130, 130);
            pdf.text("No bonuses in this period.", mL, y); y += 10;
        } else {
            const bCols = [["Title", 60], ["Amount", 32], ["Date", 30], ["Status", 26], ["Note", 26]];
            let bx = mL;
            pdf.setFillColor(30, 40, 55); pdf.rect(mL, y - 5, cW, 9, "F");
            pdf.setFontSize(8); pdf.setFont("helvetica", "bold"); pdf.setTextColor(150, 150, 150);
            bCols.forEach(([label, w]) => { pdf.text(label, bx + 2, y); bx += w; });
            y += 5;
            filteredBonuses.forEach((b, idx) => {
                if (y > 265) { pdf.addPage(); y = 20; }
                if (idx % 2 === 0) { pdf.setFillColor(22, 30, 42); pdf.rect(mL, y - 4, cW, 8, "F"); }
                bx = mL;
                pdf.setFontSize(9); pdf.setFont("helvetica", "normal"); pdf.setTextColor(220, 220, 220);
                const title = (b.title || "Bonus").length > 32 ? (b.title || "Bonus").slice(0, 30) + "…" : (b.title || "Bonus");
                pdf.text(title, bx + 2, y); bx += 60;
                if (b.status === "paid") { pdf.setTextColor(34, 197, 94); } else { pdf.setTextColor(245, 158, 11); }
                pdf.text(`Rs.${Number(b.amount).toLocaleString()}`, bx + 2, y); bx += 32;
                pdf.setTextColor(220, 220, 220);
                pdf.text(b.created_at ? new Date(b.created_at).toLocaleDateString() : "—", bx + 2, y); bx += 30;
                if (b.status === "paid") { pdf.setTextColor(34, 197, 94); pdf.text("Paid", bx + 2, y); }
                else { pdf.setTextColor(245, 158, 11); pdf.text("Pending", bx + 2, y); }
                bx += 26;
                pdf.setTextColor(180, 180, 180);
                pdf.text((b.note || "—").slice(0, 18), bx + 2, y);
                y += 8;
            });
            pdf.setTextColor(245, 158, 11); pdf.setFont("helvetica", "bold"); pdf.setFontSize(9);
            pdf.text(`Total Bonuses Received: Rs.${totalBonuses.toLocaleString()}`, mL, y + 4); y += 12;
        }

        // Footer
        if (y > 265) { pdf.addPage(); y = 20; }
        y += 4;
        pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 5;
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(60, 60, 60);
        pdf.text("Foxbeep Technology Pvt. Ltd.", mL, y); y += 5;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(120, 120, 120);
        pdf.text("Madhyapur Thimi, Bhaktapur, Nepal  \u2022  Ph: +977-9705516131", mL, y); y += 4;
        pdf.text("Foxbeeptech@gmail.com  \u2022  Support@foxbeep.com  \u2022  www.foxbeep.com", mL, y); y += 5;
        pdf.setFont("helvetica", "italic"); pdf.setFontSize(8);
        pdf.text(`Computer-generated report. Generated: ${new Date().toLocaleDateString()}`, mL, y);

        pdf.save(`Analytics_${user.name.replace(/\s+/g, "_")}_${filter}.pdf`);
        showToast("Analytics report downloaded!");
    };

    // Donut chart for project status
    const DonutChart = ({ completed, pending }) => {
        const total = completed + pending;
        if (total === 0) return <div style={{ textAlign: "center", color: C.muted, padding: 20, fontSize: 13 }}>No projects in range</div>;
        const r = 36, cx = 50, cy = 50, circumference = 2 * Math.PI * r;
        const completedPct = completed / total;
        const completedDash = completedPct * circumference;
        return (
            <div style={{ display: "flex", alignItems: "center", gap: mobile ? 16 : 24, flexWrap: "wrap" }}>
                <svg viewBox="0 0 100 100" style={{ width: mobile ? 90 : 110, flexShrink: 0 }}>
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.border} strokeWidth="10" />
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.yellow} strokeWidth="10"
                        strokeDasharray={`${(1 - completedPct) * circumference} ${circumference}`}
                        strokeDashoffset={0} transform={`rotate(-90 ${cx} ${cy})`} strokeLinecap="round" />
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.green} strokeWidth="10"
                        strokeDasharray={`${completedDash} ${circumference}`}
                        strokeDashoffset={0} transform={`rotate(-90 ${cx} ${cy})`} strokeLinecap="round" />
                    <text x={cx} y={cy - 5} textAnchor="middle" fontSize="13" fontWeight="bold" fill={C.white}>{completionRate}%</text>
                    <text x={cx} y={cy + 9} textAnchor="middle" fontSize="6" fill={C.muted}>done</text>
                </svg>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.green, flexShrink: 0 }} />
                        <div><div style={{ fontSize: 18, fontWeight: 900, color: C.green }}>{completed}</div><div style={{ fontSize: 11, color: C.muted }}>Completed</div></div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.yellow, flexShrink: 0 }} />
                        <div><div style={{ fontSize: 18, fontWeight: 900, color: C.yellow }}>{pending}</div><div style={{ fontSize: 11, color: C.muted }}>Pending</div></div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Header */}
            <div style={{ display: "flex", alignItems: mobile ? "flex-start" : "center", flexDirection: mobile ? "column" : "row", justifyContent: "space-between", gap: 12, marginBottom: 24 }}>
                <div>
                    <h2 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 22 }}>📊 Analytics</h2>
                    <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Your performance, earnings & withdrawal insights</p>
                </div>
                {/* Filter bar + Export */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    {[["this_month", "This Month"], ["last_month", "Last Month"], ["last_3", "Last 3 Mo."], ["this_year", "This Year"], ["custom", "Custom"]].map(([id, label]) => (
                        <button key={id} onClick={() => setFilter(id)} style={{ padding: "7px 13px", borderRadius: 8, border: `1px solid ${filter === id ? C.accent : C.border}`, background: filter === id ? C.accent + "22" : C.card2, color: filter === id ? C.accent : C.muted, fontWeight: filter === id ? 700 : 500, fontSize: 12, cursor: "pointer", transition: "all 0.15s" }}>{label}</button>
                    ))}
                    <button onClick={downloadAnalyticsPDF} style={{ ...styles.btn(C.green), padding: "7px 14px", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}><Download size={13} /> Export PDF</button>
                </div>
            </div>

            {/* Custom date range */}
            {filter === "custom" && (
                <div style={{ ...styles.card, marginBottom: 20, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>FROM</label>
                        <input type="date" style={{ ...styles.input, width: "auto" }} value={customStart} onChange={e => setCustomStart(e.target.value)} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>TO</label>
                        <input type="date" style={{ ...styles.input, width: "auto" }} value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                    </div>
                </div>
            )}

            {/* Summary KPI cards */}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(5, 1fr)", gap: mobile ? 10 : 16, marginBottom: 24 }}>
                <StatCard icon="📁" label="Total Projects" value={filteredProjects.length} color={C.accent} sub={`${filteredCompleted.length} completed`} />
                <StatCard icon="💰" label="Earned" value={`Rs.${totalEarned.toLocaleString()}`} color={C.green} sub={`${filteredCompleted.length} project(s)`} />
                <StatCard icon="📤" label="Withdrawn" value={`Rs.${totalWithdrawn.toLocaleString()}`} color={C.purple} sub={`${filteredCleared.length} request(s)`} />
                <StatCard icon="🎁" label="Bonuses" value={`Rs.${totalBonuses.toLocaleString()}`} color={C.yellow} sub={`${filteredPaidBonuses.length} paid`} />
                <StatCard icon="🎯" label="Completion" value={`${completionRate}%`} color={completionRate >= 70 ? C.green : completionRate >= 40 ? C.yellow : C.red} sub={`${filteredPending.length} still pending`} />
            </div>

            {/* Row 1: Earnings line chart + Project donut */}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1.6fr 1fr", gap: mobile ? 12 : 16, marginBottom: 16 }}>
                {/* Earnings chart */}
                <div style={styles.card}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: C.white }}>💸 Earnings Over Time</div>
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Completed project income</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 20, fontWeight: 900, color: C.green }}>Rs.{totalEarned.toLocaleString()}</div>
                            <div style={{ fontSize: 11, color: C.muted }}>total earned</div>
                        </div>
                    </div>
                    {earningData.every(d => d.value === 0)
                        ? <div style={{ textAlign: "center", color: C.muted, padding: "32px 0", fontSize: 13 }}>No earnings in this period</div>
                        : <LineChart data={earningData} color={C.green} prefix="Rs." />}
                </div>

                {/* Project status donut */}
                <div style={styles.card}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: C.white, marginBottom: 4 }}>🎯 Project Status</div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Completed vs Pending</div>
                    <DonutChart completed={filteredCompleted.length} pending={filteredPending.length} />
                </div>
            </div>

            {/* Row 2: Projects bar chart */}
            <div style={{ ...styles.card, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: C.white }}>📁 Projects Over Time</div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Number of projects added</div>
                    </div>
                    <span style={styles.badge(C.accent)}>{filteredProjects.length} total</span>
                </div>
                {projectCountData.every(d => d.value === 0)
                    ? <div style={{ textAlign: "center", color: C.muted, padding: "32px 0", fontSize: 13 }}>No projects in this period</div>
                    : <BarChart data={projectCountData} color={C.accent} />}
            </div>

            {/* Row 3: Withdrawal bar chart */}
            <div style={{ ...styles.card, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: C.white }}>📤 Withdrawal History</div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Cleared withdrawals over time</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: C.purple }}>Rs.{totalWithdrawn.toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>total withdrawn</div>
                    </div>
                </div>
                {withdrawData.every(d => d.value === 0)
                    ? <div style={{ textAlign: "center", color: C.muted, padding: "32px 0", fontSize: 13 }}>No cleared withdrawals in this period</div>
                    : <BarChart data={withdrawData} color={C.purple} prefix="Rs." />}
            </div>

            {/* Row 4: Withdrawal detail table */}
            <div style={styles.card}>
                <div style={{ fontWeight: 800, fontSize: 15, color: C.white, marginBottom: 4 }}>📋 Withdrawal Detail</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>All withdrawal requests in period</div>
                {filteredWithdraws.length === 0
                    ? <div style={{ textAlign: "center", color: C.muted, padding: "24px 0", fontSize: 13 }}>No withdrawal requests in this period</div>
                    : mobile ? (
                        filteredWithdraws.map(r => (
                            <div key={r.id} style={{ padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <div>
                                        <div style={{ fontSize: 18, fontWeight: 900, color: r.status === "cleared" ? C.green : C.yellow }}>Rs.{Number(r.amount).toLocaleString()}</div>
                                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{r.payment_method} • {r.account_number}</div>
                                        <div style={{ fontSize: 11, color: C.muted }}>Requested: {new Date(r.created_at).toLocaleDateString()}</div>
                                        {r.cleared_at && <div style={{ fontSize: 11, color: C.green }}>Cleared: {new Date(r.cleared_at).toLocaleDateString()}</div>}
                                    </div>
                                    <span style={styles.badge(r.status === "cleared" ? C.green : C.yellow)}>{r.status === "cleared" ? "✅ Cleared" : "⏳ Pending"}</span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ ...styles.table, minWidth: 480 }}>
                                <thead><tr>
                                    <th style={styles.th}>Amount</th>
                                    <th style={styles.th}>Method</th>
                                    <th style={styles.th}>Account</th>
                                    <th style={styles.th}>Requested</th>
                                    <th style={styles.th}>Cleared</th>
                                    <th style={styles.th}>Status</th>
                                </tr></thead>
                                <tbody>
                                    {filteredWithdraws.map(r => (
                                        <tr key={r.id}>
                                            <td style={styles.td}><strong style={{ color: r.status === "cleared" ? C.green : C.yellow }}>Rs.{Number(r.amount).toLocaleString()}</strong></td>
                                            <td style={styles.td}>{r.payment_method}</td>
                                            <td style={styles.td}>{r.account_number}</td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString()}</td>
                                            <td style={{ ...styles.td, fontSize: 12, color: C.green }}>{r.cleared_at ? new Date(r.cleared_at).toLocaleDateString() : "—"}</td>
                                            <td style={styles.td}><span style={styles.badge(r.status === "cleared" ? C.green : C.yellow)}>{r.status === "cleared" ? "✅ Cleared" : "⏳ Pending"}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                }
            </div>
        </div>
    );
}

function LabelInput({ label, value, onChange, type = "text", placeholder = "" }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6, letterSpacing: 0.5 }}>{label}</label>
            <input style={styles.input} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
        </div>
    );
}


// ─── System Settings Tab ──────────────────────────────────────────────────────
function SystemSettingsTab({ reload, showSaved }) {
    const TIMEZONES = [
        ["Pacific Time (UTC-8/-7)", "America/Los_Angeles"],
        ["Mountain Time (UTC-7/-6)", "America/Denver"],
        ["Central Time (UTC-6/-5)", "America/Chicago"],
        ["Eastern Time (UTC-5/-4)", "America/New_York"],
        ["London (UTC+0/+1)", "Europe/London"],
        ["Paris / Berlin (UTC+1/+2)", "Europe/Paris"],
        ["Dubai (UTC+4)", "Asia/Dubai"],
        ["India (UTC+5:30)", "Asia/Kolkata"],
        ["Kathmandu (UTC+5:45)", "Asia/Kathmandu"],
        ["Bangladesh (UTC+6)", "Asia/Dhaka"],
        ["Bangkok (UTC+7)", "Asia/Bangkok"],
        ["Singapore / KL (UTC+8)", "Asia/Singapore"],
        ["Tokyo / Seoul (UTC+9)", "Asia/Tokyo"],
        ["Sydney (UTC+10/+11)", "Australia/Sydney"],
    ];

    const [tz, setTz] = useState(_appSettings.timezone || "America/Los_Angeles");
    const [postPts, setPostPts] = useState(String(_appSettings.post_points ?? 2));
    const [defaultPage, setDefaultPage] = useState(_appSettings.default_page || "explore");
    const [officeLat, setOfficeLat] = useState(String(_appSettings.office_lat ?? "27.672168937354662"));
    const [officeLng, setOfficeLng] = useState(String(_appSettings.office_lng ?? "85.38469491534256"));
    const [officeRadius, setOfficeRadius] = useState(String(_appSettings.office_radius ?? 100));
    const [officeIp, setOfficeIp] = useState(_appSettings.office_ip || "103.10.31.93,2001:4860:7:50a::fd");
    const [saving, setSaving] = useState(false);

    const nowInTz = () => {
        try {
            return new Date().toLocaleString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
        } catch { return "—"; }
    };

    const [preview, setPreview] = useState(nowInTz());
    useEffect(() => {
        const t = setInterval(() => setPreview(nowInTz()), 1000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tz]);

    const save = async () => {
        setSaving(true);
        try {
            await db.upsertAppSettings({ id: 1, timezone: tz, post_points: Number(postPts) || 2, default_page: defaultPage, office_lat: Number(officeLat), office_lng: Number(officeLng), office_radius: Number(officeRadius) || 100, office_ip: officeIp.trim() });
            _appSettings.timezone = tz;
            _appSettings.post_points = Number(postPts) || 2;
            _appSettings.default_page = defaultPage;
            _appSettings.office_lat = Number(officeLat);
            _appSettings.office_lng = Number(officeLng);
            _appSettings.office_radius = Number(officeRadius) || 100;
            _appSettings.office_ip = officeIp.trim();
            await reload();
            showSaved("System settings saved!");
        } finally { setSaving(false); }
    };

    return (
        <div style={styles.card}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.white, marginBottom: 4 }}>🌐 System Settings</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>These settings apply to the entire system for all users</div>

            <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 8, letterSpacing: 0.5 }}>TIMEZONE</label>
                <select style={{ ...styles.input, marginBottom: 10 }} value={tz} onChange={e => setTz(e.target.value)}>
                    {TIMEZONES.map(([label, val]) => <option key={val} value={val}>{label}</option>)}
                </select>
                <div style={{ padding: "10px 14px", background: C.accent + "11", border: `1px solid ${C.accent}33`, borderRadius: 10, display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 20 }}>🕐</span>
                    <div>
                        <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>CURRENT TIME IN SELECTED ZONE</div>
                        <div style={{ fontSize: 18, fontWeight: 900, color: C.accent, fontVariantNumeric: "tabular-nums" }}>{preview}</div>
                    </div>
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>This timezone is used for Last Login timestamps shown across the system.</div>
            </div>

            <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 8, letterSpacing: 0.5 }}>🏠 STAFF DEFAULT PAGE (on login)</label>
                <select style={styles.input} value={defaultPage} onChange={e => setDefaultPage(e.target.value)}>
                    <option value="explore">🌐 Feeds</option>
                    <option value="dashboard">🏠 My Dashboard</option>
                    <option value="attendance">📋 My Attendance</option>
                    <option value="mysalary">💰 My Salary</option>
                </select>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>The page staff see immediately after logging in. Admin always lands on Dashboard.</div>
            </div>

            <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 8, letterSpacing: 0.5 }}>⭐ EXPLORE POST POINTS</label>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <input
                        style={{ ...styles.input, width: 100 }}
                        type="number"
                        min="0"
                        value={postPts}
                        onChange={e => setPostPts(e.target.value)}
                    />
                    <span style={{ fontSize: 13, color: C.muted }}>points awarded to staff per Feeds post</span>
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Set to 0 to disable points for posting. Admin never receives points.</div>
            </div>

            <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 8, letterSpacing: 0.5 }}>📍 OFFICE LOCATION (for clock-in verification)</label>
                <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Latitude</div>
                        <input style={styles.input} type="text" value={officeLat} onChange={e => setOfficeLat(e.target.value)} placeholder="27.672168..." />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Longitude</div>
                        <input style={styles.input} type="text" value={officeLng} onChange={e => setOfficeLng(e.target.value)} placeholder="85.384694..." />
                    </div>
                    <div style={{ width: 90 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Radius (m)</div>
                        <input style={styles.input} type="number" min="10" value={officeRadius} onChange={e => setOfficeRadius(e.target.value)} />
                    </div>
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Right-click your office on Google Maps → copy coordinates</div>
                <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6, letterSpacing: 0.5 }}>🌐 ALLOWED OFFICE IP(s)</label>
                <input style={styles.input} type="text" value={officeIp} onChange={e => setOfficeIp(e.target.value)} placeholder="103.10.31.93,2001:4860:7:50a::fd" />
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Comma-separated. Staff can clock in if on this IP <strong>OR</strong> within the GPS radius.</div>
            </div>

            <button style={{ ...styles.btn(C.accent), width: "100%", padding: 13, fontSize: 15 }} onClick={save} disabled={saving}>{saving ? "Saving..." : "Save System Settings"}</button>
        </div>
    );
}

// ─── Tools Page ───────────────────────────────────────────────────────────────
function ImageCompressorTool() {
    const [original, setOriginal] = useState(null);
    const [compressed, setCompressed] = useState(null);
    const [quality, setQuality] = useState(80);
    const [maxWidth, setMaxWidth] = useState(1920);
    const [forceJpeg, setForceJpeg] = useState(false);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef();
    const originalImgRef = useRef(null);
    const originalMetaRef = useRef(null);

    const runCompress = (img, fileType, fileName, q, mw, jpeg) => {
        let w = img.naturalWidth, h = img.naturalHeight;
        if (w > mw) { h = Math.round(h * mw / w); w = mw; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        // PNG ignores quality — force JPEG if PNG or user toggled
        const isPng = fileType === "image/png" && !jpeg;
        const outType = isPng ? "image/png" : "image/jpeg";
        const dataUrl = canvas.toDataURL(outType, outType === "image/jpeg" ? q / 100 : undefined);
        const compSize = atob(dataUrl.split(",")[1]).length;
        return { url: dataUrl, size: compSize, name: fileName.replace(/\.[^.]+$/, isPng ? ".png" : ".jpg"), w, h, isPng };
    };

    const processFile = (file) => {
        if (!file || !file.type.startsWith("image/")) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const meta = { url: e.target.result, size: file.size, name: file.name, type: file.type };
            setOriginal(meta);
            setCompressed(null);
            const img = new Image();
            img.onload = () => {
                originalImgRef.current = img;
                originalMetaRef.current = meta;
                setCompressed(runCompress(img, file.type, file.name, quality, maxWidth, forceJpeg));
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    };

    const applySettings = (q, mw, jpeg) => {
        if (!originalImgRef.current || !originalMetaRef.current) return;
        const { type, name } = originalMetaRef.current;
        setCompressed(runCompress(originalImgRef.current, type, name, q, mw, jpeg));
    };

    const fmtSize = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;
    const saving = original && compressed ? Math.round((1 - compressed.size / original.size) * 100) : 0;

    return (
        <div style={styles.card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <ImageDown size={20} color={C.accent} />
                <div style={{ fontWeight: 800, fontSize: 15, color: C.white }}>Image Compressor</div>
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Compress images locally — nothing is uploaded to any server.</div>

            {/* Drop zone */}
            <div
                onClick={() => inputRef.current.click()}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]); }}
                style={{ border: `2px dashed ${dragging ? C.accent : C.border}`, borderRadius: 14, padding: "32px 20px", textAlign: "center", cursor: "pointer", marginBottom: 20, background: dragging ? C.accent + "11" : "transparent", transition: "all 0.2s" }}
            >
                <ImageDown size={32} color={C.muted} style={{ marginBottom: 8 }} />
                <div style={{ color: C.muted, fontSize: 13 }}>Drop an image here or <span style={{ color: C.accent, fontWeight: 700 }}>click to browse</span></div>
                <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>JPG, PNG, WebP supported</div>
                <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => processFile(e.target.files[0])} />
            </div>

            {/* Controls */}
            <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6 }}>QUALITY — {quality}%</label>
                    <input type="range" min="10" max="100" value={quality} onChange={e => { const q = Number(e.target.value); setQuality(q); applySettings(q, maxWidth, forceJpeg); }} style={{ width: "100%", accentColor: C.accent }} />
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6 }}>MAX WIDTH (px)</label>
                    <input type="number" min="100" max="8000" value={maxWidth} onChange={e => { const mw = Number(e.target.value); setMaxWidth(mw); applySettings(quality, mw, forceJpeg); }} style={{ ...styles.input }} />
                </div>
            </div>
            {original?.type === "image/png" && (
                <div style={{ marginBottom: 16, padding: "10px 14px", background: C.yellow + "18", border: `1px solid ${C.yellow}44`, borderRadius: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: C.yellow }}>⚠️ PNG is lossless — quality slider has no effect.</span>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: C.white }}>
                        <input type="checkbox" checked={forceJpeg} onChange={e => { const j = e.target.checked; setForceJpeg(j); applySettings(quality, maxWidth, j); }} />
                        Convert to JPEG (enables quality compression)
                    </label>
                </div>
            )}

            {original && (
                <button onClick={() => applySettings(quality, maxWidth, forceJpeg)} style={{ ...styles.btn(C.accent), marginBottom: 20 }}>
                    Resize / Recompress
                </button>
            )}

            {/* Results */}
            {original && compressed && (
                <div>
                    <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 140, background: C.card, borderRadius: 12, padding: 16, border: `1px solid ${C.border}` }}>
                            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>ORIGINAL</div>
                            <div style={{ fontWeight: 800, fontSize: 18, color: C.white }}>{fmtSize(original.size)}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 140, background: C.card, borderRadius: 12, padding: 16, border: `1px solid ${C.green}44` }}>
                            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>COMPRESSED</div>
                            <div style={{ fontWeight: 800, fontSize: 18, color: C.green }}>{fmtSize(compressed.size)}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 140, background: C.card, borderRadius: 12, padding: 16, border: `1px solid ${C.accent}44` }}>
                            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>SIZE SAVED</div>
                            <div style={{ fontWeight: 800, fontSize: 18, color: saving > 0 ? C.accent : "#ef4444" }}>{saving > 0 ? `-${saving}%` : `+${Math.abs(saving)}%`}</div>
                        </div>
                    </div>
                    <div style={{ marginBottom: 16, borderRadius: 12, overflow: "hidden", border: `1px solid ${C.border}` }}>
                        <img src={compressed.url} alt="compressed preview" style={{ width: "100%", display: "block", maxHeight: 300, objectFit: "contain", background: "#000" }} />
                    </div>
                    <a href={compressed.url} download={compressed.name} style={{ ...styles.btn(C.green), display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
                        <Download size={16} /> Download ({fmtSize(compressed.size)})
                    </a>
                </div>
            )}
        </div>
    );
}

function ToolsPage() {
    const [activeTool, setActiveTool] = useState("compressor");
    const tools = [
        { id: "compressor", icon: ImageDown, label: "Image Compressor" },
    ];
    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <Wrench size={22} color={C.accent} />
                <h2 style={{ margin: 0, fontWeight: 900, fontSize: 22, color: C.white }}>Tools</h2>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
                {tools.map(t => (
                    <button key={t.id} onClick={() => setActiveTool(t.id)} style={{ ...styles.btn(activeTool === t.id ? C.accent : C.card), display: "flex", alignItems: "center", gap: 6, border: `1px solid ${activeTool === t.id ? C.accent : C.border}` }}>
                        <t.icon size={15} /> {t.label}
                    </button>
                ))}
            </div>
            {activeTool === "compressor" && <ImageCompressorTool />}
        </div>
    );
}

// ─── Trello Board Page ─────────────────────────────────────────────────────────
// Trello credentials live only in the "trello-proxy" Edge Function secrets.
// The board id is not secret (used for the "Open in Trello" link).
const TRELLO_BOARD = "5TKMEVKx";

const TRELLO_LABEL_COLORS = {
    green: "#16a34a", green_light: "#4ade80", green_dark: "#14532d",
    yellow: "#ca8a04", yellow_light: "#facc15", yellow_dark: "#713f12",
    orange: "#ea580c", orange_light: "#fb923c", orange_dark: "#7c2d12",
    red: "#dc2626", red_light: "#f87171", red_dark: "#7f1d1d",
    purple: "#9333ea", purple_light: "#c084fc", purple_dark: "#581c87",
    blue: "#2563eb", blue_light: "#60a5fa", blue_dark: "#1e3a8a",
    sky: "#0284c7", sky_light: "#38bdf8", sky_dark: "#0c4a6e",
    lime: "#65a30d", lime_light: "#a3e635", lime_dark: "#365314",
    pink: "#db2777", pink_light: "#f472b6", pink_dark: "#831843",
    black: "#374151",
};

function TrelloPage() {
    const mobile = useMobile();
    const [lists, setLists] = useState([]);
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [expandedCard, setExpandedCard] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);
    const [filterList, setFilterList] = useState("all");

    const fetchBoard = async () => {
        setLoading(true); setErr("");
        try {
            const { lists: listsData, cards: cardsData } = await callFunction("trello-proxy", {});
            if (!Array.isArray(listsData)) throw new Error("Failed to load lists");
            setLists(listsData);
            setCards(cardsData || []);
            setLastFetched(new Date());
        } catch (e) {
            setErr("Could not load Trello board. Check your connection.");
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchBoard(); }, []);

    const getCardsForList = (listId) => cards.filter(c => c.idList === listId).sort((a, b) => a.pos - b.pos);
    const totalCards = cards.length;
    const filteredLists = filterList === "all" ? lists : lists.filter(l => l.id === filterList);

    if (loading) return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 80, gap: 16 }}>
            <div style={{ fontSize: 36 }}>🔄</div>
            <div style={{ color: C.muted, fontSize: 14 }}>Loading Trello board...</div>
        </div>
    );

    if (err) return (
        <div style={{ ...styles.card, textAlign: "center", padding: 48 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <div style={{ color: C.red, fontWeight: 700, marginBottom: 8 }}>{err}</div>
            <button style={styles.btn()} onClick={fetchBoard}>Retry</button>
        </div>
    );

    return (
        <div>
            {/* Header */}
            <div style={{ display: "flex", alignItems: mobile ? "flex-start" : "center", flexDirection: mobile ? "column" : "row", justifyContent: "space-between", marginBottom: 20, gap: 12 }}>
                <div>
                    <h2 style={{ margin: 0, fontWeight: 900, fontSize: 22 }}>📋 Trello Board</h2>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Foxbeep VE Team 12 · {totalCards} open cards · {lastFetched && `Updated ${lastFetched.toLocaleTimeString()}`}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <select
                        value={filterList}
                        onChange={e => setFilterList(e.target.value)}
                        style={{ ...styles.input, width: "auto", fontSize: 13, padding: "8px 12px" }}
                    >
                        <option value="all">All Lists ({lists.length})</option>
                        {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({getCardsForList(l.id).length})</option>)}
                    </select>
                    <button style={{ ...styles.btn(), padding: "8px 16px", fontSize: 13 }} onClick={fetchBoard}>🔄 Refresh</button>
                    <a href={`https://trello.com/b/${TRELLO_BOARD}`} target="_blank" rel="noreferrer" style={{ ...styles.btn(C.green), padding: "8px 16px", fontSize: 13, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        Open in Trello ↗
                    </a>
                </div>
            </div>

            {/* Stats row */}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
                {[
                    { label: "Total Cards", value: totalCards, color: C.accent },
                    { label: "Lists", value: lists.length, color: C.accent2 },
                    { label: "With Due Date", value: cards.filter(c => c.due).length, color: C.yellow },
                    { label: "Labelled", value: cards.filter(c => c.labels?.length > 0).length, color: C.green },
                ].map(s => (
                    <div key={s.label} style={{ ...styles.card, padding: "14px 16px", borderLeft: `3px solid ${s.color}` }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: C.white }}>{s.value}</div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Kanban Board */}
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 16 }}>
                <div style={{ display: "flex", gap: 14, alignItems: "flex-start", minWidth: "max-content" }}>
                    {filteredLists.map(list => {
                        const listCards = getCardsForList(list.id);
                        return (
                            <div key={list.id} style={{ width: 270, flexShrink: 0 }}>
                                {/* List header */}
                                <div style={{ background: C.card2, borderRadius: "10px 10px 0 0", padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `2px solid ${C.accent}44` }}>
                                    <span style={{ fontWeight: 700, fontSize: 13, color: C.white }}>{list.name}</span>
                                    <span style={{ ...styles.badge(C.accent), fontSize: 11 }}>{listCards.length}</span>
                                </div>
                                {/* Cards */}
                                <div style={{ background: C.card2 + "88", borderRadius: "0 0 10px 10px", padding: "8px 8px", display: "flex", flexDirection: "column", gap: 8, maxHeight: 600, overflowY: "auto" }}>
                                    {listCards.length === 0 && (
                                        <div style={{ padding: "20px 10px", textAlign: "center", color: C.muted, fontSize: 12 }}>No cards</div>
                                    )}
                                    {listCards.map(card => {
                                        const isExpanded = expandedCard === card.id;
                                        const isDue = card.due ? new Date(card.due) < new Date() : false;
                                        const dueSoon = card.due ? (new Date(card.due) - new Date()) < 24 * 60 * 60 * 1000 : false;
                                        return (
                                            <div key={card.id}
                                                onClick={() => setExpandedCard(isExpanded ? null : card.id)}
                                                style={{ background: C.card, borderRadius: 8, padding: "10px 12px", cursor: "pointer", border: `1px solid ${isExpanded ? C.accent + "66" : C.border}`, transition: "all 0.15s" }}
                                            >
                                                {/* Labels */}
                                                {card.labels?.length > 0 && (
                                                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                                                        {card.labels.map(label => (
                                                            <span key={label.id} style={{ height: 6, borderRadius: 3, minWidth: 28, background: TRELLO_LABEL_COLORS[label.color] || C.muted, display: "inline-block" }} title={label.name} />
                                                        ))}
                                                    </div>
                                                )}
                                                {/* Card name */}
                                                <div style={{ fontSize: 13, color: C.white, fontWeight: 600, lineHeight: 1.4, marginBottom: 6 }}>{card.name}</div>
                                                {/* Due date */}
                                                {card.due && (
                                                    <div style={{ fontSize: 11, color: isDue ? C.red : dueSoon ? C.yellow : C.muted, display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                                                        🕐 {new Date(card.due).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                                        {isDue && <span style={styles.badge(C.red)}>Overdue</span>}
                                                        {!isDue && dueSoon && <span style={styles.badge(C.yellow)}>Due Soon</span>}
                                                    </div>
                                                )}
                                                {/* Label names */}
                                                {card.labels?.length > 0 && (
                                                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: isExpanded ? 8 : 0 }}>
                                                        {card.labels.map(label => label.name && (
                                                            <span key={label.id} style={{ ...styles.badge(TRELLO_LABEL_COLORS[label.color] || C.muted), fontSize: 10 }}>{label.name}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {/* Expanded: description + link */}
                                                {isExpanded && card.desc && (
                                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                                                        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                                            {card.desc.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                                                                /^https?:\/\//.test(part)
                                                                    ? <a key={i} href={part} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.accent, textDecoration: "underline", wordBreak: "break-all" }}>{part}</a>
                                                                    : part
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                {isExpanded && (
                                                    <a href={card.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                                                        style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 12, color: C.accent, textDecoration: "none", fontWeight: 600 }}>
                                                        Open in Trello ↗
                                                    </a>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ─── Settings Page ────────────────────────────────────────────────────────────
function SettingsPage({ user, isAdmin, reload, setUser }) {
    const mobile = useMobile();
    const [tab, setTab] = useState("profile");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState("");
    const [imgPreview, setImgPreview] = useState(null);
    const [imgErr, setImgErr] = useState("");
    const [imgDeleted, setImgDeleted] = useState(false);

    const staffRecord = _staff.find(s => s.id === user.id);

    const [profile, setProfile] = useState({
        name: user.name || "",
        email: isAdmin ? "support@foxbeep.com" : (staffRecord?.email || user.email || ""),
        bio: isAdmin ? "" : (staffRecord?.bio || ""),
        age: isAdmin ? "" : (staffRecord?.age || ""),
        phone: isAdmin ? "" : (staffRecord?.phone || ""),
        dob: isAdmin ? "" : (staffRecord?.dob || ""),
    });

    const [bank, setBank] = useState({
        bank_name: isAdmin ? "" : (staffRecord?.bank_name || ""),
        bank_account: isAdmin ? "" : (staffRecord?.bank_account || ""),
        bank_branch: isAdmin ? "" : (staffRecord?.bank_branch || ""),
        esewa_number: isAdmin ? "" : (staffRecord?.esewa_number || ""),
    });

    const [passwords, setPasswords] = useState({ current: "", newPass: "", confirm: "" });
    const [passErr, setPassErr] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);

    const showSaved = (msg) => { setSaved(msg); setTimeout(() => setSaved(""), 2500); };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImgErr("");
        if (file.size > 500 * 1024) {
            setImgErr(`Image is too large (${(file.size / 1024).toFixed(0)} KB). Please choose a photo under 500 KB.`);
            e.target.value = "";
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => setImgPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const saveProfile = async () => {
        setSaving(true);
        try {
            if (isAdmin) {
                // Admin: update their staff DB record if it exists, else just update local state
                const adminRecord = _staff.find(s => s.id === user.id);
                if (adminRecord) await db.updateStaff(user.id, { name: profile.name });
                setUser(prev => ({ ...prev, name: profile.name }));
            } else {
                const updates = {
                    name: profile.name,
                    // email is locked — managed by Supabase Auth, not editable here
                    bio: profile.bio,
                    age: profile.age ? Number(profile.age) : null,
                    phone: profile.phone,
                    dob: profile.dob || null,
                    profile_image: imgDeleted ? null : (imgPreview || staffRecord?.profile_image || null),
                };
                await db.updateStaff(user.id, updates);
                await reload();
                setUser(prev => ({ ...prev, name: profile.name }));
            }
            showSaved("Profile saved!");
        } finally { setSaving(false); }
    };

    const saveBank = async () => {
        if (isAdmin) return;
        setSaving(true);
        try {
            await db.updateStaff(user.id, {
                bank_name: bank.bank_name,
                bank_account: bank.bank_account,
                bank_branch: bank.bank_branch,
                esewa_number: bank.esewa_number,
            });
            await reload();
            showSaved("Bank details saved!");
        } finally { setSaving(false); }
    };

    const changePassword = async () => {
        setPassErr("");
        if (passwords.newPass.length < 6) { setPassErr("New password must be at least 6 characters."); return; }
        if (passwords.newPass !== passwords.confirm) { setPassErr("Passwords do not match."); return; }
        setSaving(true);
        try {
            // Re-authenticate with current password before allowing change
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { error: reAuthErr } = await supabase.auth.signInWithPassword({
                    email: session.user.email,
                    password: passwords.current,
                });
                if (reAuthErr) { setPassErr("Current password is incorrect."); return; }
            }
            const { error } = await supabase.auth.updateUser({ password: passwords.newPass });
            if (error) { setPassErr(error.message); return; }
            setPasswords({ current: "", newPass: "", confirm: "" });
            showSaved("Password changed!");
        } finally { setSaving(false); }
    };

    const profileImage = imgDeleted ? null : (imgPreview || staffRecord?.profile_image || null);
    const tabs = [
        { id: "profile", label: "👤 Profile" },
        { id: "bank", label: "🏦 Bank", hidden: isAdmin },
        { id: "password", label: "🔒 Password" },
        { id: "rewards", label: "⭐ Points & Rewards", hidden: !isAdmin },
        { id: "system", label: "🌐 System", hidden: !isAdmin },
    ].filter(t => !t.hidden);



    return (
        <div style={{ maxWidth: 640 }}>
            <h2 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 22 }}>⚙️ Settings</h2>
            <p style={{ margin: "0 0 24px", color: C.muted, fontSize: 13 }}>Manage your account and preferences</p>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `2px solid ${C.border}`, overflowX: mobile ? "auto" : "unset" }}>
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: mobile ? "10px 14px" : "10px 20px", fontWeight: 700, fontSize: mobile ? 13 : 14, border: "none", cursor: "pointer", background: "transparent", borderBottom: tab === t.id ? `3px solid ${C.accent}` : "3px solid transparent", color: tab === t.id ? C.accent : C.muted, marginBottom: -2, whiteSpace: "nowrap" }}>{t.label}</button>
                ))}
            </div>

            {saved && <div style={{ marginBottom: 16, padding: "10px 14px", background: C.green + "22", border: `1px solid ${C.green}44`, borderRadius: 10, color: C.green, fontSize: 13, fontWeight: 600 }}>✅ {saved}</div>}

            {/* ── PROFILE TAB ── */}
            {tab === "profile" && (
                <div style={styles.card}>
                    {/* Avatar */}
                    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ position: "relative" }}>
                            <div style={{ width: 80, height: 80, borderRadius: "50%", background: C.card2, border: `2px solid ${C.border}`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>
                                {profileImage ? <img src={profileImage} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
                            </div>
                            {!isAdmin && (
                                <label style={{ position: "absolute", bottom: 0, right: 0, width: 26, height: 26, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13, border: `2px solid ${C.bg}` }}>
                                    📷
                                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageChange} />
                                </label>
                            )}
                        </div>
                        {/* Admin: upload + delete controls shown next to avatar */}
                        {isAdmin && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                <div style={{ fontSize: 13, color: C.muted }}>Admin profile picture</div>
                                {profileImage ? (
                                    <button onClick={() => { setImgDeleted(true); setImgPreview(null); }} style={{ ...styles.btn(C.red), padding: "6px 14px", fontSize: 12 }}>🗑 Remove Picture</button>
                                ) : (
                                    <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>No picture set</div>
                                )}
                            </div>
                        )}
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 18, color: C.white }}>{user.name}</div>
                            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{isAdmin ? "Administrator" : staffRecord?.designation}</div>
                            {!isAdmin && staffRecord?.employment_type && (
                                <span style={{ ...styles.badge(staffRecord.employment_type === "freelancer" ? C.purple : staffRecord.employment_type === "part_time" ? C.yellow : C.green), marginTop: 6, display: "inline-block" }}>
                                    {staffRecord.employment_type === "freelancer" ? "Freelancer" : staffRecord.employment_type === "part_time" ? "Part Time" : "Full Time"}
                                </span>
                            )}
                        </div>
                    </div>

                    {imgErr && <div style={{ marginBottom: 12, padding: "10px 14px", background: C.red + "22", border: `1px solid ${C.red}44`, borderRadius: 10, color: C.red, fontSize: 13 }}>⚠️ {imgErr}</div>}
                    {imgPreview && !imgErr && <div style={{ marginBottom: 12, padding: "8px 14px", background: C.green + "22", border: `1px solid ${C.green}44`, borderRadius: 10, color: C.green, fontSize: 13 }}>✅ Photo ready to save — click Save Profile below.</div>}
                    <LabelInput label="FULL NAME" value={profile.name} onChange={v => setProfile(p => ({ ...p, name: v }))} placeholder="Your full name" />
                    <LabelInput label="EMAIL ADDRESS" value={profile.email} onChange={v => setProfile(p => ({ ...p, email: v }))} type="email" placeholder="email@example.com" />
                    {!isAdmin && <>
                        <LabelInput label="PHONE NUMBER" value={profile.phone} onChange={v => setProfile(p => ({ ...p, phone: v }))} placeholder="98XXXXXXXX" />
                        <LabelInput label="AGE" value={profile.age} onChange={v => setProfile(p => ({ ...p, age: v }))} type="number" placeholder="Your age" />
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6, letterSpacing: 0.5 }}>DATE OF BIRTH</label>
                            <input type="date" style={styles.input} value={profile.dob || ""} onChange={e => setProfile(p => ({ ...p, dob: e.target.value }))} />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6, letterSpacing: 0.5 }}>BIO</label>
                            <textarea style={{ ...styles.input, height: 80, resize: "vertical" }} value={profile.bio} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} placeholder="A short bio about yourself..." />
                        </div>
                    </>}
                    <button style={{ ...styles.btn(C.accent), width: "100%", padding: 13, fontSize: 15 }} onClick={saveProfile} disabled={saving}>{saving ? "Saving..." : "Save Profile"}</button>
                </div>
            )}

            {/* ── BANK TAB ── */}
            {tab === "bank" && !isAdmin && (
                <div style={styles.card}>
                    <div style={{ marginBottom: 20, padding: "12px 14px", background: C.accent + "11", border: `1px solid ${C.accent}33`, borderRadius: 10, fontSize: 13, color: C.muted }}>
                        💡 These details are used for salary withdrawals. Keep them accurate and up to date.
                    </div>
                    <LabelInput label="BANK NAME" value={bank.bank_name} onChange={v => setBank(b => ({ ...b, bank_name: v }))} placeholder="e.g. NIC Asia Bank" />
                    <LabelInput label="BANK ACCOUNT NUMBER" value={bank.bank_account} onChange={v => setBank(b => ({ ...b, bank_account: v }))} placeholder="Account number" />
                    <LabelInput label="BRANCH" value={bank.bank_branch} onChange={v => setBank(b => ({ ...b, bank_branch: v }))} placeholder="e.g. Kathmandu Main Branch" />
                    <div style={{ marginBottom: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.white, marginBottom: 12 }}>📱 eSewa</div>
                        <LabelInput label="ESEWA NUMBER" value={bank.esewa_number} onChange={v => setBank(b => ({ ...b, esewa_number: v }))} placeholder="98XXXXXXXX" />
                    </div>
                    <button style={{ ...styles.btn(C.accent), width: "100%", padding: 13, fontSize: 15 }} onClick={saveBank} disabled={saving}>{saving ? "Saving..." : "Save Bank Details"}</button>
                </div>
            )}

            {/* ── SYSTEM TAB (admin only) ── */}
            {tab === "rewards" && isAdmin && <RewardsSettingsPanel reload={reload} />}
            {tab === "system" && isAdmin && <SystemSettingsTab reload={reload} showSaved={showSaved} />}

            {/* ── PASSWORD TAB ── */}
            {tab === "password" && (
                <div style={styles.card}>
                    {isAdmin && <div style={{ marginBottom: 16, padding: "12px 14px", background: C.yellow + "11", border: `1px solid ${C.yellow}33`, borderRadius: 10, fontSize: 13, color: C.muted }}>ℹ️ Admin password is managed directly in the app config. Contact your developer to change it.</div>}
                    {!isAdmin && <>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6 }}>CURRENT PASSWORD</label>
                            <div style={{ position: "relative" }}>
                                <input style={{ ...styles.input, paddingRight: 44 }} type={showCurrent ? "text" : "password"} value={passwords.current} onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} placeholder="••••••••" />
                                <span onClick={() => setShowCurrent(v => !v)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", fontSize: 16 }}>{showCurrent ? "🙈" : "👁"}</span>
                            </div>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6 }}>NEW PASSWORD</label>
                            <div style={{ position: "relative" }}>
                                <input style={{ ...styles.input, paddingRight: 44 }} type={showNew ? "text" : "password"} value={passwords.newPass} onChange={e => setPasswords(p => ({ ...p, newPass: e.target.value }))} placeholder="Min. 6 characters" />
                                <span onClick={() => setShowNew(v => !v)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", fontSize: 16 }}>{showNew ? "🙈" : "👁"}</span>
                            </div>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6 }}>CONFIRM NEW PASSWORD</label>
                            <input style={styles.input} type="password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} placeholder="Repeat new password" />
                        </div>
                        {passErr && <div style={{ marginBottom: 12, padding: "10px 14px", background: C.red + "22", border: `1px solid ${C.red}33`, borderRadius: 10, color: C.red, fontSize: 13 }}>⚠️ {passErr}</div>}
                        <button style={{ ...styles.btn(C.red), width: "100%", padding: 13, fontSize: 15 }} onClick={changePassword} disabled={saving}>{saving ? "Changing..." : "🔒 Change Password"}</button>
                    </>}
                </div>
            )}
        </div>
    );
}

// ─── Notification Bell ────────────────────────────────────────────────────────
function NotificationBell({ user, reload }) {
    const [open, setOpen] = useState(false);
    const isFreelancer = _staff.find(s => s.id === user.id)?.employment_type === "freelancer";

    // Freelancer-specific: project + withdrawal notifs (derived from data)
    const myProjects = isFreelancer ? _projects.filter(p => p.freelancer_id === user.id) : [];
    const myRequests = isFreelancer ? _withdrawRequests.filter(r => r.freelancer_id === user.id) : [];

    // Universal reply notifications from DB
    const myDbNotifs = _notifications.filter(n => n.recipient_id === user.id);
    const unreadDbCount = myDbNotifs.filter(n => !n.read).length;

    // Build unified notification list
    const allNotifs = [
        ...myDbNotifs.map(n => ({
            id: n.id,
            icon: "💬",
            color: n.read ? C.muted : C.accent,
            text: n.message,
            sub: n.post_preview ? `"${n.post_preview}${n.post_preview.length >= 60 ? "..." : ""}"` : "",
            date: n.created_at,
            read: n.read,
            isDb: true,
        })),
        ...myProjects.map(p => ({
            id: "proj_" + p.id,
            icon: p.status === "cleared" ? "✔️" : "⏳",
            color: p.status === "cleared" ? C.green : C.yellow,
            text: p.status === "cleared" ? `Project "${p.title}" marked as Completed` : `Project "${p.title}" is Pending`,
            sub: `Rs.${Number(p.price).toLocaleString()}`,
            date: p.created_at,
            read: true,
            isDb: false,
        })),
        ...myRequests.map(r => ({
            id: "req_" + r.id,
            icon: r.status === "cleared" ? "✅" : "⏳",
            color: r.status === "cleared" ? C.green : C.yellow,
            text: r.status === "cleared" ? `Withdrawal of Rs.${Number(r.amount).toLocaleString()} cleared` : `Withdrawal of Rs.${Number(r.amount).toLocaleString()} pending`,
            sub: r.status === "cleared" && r.cleared_at ? `Cleared ${timeAgoLocal(r.cleared_at)}` : `Requested ${timeAgoLocal(r.created_at)}`,
            date: r.cleared_at || r.created_at,
            read: true,
            isDb: false,
        })),
    ].sort((a, b) => new Date(normalizeISO(b.date) || 0) - new Date(normalizeISO(a.date) || 0));

    const totalUnread = unreadDbCount;

    const handleOpen = async () => {
        const wasOpen = open;
        setOpen(v => !v);
        // Mark all DB notifications as read when opening
        if (!wasOpen && unreadDbCount > 0) {
            await db.markAllRead(user.id);
            if (reload) await reload();
        }
    };

    const clearAll = async () => {
        const toDelete = myDbNotifs.map(n => db.deleteNotification(n.id));
        await Promise.all(toDelete);
        if (reload) await reload();
    };

    return (
        <div style={{ position: "relative" }}>
            <div onClick={handleOpen} style={{ cursor: "pointer", position: "relative", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, background: open ? C.accent + "22" : C.card2, border: `1px solid ${open ? C.accent : C.border}` }}>
                <span style={{ fontSize: 18 }}>🔔</span>
                {totalUnread > 0 && (
                    <span style={{ position: "absolute", top: 3, right: 3, minWidth: 16, height: 16, background: C.red, borderRadius: 8, border: `2px solid ${C.bg}`, fontSize: 9, fontWeight: 900, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
                        {totalUnread > 9 ? "9+" : totalUnread}
                    </span>
                )}
            </div>
            {open && (
                <>
                    <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 998 }} />
                    <div style={{ position: "absolute", top: 48, right: 0, width: window.innerWidth < 480 ? "calc(100vw - 24px)" : 360, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", overflow: "hidden" }}>
                        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontWeight: 800, fontSize: 15, color: C.white }}>🔔 Notifications</span>
                            {myDbNotifs.length > 0 && (
                                <button onClick={clearAll} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Clear all</button>
                            )}
                        </div>
                        <div style={{ maxHeight: 420, overflowY: "auto" }}>
                            {allNotifs.length === 0 && (
                                <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>
                                    <div style={{ fontSize: 28, marginBottom: 8 }}>🔕</div>
                                    No notifications yet.
                                </div>
                            )}
                            {allNotifs.map(n => (
                                <div key={n.id} style={{ display: "flex", gap: 12, padding: "11px 16px", borderBottom: `1px solid ${C.border}`, alignItems: "flex-start", background: !n.read ? C.accent + "0a" : "transparent", transition: "background 0.2s" }}>
                                    <div style={{ fontSize: 20, marginTop: 1, flexShrink: 0 }}>{n.icon}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, color: n.read ? C.muted : C.white, fontWeight: n.read ? 500 : 700, lineHeight: 1.4 }}>{n.text}</div>
                                        {n.sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.sub}</div>}
                                        <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{timeAgoLocal(n.date)}</div>
                                    </div>
                                    {!n.read && <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.accent, flexShrink: 0, marginTop: 5 }} />}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}


// ─── Skeleton Loading ─────────────────────────────────────────────────────────
function SkeletonBlock({ w = "100%", h = 16, radius = 8, mb = 0 }) {
    return (
        <div style={{
            width: w, height: h, borderRadius: radius, marginBottom: mb,
            background: `linear-gradient(90deg, ${C.card2} 25%, ${C.border} 50%, ${C.card2} 75%)`,
            backgroundSize: "200% 100%",
            animation: "skeletonShimmer 1.4s infinite",
        }} />
    );
}

function SkeletonScreen() {
    return (
        <div style={{ ...styles.app, display: "flex" }}>
            <style>{`
        @keyframes skeletonShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
            {/* Sidebar skeleton */}
            <div style={{ width: 215, background: C.card, borderRight: `1px solid ${C.border}`, height: "100vh", padding: "20px 16px", flexShrink: 0, display: "flex", flexDirection: "column", gap: 0 }}>
                {/* Logo */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
                    <SkeletonBlock w={32} h={32} radius={8} />
                    <SkeletonBlock w={90} h={14} radius={6} />
                </div>
                {/* Section label + items */}
                {[4, 5, 3, 4, 3].map((count, si) => (
                    <div key={si} style={{ marginBottom: 16 }}>
                        <SkeletonBlock w={60} h={8} radius={4} mb={10} />
                        {Array.from({ length: count }).map((_, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", marginBottom: 2 }}>
                                <SkeletonBlock w={18} h={18} radius={4} />
                                <SkeletonBlock w={`${55 + Math.random() * 30}%`} h={12} radius={4} />
                            </div>
                        ))}
                    </div>
                ))}
                {/* Bottom user card */}
                <div style={{ marginTop: "auto", padding: "12px", background: C.card2, borderRadius: 10 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                        <SkeletonBlock w={36} h={36} radius={"50%"} />
                        <div style={{ flex: 1 }}>
                            <SkeletonBlock w="70%" h={12} radius={4} mb={6} />
                            <SkeletonBlock w="50%" h={10} radius={4} />
                        </div>
                    </div>
                </div>
            </div>
            {/* Main content skeleton */}
            <div style={{ flex: 1, padding: 32 }}>
                {/* Top bar */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 32 }}>
                    <SkeletonBlock w={40} h={40} radius={10} />
                    <SkeletonBlock w={40} h={40} radius={10} />
                </div>
                {/* Page title */}
                <SkeletonBlock w={220} h={28} radius={8} mb={8} />
                <SkeletonBlock w={160} h={14} radius={6} mb={28} />
                {/* Stat cards row */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} style={{ background: C.card, borderRadius: 14, padding: 20 }}>
                            <SkeletonBlock w={36} h={36} radius={10} mb={12} />
                            <SkeletonBlock w="60%" h={12} radius={4} mb={8} />
                            <SkeletonBlock w="40%" h={20} radius={6} />
                        </div>
                    ))}
                </div>
                {/* Card block */}
                <div style={{ background: C.card, borderRadius: 14, padding: 24, marginBottom: 16 }}>
                    <SkeletonBlock w={180} h={16} radius={6} mb={16} />
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", paddingBottom: 14, marginBottom: 14, borderBottom: `1px solid ${C.border}` }}>
                            <SkeletonBlock w={38} h={38} radius={"50%"} />
                            <div style={{ flex: 1 }}>
                                <SkeletonBlock w="50%" h={13} radius={4} mb={7} />
                                <SkeletonBlock w="35%" h={10} radius={4} />
                            </div>
                            <SkeletonBlock w={70} h={24} radius={8} />
                        </div>
                    ))}
                </div>
                {/* Second card */}
                <div style={{ background: C.card, borderRadius: 14, padding: 24 }}>
                    <SkeletonBlock w={140} h={16} radius={6} mb={16} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <SkeletonBlock key={i} h={80} radius={10} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── User Access Management ───────────────────────────────────────────────────
function UserAccessPage() {
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [grantMap, setGrantMap] = useState({}); // { staffId: Set(menu_ids) }
    const [saving, setSaving] = useState(null);
    const [saveMsg, setSaveMsg] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const [staff, access] = await Promise.all([
                sb("staff?select=id,name,designation,employment_type,profile_image&order=name"),
                sb("user_menu_access?select=staff_id,menu_id"),
            ]);
            const nonAdmin = (Array.isArray(staff) ? staff : []).filter(s => s.id !== "admin");
            setStaffList(nonAdmin);
            // Build grant map
            const map = {};
            nonAdmin.forEach(s => { map[s.id] = new Set(); });
            (Array.isArray(access) ? access : []).forEach(r => {
                if (map[r.staff_id]) map[r.staff_id].add(r.menu_id);
            });
            setGrantMap(map);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const toggleMenu = (staffId, menuId) => {
        setGrantMap(prev => {
            const next = { ...prev, [staffId]: new Set(prev[staffId]) };
            if (next[staffId].has(menuId)) next[staffId].delete(menuId);
            else next[staffId].add(menuId);
            return next;
        });
    };

    const handleSave = async (staffId) => {
        setSaving(staffId); setSaveMsg(p => ({ ...p, [staffId]: "" }));
        try {
            const desired = [...(grantMap[staffId] || new Set())];
            // Delete all existing then re-insert
            await sb(`user_menu_access?staff_id=eq.${staffId}`, { method: "DELETE", prefer: "return=minimal" });
            if (desired.length > 0) {
                await sb("user_menu_access", {
                    method: "POST",
                    body: JSON.stringify(desired.map(menu_id => ({ staff_id: staffId, menu_id }))),
                    headers: { "Prefer": "resolution=ignore-duplicates,return=minimal" },
                });
            }
            setSaveMsg(p => ({ ...p, [staffId]: "Saved!" }));
            setTimeout(() => setSaveMsg(p => ({ ...p, [staffId]: "" })), 2000);
        } catch (e) { setSaveMsg(p => ({ ...p, [staffId]: "Error: " + e.message })); }
        finally { setSaving(null); }
    };

    const etColor = (et) => et === "freelancer" ? C.purple : et === "part_time" ? C.yellow : C.green;
    const etLabel = (et) => et === "freelancer" ? "Freelancer" : et === "part_time" ? "Part Time" : "Full Time";

    return (
        <div style={{ maxWidth: 720 }}>
            <h2 style={{ margin: "0 0 4px", fontWeight: 900, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><Shield size={22} /> User Access Management</h2>
            <p style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>Grant extra menu access to specific staff members on top of their existing role.</p>

            {loading ? (
                <div style={{ color: C.muted, fontSize: 13, padding: 20 }}>Loading...</div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {staffList.map(s => {
                        const grants = grantMap[s.id] || new Set();
                        const isOpen = expandedId === s.id;
                        const grantCount = grants.size;
                        return (
                            <div key={s.id} style={{ background: C.card, borderRadius: 12, border: `1px solid ${isOpen ? C.accent + "55" : C.border}`, overflow: "hidden", transition: "border 0.2s" }}>
                                {/* Header row */}
                                <div onClick={() => setExpandedId(isOpen ? null : s.id)}
                                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", cursor: "pointer" }}>
                                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.border, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {s.profile_image ? <img src={s.profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={16} color={C.muted} />}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 14, color: C.white }}>{s.name}</div>
                                        <div style={{ fontSize: 11, color: C.muted }}>{s.designation || "—"}</div>
                                    </div>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: etColor(s.employment_type) + "22", color: etColor(s.employment_type) }}>{etLabel(s.employment_type)}</span>
                                    {grantCount > 0 && (
                                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: C.accent + "22", color: C.accent }}>{grantCount} access{grantCount !== 1 ? "es" : ""}</span>
                                    )}
                                    <ChevronRight size={16} color={C.muted} style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }} />
                                </div>

                                {/* Expanded permissions */}
                                {isOpen && (
                                    <div style={{ borderTop: `1px solid ${C.border}`, padding: "16px 18px", background: C.card2 }}>
                                        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 14 }}>Extra Menu Access</div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginBottom: 16 }}>
                                            {GRANTABLE_MENUS.map(m => {
                                                const checked = grants.has(m.id);
                                                return (
                                                    <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${checked ? C.accent : C.border}`, background: checked ? C.accent + "11" : "transparent", cursor: "pointer", transition: "all 0.15s" }}>
                                                        <input type="checkbox" checked={checked} onChange={() => toggleMenu(s.id, m.id)} style={{ display: "none" }} />
                                                        <div style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${checked ? C.accent : C.border}`, background: checked ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                                                            {checked && <CheckCircle size={12} color="#fff" />}
                                                        </div>
                                                        <m.icon size={14} color={checked ? C.accent : C.muted} />
                                                        <span style={{ fontSize: 13, color: checked ? C.white : C.muted, fontWeight: checked ? 600 : 400 }}>{m.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <button onClick={() => handleSave(s.id)} disabled={saving === s.id}
                                                style={{ ...styles.btn(C.accent), padding: "9px 24px", fontWeight: 700, display: "flex", alignItems: "center", gap: 7 }}>
                                                <CheckCircle size={14} /> {saving === s.id ? "Saving..." : "Save Access"}
                                            </button>
                                            {saveMsg[s.id] && <span style={{ fontSize: 12, color: saveMsg[s.id].startsWith("Error") ? C.red : C.green, fontWeight: 600 }}>{saveMsg[s.id]}</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Manage Tenants ───────────────────────────────────────────────────────────
function ManageTenantsPage({ user = {}, isAdmin = false }) {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editAddress, setEditAddress] = useState("");
    const [editContact, setEditContact] = useState("");
    const [saving, setSaving] = useState(false);
    const [addMode, setAddMode] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const t = await sb("tenants?order=name"); setTenants(Array.isArray(t) ? t : []); }
        catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const startEdit = (t) => { setEditId(t.id); setEditName(t.name); setEditAddress(t.address || ""); setEditContact(t.contact || ""); setAddMode(false); };
    const cancelEdit = () => { setEditId(null); setAddMode(false); };

    const handleSave = async () => {
        if (!editName.trim()) return;
        setSaving(true);
        try {
            await sb(`tenants?id=eq.${editId}`, { method: "PATCH", body: JSON.stringify({ name: editName.trim(), address: editAddress.trim(), contact: editContact.trim() }), prefer: "return=minimal" });
            setEditId(null);
            await load();
        } catch (e) { console.error(e); }
        finally { setSaving(false); }
    };

    const handleAdd = async () => {
        if (!editName.trim()) return;
        setSaving(true);
        try {
            await sb("tenants", { method: "POST", body: JSON.stringify({ name: editName.trim(), address: editAddress.trim(), contact: editContact.trim(), created_by: user.id }) });
            setAddMode(false); setEditName(""); setEditAddress(""); setEditContact("");
            await load();
        } catch (e) { console.error(e); }
        finally { setSaving(false); }
    };

    const handleDelete = async (t) => {
        if (!window.confirm(`Delete tenant "${t.name}"? This will not delete their receipts.`)) return;
        await sb(`tenants?id=eq.${t.id}`, { method: "DELETE", prefer: "return=minimal" });
        await load();
    };

    const iStyle = { width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card2, color: C.white, fontSize: 13, outline: "none", boxSizing: "border-box" };

    return (
        <div style={{ maxWidth: 680 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontWeight: 900, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><Users size={22} /> Manage Tenants</h2>
                {!addMode && <button onClick={() => { setAddMode(true); setEditId(null); setEditName(""); setEditAddress(""); setEditContact(""); }} style={{ ...styles.btn(C.accent), display: "flex", alignItems: "center", gap: 7, padding: "9px 18px" }}><PenLine size={14} /> Add Tenant</button>}
            </div>

            {/* Add form */}
            {addMode && (
                <div style={{ background: C.card, borderRadius: 12, padding: 18, border: `1px solid ${C.accent}44`, marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.accent, letterSpacing: 1, marginBottom: 14, textTransform: "uppercase" }}>New Tenant</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
                        <div><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>Name *</div><input style={iStyle} placeholder="Full name" value={editName} onChange={e => setEditName(e.target.value)} /></div>
                        <div><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>Address</div><input style={iStyle} placeholder="e.g. Kathmandu-10" value={editAddress} onChange={e => setEditAddress(e.target.value)} /></div>
                        <div><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>Contact</div><input style={iStyle} placeholder="98XXXXXXXX" value={editContact} onChange={e => setEditContact(e.target.value)} /></div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button onClick={handleAdd} disabled={saving || !editName.trim()} style={{ ...styles.btn(C.accent), padding: "8px 20px", opacity: (!editName.trim() || saving) ? 0.5 : 1 }}>{saving ? "Saving..." : "Save"}</button>
                        <button onClick={cancelEdit} style={{ ...styles.btn(C.card2), padding: "8px 16px", color: C.muted }}>Cancel</button>
                    </div>
                </div>
            )}

            {loading ? (
                <div style={{ color: C.muted, fontSize: 13, padding: 20 }}>Loading...</div>
            ) : tenants.length === 0 ? (
                <div style={{ textAlign: "center", padding: 48, color: C.muted }}>
                    <Users size={36} style={{ opacity: 0.3, marginBottom: 10 }} />
                    <div>No tenants yet.</div>
                </div>
            ) : (
                <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                    {tenants.map((t, i) => (
                        <div key={t.id}>
                            {editId === t.id ? (
                                /* Inline edit row */
                                <div style={{ padding: "14px 18px", background: C.card2 }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
                                        <div><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>Name *</div><input style={iStyle} value={editName} onChange={e => setEditName(e.target.value)} /></div>
                                        <div><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>Address</div><input style={iStyle} value={editAddress} onChange={e => setEditAddress(e.target.value)} /></div>
                                        <div><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>Contact</div><input style={iStyle} value={editContact} onChange={e => setEditContact(e.target.value)} /></div>
                                    </div>
                                    <div style={{ display: "flex", gap: 10 }}>
                                        <button onClick={handleSave} disabled={saving || !editName.trim()} style={{ ...styles.btn(C.accent), padding: "7px 18px", opacity: (!editName.trim() || saving) ? 0.5 : 1 }}>{saving ? "Saving..." : "Save"}</button>
                                        <button onClick={cancelEdit} style={{ ...styles.btn(C.card2), padding: "7px 14px", color: C.muted }}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                /* Display row */
                                <div style={{ display: "flex", alignItems: "center", padding: "13px 18px", gap: 14 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.accent + "22", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <User size={16} color={C.accent} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 14, color: C.white }}>{t.name}</div>
                                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                                            {[t.address, t.contact].filter(Boolean).join(" · ") || "No details"}
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                        {(isAdmin || t.created_by === user.id) && <>
                                            <button onClick={() => startEdit(t)} style={{ ...styles.btn(C.card2), padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 5, color: C.muted }}><PenLine size={13} /> Edit</button>
                                            <button onClick={() => handleDelete(t)} style={{ ...styles.btn(C.red + "22"), padding: "6px 10px", fontSize: 12, display: "flex", alignItems: "center", color: C.red, border: `1px solid ${C.red}33` }}><Trash2 size={13} /></button>
                                        </>}
                                    </div>
                                </div>
                            )}
                            {i < tenants.length - 1 && <div style={{ borderBottom: `1px solid ${C.border}` }} />}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Rent Receipt helpers ─────────────────────────────────────────────────────
const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString("en-IN")}`;

const downloadReceiptPDF = async (receiptNumber) => {
    const el = document.getElementById("rent-receipt-preview");
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff", logging: false });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = (canvas.height * pdfW) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfW, pdfH);
    pdf.save(`${receiptNumber || "receipt"}.pdf`);
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function ReceiptPreview({ r }) {
    const unitsUsed = Math.max(0, (parseFloat(r.currUnit) || 0) - (parseFloat(r.prevUnit) || 0));
    const electricityCost = unitsUsed * (parseFloat(r.rateUnit) || 17);
    const cleanAmt = parseFloat(r.cleanFee) || 0;
    const total = (parseFloat(r.rentAmt) || 0) + electricityCost + cleanAmt;
    const rentPeriodLabel = r.rentFrom && r.rentTo
        ? `${r.rentFrom} to ${r.rentTo}${r.monthName ? ` (${r.monthName})` : ""}`
        : r.monthName || "";
    return (
        <div id="rent-receipt-preview" style={{ background: "#fff", color: "#111", borderRadius: 14, padding: 36, fontFamily: "'DM Sans', sans-serif", fontSize: 13, lineHeight: 1.6 }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, borderBottom: "2px solid #111", paddingBottom: 14 }}>
                <div>
                    <img src="/foxbeep-logo.png" alt="Foxbeep" style={{ height: 48, objectFit: "contain", marginBottom: 6 }} />
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#111", letterSpacing: 0.3 }}>Foxbeep Technology Pvt. Ltd.</div>
                    <div style={{ fontSize: 11, color: "#555" }}>Madhyapur Thimi, Bhaktapur</div>
                    <div style={{ fontSize: 11, color: "#555" }}>+977 9705516131 &nbsp;|&nbsp; support@foxbeep.com &nbsp;|&nbsp; foxbeep.com</div>
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: "#111", lineHeight: 1.1 }}>RENT RECEIPT</div>
                    <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>FOR TENANTS</div>
                    {r.receiptNumber && <div style={{ fontSize: 12, color: "#333", marginTop: 6, fontWeight: 700 }}>Receipt No: <span style={{ color: "#111" }}>{r.receiptNumber}</span></div>}
                    {r.issueDate && <div style={{ fontSize: 12, color: "#333", marginTop: 4, fontWeight: 700, background: "#f0f0f0", borderRadius: 6, padding: "3px 10px", display: "inline-block" }}>Date: {r.issueDate} BS</div>}
                </div>
            </div>
            {/* Tenant Info */}
            <div style={{ background: "#f8f8f8", borderRadius: 8, padding: "12px 16px", marginBottom: 18 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, color: "#777", textTransform: "uppercase", marginBottom: 8 }}>Tenant Information</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <tbody>
                        <tr><td style={{ color: "#555", paddingRight: 16, paddingBottom: 4, whiteSpace: "nowrap", width: 80 }}>Name</td><td style={{ fontWeight: 700, color: "#111" }}>{r.tenantName || "—"}</td></tr>
                        <tr><td style={{ color: "#555", paddingRight: 16, paddingBottom: 4, whiteSpace: "nowrap" }}>Address</td><td style={{ color: "#333" }}>{r.address || "—"}</td></tr>
                        <tr><td style={{ color: "#555", paddingRight: 16, whiteSpace: "nowrap" }}>Contact</td><td style={{ color: "#333" }}>{r.contact || "—"}</td></tr>
                    </tbody>
                </table>
            </div>
            {/* Payment Summary */}
            <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, color: "#777", textTransform: "uppercase", marginBottom: 8 }}>Payment Summary</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr style={{ borderBottom: "2px solid #ccc", background: "#f0f0f0" }}>
                            <th style={{ textAlign: "left", padding: "6px 10px", color: "#444", fontWeight: 700, fontSize: 11 }}>Description</th>
                            <th style={{ textAlign: "right", padding: "6px 10px", color: "#444", fontWeight: 700, fontSize: 11 }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ borderBottom: "1px solid #eee" }}>
                            <td style={{ padding: "9px 10px" }}>
                                <div style={{ fontWeight: 600, color: "#111" }}>Rent</div>
                                {rentPeriodLabel && <div style={{ fontSize: 11, color: "#777" }}>{rentPeriodLabel}</div>}
                            </td>
                            <td style={{ textAlign: "right", padding: "9px 10px", fontWeight: 600, color: "#111", whiteSpace: "nowrap" }}>{fmtNPR(r.rentAmt)}</td>
                        </tr>
                        <tr style={{ borderBottom: "1px solid #eee" }}>
                            <td style={{ padding: "9px 10px" }}>
                                <div style={{ fontWeight: 600, color: "#111" }}>Utility Fee (Electricity)</div>
                                {(r.prevUnit || r.currUnit) && <div style={{ fontSize: 11, color: "#777" }}>Prev: {r.prevUnit || 0} unit · Curr: {r.currUnit || 0} unit · Used: {unitsUsed} units × Rs.{r.rateUnit || 17}/unit</div>}
                            </td>
                            <td style={{ textAlign: "right", padding: "9px 10px", fontWeight: 600, color: "#111", whiteSpace: "nowrap" }}>{fmtNPR(electricityCost)}</td>
                        </tr>
                        {cleanAmt > 0 && (
                            <tr style={{ borderBottom: "1px solid #eee" }}>
                                <td style={{ padding: "9px 10px", fontWeight: 600, color: "#111" }}>Cleanliness Fee</td>
                                <td style={{ textAlign: "right", padding: "9px 10px", fontWeight: 600, color: "#111", whiteSpace: "nowrap" }}>{fmtNPR(cleanAmt)}</td>
                            </tr>
                        )}
                        <tr style={{ background: "#111" }}>
                            <td style={{ padding: "11px 10px", fontWeight: 900, fontSize: 14, color: "#fff" }}>Total Amount to be Paid</td>
                            <td style={{ textAlign: "right", padding: "11px 10px", fontWeight: 900, fontSize: 15, color: "#fff", whiteSpace: "nowrap" }}>{fmtNPR(total)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {/* Payment Details */}
            <div style={{ background: "#f8f8f8", borderRadius: 8, padding: "10px 16px", marginBottom: 28 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, color: "#777", textTransform: "uppercase", marginBottom: 6 }}>Payment Details</div>
                <div style={{ fontSize: 13, color: "#333" }}>
                    <span style={{ color: "#555" }}>Method: </span><strong style={{ color: "#111" }}>{r.payMethod || "—"}</strong>
                    {r.bankName && <><span style={{ color: "#555", marginLeft: 16 }}>Bank: </span><strong style={{ color: "#111" }}>{r.bankName}</strong></>}
                </div>
            </div>
            {/* Signatures */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, marginTop: 36 }}>
                <div style={{ textAlign: "center" }}><div style={{ borderTop: "1.5px solid #bbb", paddingTop: 8, fontSize: 12, color: "#666" }}>Tenant's Signature</div></div>
                <div style={{ textAlign: "center" }}><div style={{ borderTop: "1.5px solid #bbb", paddingTop: 8, fontSize: 12, color: "#666" }}>Authorized Signature</div></div>
            </div>
            <div style={{ marginTop: 24, textAlign: "center", fontSize: 10, color: "#bbb", borderTop: "1px solid #eee", paddingTop: 10 }}>
                Computer-generated receipt &nbsp;·&nbsp; Foxbeep Technology Pvt. Ltd. &nbsp;·&nbsp; foxbeep.com
            </div>
        </div>
    );
}

function MarkAsPaidModal({ receipt, onClose, onPaid }) {
    const [txn, setTxn] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const handleSave = async () => {
        setSaving(true); setErr("");
        try {
            await sb(`rent_receipts?id=eq.${receipt.id}`, {
                method: "PATCH",
                body: JSON.stringify({ status: "paid", txn_number: txn.trim() || null, paid_at: new Date().toISOString() }),
                prefer: "return=minimal",
            });
            onPaid();
        } catch (e) { setErr(e.message); }
        finally { setSaving(false); }
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ background: C.card, borderRadius: 16, padding: 28, width: "100%", maxWidth: 420, border: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.white, display: "flex", alignItems: "center", gap: 8 }}><CheckCircle size={18} color={C.green} /> Mark as Paid</div>
                    <X size={20} style={{ cursor: "pointer", color: C.muted }} onClick={onClose} />
                </div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>
                    Mark payment received for <strong style={{ color: C.white }}>{receipt.tenant_name || receipt.receipt_name}</strong>.
                </div>
                <input
                    style={{ ...styles.input, marginBottom: 16 }}
                    placeholder="Transaction number (optional)"
                    value={txn}
                    onChange={e => setTxn(e.target.value)}
                />
                {err && <div style={{ color: C.red, fontSize: 12, marginBottom: 12 }}>{err}</div>}
                <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={onClose} style={{ ...styles.btn(C.border), flex: 1, color: C.muted }}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={{ ...styles.btn(C.green), flex: 1, opacity: saving ? 0.6 : 1 }}>
                        {saving ? "Saving..." : "Confirm Paid"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function RentReceiptPage({ user = {}, isAdmin = false }) {
    const mobile = useMobile();
    const [view, setView] = useState("list"); // "list" | "form"
    const [editId, setEditId] = useState(null); // null = new receipt

    // ── Tenants ──
    const [tenants, setTenants] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState("");
    const [savingTenant, setSavingTenant] = useState(false);

    // ── Receipts list ──
    const [receipts, setReceipts] = useState([]);
    const [listFilter, setListFilter] = useState("all"); // "all"|"draft"|"paid"
    const [loadingList, setLoadingList] = useState(true);

    // ── Form state ──
    const [receiptNumber, setReceiptNumber] = useState("");
    const [receiptName, setReceiptName] = useState("");
    const [issueDate, setIssueDate] = useState("");
    const [tenantName, setTenantName] = useState("");
    const [address, setAddress] = useState("");
    const [contact, setContact] = useState("");
    const [rentFrom, setRentFrom] = useState("");
    const [rentTo, setRentTo] = useState("");
    const [monthName, setMonthName] = useState("");
    const [rentAmt, setRentAmt] = useState("");
    const [prevUnit, setPrevUnit] = useState("");
    const [currUnit, setCurrUnit] = useState("");
    const [rateUnit, setRateUnit] = useState("17");
    const [cleanFee, setCleanFee] = useState("200");
    const [payMethod, setPayMethod] = useState("Bank Transfer");
    const [bankName, setBankName] = useState("Rastriya Banijya Bank");

    // ── UI state ──
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState("");
    const [markPaidReceipt, setMarkPaidReceipt] = useState(null);
    const [viewReceiptModal, setViewReceiptModal] = useState(null);

    const loadAll = async () => {
        setLoadingList(true);
        try {
            const [t, r] = await Promise.all([
                sb("tenants?order=name"),
                sb("rent_receipts?order=created_at.desc"),
            ]);
            setTenants(Array.isArray(t) ? t : []);
            setReceipts(Array.isArray(r) ? r : []);
        } catch (e) { console.error(e); }
        finally { setLoadingList(false); }
    };

    useEffect(() => { loadAll(); }, []);

    const resetForm = () => {
        setEditId(null); setReceiptNumber(""); setReceiptName(""); setIssueDate(""); setSelectedTenantId("");
        setTenantName(""); setAddress(""); setContact("");
        setRentFrom(""); setRentTo(""); setMonthName(""); setRentAmt("");
        setPrevUnit(""); setCurrUnit(""); setRateUnit("17"); setCleanFee("200");
        setPayMethod("Bank Transfer"); setBankName(""); setSaveMsg("");
    };

    const openNewForm = () => { resetForm(); setView("form"); };

    const openEdit = (rec) => {
        setEditId(rec.id);
        setReceiptNumber(rec.receipt_number || "");
        setReceiptName(rec.receipt_name || "");
        setIssueDate(rec.issue_date || "");
        setSelectedTenantId(rec.tenant_id || "");
        setTenantName(rec.tenant_name || "");
        setAddress(rec.address || "");
        setContact(rec.contact || "");
        setRentFrom(rec.rent_from || "");
        setRentTo(rec.rent_to || "");
        setMonthName(rec.month_name || "");
        setRentAmt(rec.rent_amount ? String(rec.rent_amount) : "");
        setPrevUnit(rec.prev_unit ? String(rec.prev_unit) : "");
        setCurrUnit(rec.curr_unit ? String(rec.curr_unit) : "");
        setRateUnit(rec.rate_per_unit ? String(rec.rate_per_unit) : "17");
        setCleanFee(rec.cleanliness_fee !== undefined ? String(rec.cleanliness_fee) : "200");
        setPayMethod(rec.payment_method || "Bank Transfer");
        setBankName(rec.bank_name || "");
        setSaveMsg("");
        setView("form");
    };

    const handleTenantSelect = async (tid) => {
        setSelectedTenantId(tid);
        if (!tid) return;
        const t = tenants.find(t => t.id === tid);
        if (t) { setTenantName(t.name); setAddress(t.address || ""); setContact(t.contact || ""); }
        // Auto-fill prev unit from last paid receipt for this tenant (only when creating new)
        if (!editId) {
            try {
                const prev = await sb(`rent_receipts?tenant_id=eq.${tid}&status=eq.paid&order=paid_at.desc&limit=1&select=curr_unit`);
                if (Array.isArray(prev) && prev.length > 0 && prev[0].curr_unit != null) {
                    setPrevUnit(String(prev[0].curr_unit));
                    setCurrUnit("");
                }
            } catch (e) { console.error(e); }
        }
    };

    const handleSaveTenant = async () => {
        if (!tenantName.trim()) return;
        setSavingTenant(true);
        try {
            const res = await sb("tenants", { method: "POST", body: JSON.stringify({ name: tenantName.trim(), address: address.trim(), contact: contact.trim(), created_by: user.id }) });
            const newT = Array.isArray(res) ? res[0] : res;
            setTenants(prev => [...prev, newT].sort((a, b) => a.name.localeCompare(b.name)));
            setSelectedTenantId(newT.id);
        } catch (e) { console.error(e); }
        finally { setSavingTenant(false); }
    };

    const handleSaveDraft = async () => {
        setSaving(true); setSaveMsg("");
        try {
            let rNum = receiptNumber;
            if (!editId && !rNum) {
                // Auto-generate: count existing receipts, pad to 4 digits
                const all = await sb("rent_receipts?select=id");
                rNum = "RR-" + String((Array.isArray(all) ? all.length : 0) + 1).padStart(4, "0");
                setReceiptNumber(rNum);
            }
            const payload = {
                receipt_number: rNum, receipt_name: receiptName.trim(),
                issue_date: issueDate, tenant_id: selectedTenantId || null,
                tenant_name: tenantName, address, contact,
                rent_from: rentFrom, rent_to: rentTo, month_name: monthName,
                rent_amount: parseFloat(rentAmt) || 0, prev_unit: parseFloat(prevUnit) || 0,
                curr_unit: parseFloat(currUnit) || 0, rate_per_unit: parseFloat(rateUnit) || 17,
                cleanliness_fee: parseFloat(cleanFee) || 0, payment_method: payMethod, bank_name: bankName,
                updated_at: new Date().toISOString(),
            };
            if (editId) {
                await sb(`rent_receipts?id=eq.${editId}`, { method: "PATCH", body: JSON.stringify(payload), prefer: "return=minimal" });
                setSaveMsg("Receipt updated.");
            } else {
                const res = await sb("rent_receipts", { method: "POST", body: JSON.stringify({ ...payload, status: "draft", created_by: user.id }) });
                const newR = Array.isArray(res) ? res[0] : res;
                setEditId(newR.id);
                setSaveMsg("Saved as draft.");
            }
            await loadAll();
        } catch (e) { setSaveMsg("Error: " + e.message); }
        finally { setSaving(false); }
    };

    const unitsUsed = Math.max(0, (parseFloat(currUnit) || 0) - (parseFloat(prevUnit) || 0));
    const electricityCost = unitsUsed * (parseFloat(rateUnit) || 17);
    const cleanAmt = parseFloat(cleanFee) || 0;
    const total = (parseFloat(rentAmt) || 0) + electricityCost + cleanAmt;

    const previewData = { receiptNumber, issueDate, tenantName, address, contact, rentFrom, rentTo, monthName, rentAmt, prevUnit, currUnit, rateUnit, cleanFee, payMethod, bankName };

    const iStyle = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card2, color: C.white, fontSize: 13, outline: "none", boxSizing: "border-box" };
    const lStyle = { fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 4, display: "block" };
    const fWrap = { marginBottom: 12 };
    const secTitle = { fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, marginTop: 20, paddingBottom: 4, borderBottom: `1px solid ${C.border}` };

    const filteredReceipts = receipts.filter(r => listFilter === "all" || r.status === listFilter);

    // ── LIST VIEW ──
    if (view === "list") return (
        <div>
            <style>{`@media print { body * { visibility: hidden !important; } #rent-receipt-preview, #rent-receipt-preview * { visibility: visible !important; } #rent-receipt-preview { position: fixed !important; left: 0; top: 0; width: 100%; padding: 32px !important; box-shadow: none !important; border: none !important; background: #fff !important; } @page { size: A4; margin: 16mm; } }`}</style>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                <div>
                    <h2 style={{ margin: "0 0 4px", fontWeight: 900, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><Receipt size={22} /> Office Rent Receipts</h2>
                    <div style={{ fontSize: 13, color: C.muted }}>{receipts.length} receipt{receipts.length !== 1 ? "s" : ""} total</div>
                </div>
                <button onClick={openNewForm} style={{ ...styles.btn(C.accent), display: "flex", alignItems: "center", gap: 8, padding: "10px 20px" }}>
                    <PenLine size={15} /> New Receipt
                </button>
            </div>

            {/* Filter tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {["all", "draft", "paid"].map(f => (
                    <button key={f} onClick={() => setListFilter(f)}
                        style={{ padding: "6px 16px", borderRadius: 20, border: `1px solid ${listFilter === f ? C.accent : C.border}`, background: listFilter === f ? C.accent + "22" : "transparent", color: listFilter === f ? C.white : C.muted, fontWeight: listFilter === f ? 700 : 500, fontSize: 13, cursor: "pointer", textTransform: "capitalize" }}>
                        {f}
                    </button>
                ))}
            </div>

            {loadingList ? (
                <div style={{ color: C.muted, fontSize: 13, padding: 20 }}>Loading...</div>
            ) : filteredReceipts.length === 0 ? (
                <div style={{ textAlign: "center", padding: 48, color: C.muted }}>
                    <Receipt size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                    <div style={{ fontSize: 14 }}>No receipts yet. Create your first one.</div>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {filteredReceipts.map(rec => (
                        <div key={rec.id} style={{ background: C.card, borderRadius: 12, padding: "14px 18px", border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                            <div style={{ flex: 1, minWidth: 180 }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: C.white, display: "flex", alignItems: "center", gap: 8 }}>
                                    {rec.receipt_name || rec.tenant_name || "Untitled Receipt"}
                                    {rec.receipt_number && <span style={{ fontSize: 11, fontWeight: 700, color: C.accent, background: C.accent + "18", borderRadius: 6, padding: "1px 8px" }}>{rec.receipt_number}</span>}
                                </div>
                                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                                    {rec.tenant_name}{rec.issue_date ? ` · ${rec.issue_date} BS` : ""}
                                    {rec.month_name ? ` · ${rec.month_name}` : ""}
                                </div>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.white, minWidth: 100, textAlign: "right" }}>
                                {fmtNPR((parseFloat(rec.rent_amount) || 0) + Math.max(0, (rec.curr_unit - rec.prev_unit)) * (rec.rate_per_unit || 17) + (rec.cleanliness_fee || 0))}
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <div style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: rec.status === "paid" ? C.green + "22" : C.yellow + "22", color: rec.status === "paid" ? C.green : C.yellow, display: "inline-block" }}>
                                    {rec.status === "paid" ? "Paid" : "Draft"}
                                </div>
                                {rec.status === "paid" && rec.txn_number && (
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>TXN: {rec.txn_number}</div>
                                )}
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button onClick={() => setViewReceiptModal(rec)} style={{ ...styles.btn(C.card2), padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 5, color: C.muted }}><Eye size={13} /> View</button>
                                {(isAdmin || rec.created_by === user.id) && <>
                                    <button onClick={() => openEdit(rec)} style={{ ...styles.btn(C.card2), padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 5, color: C.muted }}><PenLine size={13} /> Edit</button>
                                    {rec.status !== "paid" && (
                                        <button onClick={() => setMarkPaidReceipt(rec)} style={{ ...styles.btn(C.green), padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}><CheckCircle size={13} /> Mark Paid</button>
                                    )}
                                    <button onClick={async () => { if (!window.confirm(`Delete receipt "${rec.receipt_number || rec.receipt_name || rec.tenant_name}"?`)) return; await sb(`rent_receipts?id=eq.${rec.id}`, { method: "DELETE", prefer: "return=minimal" }); await loadAll(); }} style={{ ...styles.btn(C.red + "22"), padding: "6px 10px", fontSize: 12, display: "flex", alignItems: "center", color: C.red, border: `1px solid ${C.red}33` }}><Trash2 size={13} /></button>
                                </>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* View Receipt Modal */}
            {viewReceiptModal && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px 20px", overflowY: "auto" }}>
                    <div style={{ width: "100%", maxWidth: 680, position: "relative" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <div style={{ display: "flex", gap: 10 }}>
                                <button onClick={() => window.print()} style={{ ...styles.btn(C.card2), display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", fontSize: 13, color: C.muted }}><Printer size={14} /> Print</button>
                                <button onClick={() => downloadReceiptPDF(viewReceiptModal.receipt_number)} style={{ ...styles.btn(C.accent), display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", fontSize: 13 }}><Download size={14} /> Download ID Card</button>
                                {viewReceiptModal.status === "paid" && viewReceiptModal.txn_number && (
                                    <div style={{ ...styles.btn(C.green + "22"), display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", fontSize: 13, color: C.green, border: `1px solid ${C.green}33` }}>TXN: {viewReceiptModal.txn_number}</div>
                                )}
                            </div>
                            <button onClick={() => setViewReceiptModal(null)} style={{ ...styles.btn(C.card2), padding: "8px 14px", color: C.muted, display: "flex", alignItems: "center", gap: 5 }}><X size={14} /> Close</button>
                        </div>
                        <ReceiptPreview r={{
                            receiptNumber: viewReceiptModal.receipt_number, issueDate: viewReceiptModal.issue_date, tenantName: viewReceiptModal.tenant_name,
                            address: viewReceiptModal.address, contact: viewReceiptModal.contact,
                            rentFrom: viewReceiptModal.rent_from, rentTo: viewReceiptModal.rent_to,
                            monthName: viewReceiptModal.month_name, rentAmt: viewReceiptModal.rent_amount,
                            prevUnit: viewReceiptModal.prev_unit, currUnit: viewReceiptModal.curr_unit,
                            rateUnit: viewReceiptModal.rate_per_unit, cleanFee: viewReceiptModal.cleanliness_fee,
                            payMethod: viewReceiptModal.payment_method, bankName: viewReceiptModal.bank_name,
                        }} />
                    </div>
                </div>
            )}

            {markPaidReceipt && <MarkAsPaidModal receipt={markPaidReceipt} onClose={() => setMarkPaidReceipt(null)} onPaid={async () => { setMarkPaidReceipt(null); await loadAll(); }} />}
        </div>
    );

    // ── FORM VIEW ──
    return (
        <div>
            <style>{`@media print { body * { visibility: hidden !important; } #rent-receipt-preview, #rent-receipt-preview * { visibility: visible !important; } #rent-receipt-preview { position: fixed !important; left: 0; top: 0; width: 100%; padding: 32px !important; box-shadow: none !important; border: none !important; background: #fff !important; } @page { size: A4; margin: 16mm; } }`}</style>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <button onClick={() => { setView("list"); resetForm(); }} style={{ ...styles.btn(C.card2), padding: "7px 14px", fontSize: 13, display: "flex", alignItems: "center", gap: 6, color: C.muted }}><ChevronRight size={14} style={{ transform: "rotate(180deg)" }} /> Back</button>
                <h2 style={{ margin: 0, fontWeight: 900, fontSize: 20 }}>{editId ? "Edit Receipt" : "New Receipt"}</h2>
                {editId && <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>ID: {editId.slice(0, 8)}…</span>}
            </div>

            <div style={{ display: "flex", gap: 24, flexDirection: mobile ? "column" : "row", alignItems: "flex-start" }}>

                {/* ── FORM ── */}
                <div style={{ flex: "0 0 360px", background: C.card, borderRadius: 14, padding: 20, border: `1px solid ${C.border}` }}>

                    <div style={secTitle}>Admin Reference</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div style={fWrap}>
                            <label style={lStyle}>Receipt No. {!editId && <span style={{ color: C.muted, fontWeight: 500 }}>(auto)</span>}</label>
                            <input style={{ ...iStyle, background: editId ? C.card2 : C.card, color: editId ? C.muted : C.muted, cursor: editId ? "default" : "default" }} readOnly value={receiptNumber || (editId ? "" : "Auto on save")} />
                        </div>
                        <div style={fWrap}>
                            <label style={lStyle}>Issue Date (BS)</label>
                            <input style={iStyle} placeholder="e.g. 2082/03/15" value={issueDate} onChange={e => setIssueDate(e.target.value)} />
                        </div>
                    </div>
                    <div style={fWrap}>
                        <label style={lStyle}>Receipt Name (internal only)</label>
                        <input style={iStyle} placeholder="e.g. Office 2B - Magh 2082" value={receiptName} onChange={e => setReceiptName(e.target.value)} />
                    </div>

                    <div style={secTitle}>Tenant</div>
                    <div style={fWrap}>
                        <label style={lStyle}>Select Saved Tenant</label>
                        <select style={iStyle} value={selectedTenantId} onChange={e => handleTenantSelect(e.target.value)}>
                            <option value="">— Type manually or select saved —</option>
                            {tenants.map(t => <option key={t.id} value={t.id}>{t.name} {t.contact ? `(${t.contact})` : ""}</option>)}
                        </select>
                    </div>
                    <div style={fWrap}>
                        <label style={lStyle}>Tenant Name</label>
                        <input style={iStyle} placeholder="Full name" value={tenantName} onChange={e => setTenantName(e.target.value)} />
                    </div>
                    <div style={fWrap}>
                        <label style={lStyle}>Address</label>
                        <input style={iStyle} placeholder="e.g. Kathmandu-10" value={address} onChange={e => setAddress(e.target.value)} />
                    </div>
                    <div style={fWrap}>
                        <label style={lStyle}>Contact Number</label>
                        <input style={iStyle} placeholder="98XXXXXXXX" value={contact} onChange={e => setContact(e.target.value)} />
                    </div>
                    {!selectedTenantId && tenantName.trim() && (
                        <button onClick={handleSaveTenant} disabled={savingTenant} style={{ ...styles.btn(C.purple), padding: "6px 14px", fontSize: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                            {savingTenant ? "Saving..." : "Save as New Tenant"}
                        </button>
                    )}

                    <div style={secTitle}>Rent Details</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div style={fWrap}><label style={lStyle}>From (BS)</label><input style={iStyle} placeholder="2082/10/17" value={rentFrom} onChange={e => setRentFrom(e.target.value)} /></div>
                        <div style={fWrap}><label style={lStyle}>To (BS)</label><input style={iStyle} placeholder="2082/11/17" value={rentTo} onChange={e => setRentTo(e.target.value)} /></div>
                    </div>
                    <div style={fWrap}><label style={lStyle}>Month Label</label><input style={iStyle} placeholder="e.g. Magh - Falgun" value={monthName} onChange={e => setMonthName(e.target.value)} /></div>
                    <div style={fWrap}><label style={lStyle}>Rent Amount (NPR)</label><input style={iStyle} type="number" placeholder="e.g. 25000" value={rentAmt} onChange={e => setRentAmt(e.target.value)} /></div>

                    <div style={secTitle}>Electricity</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                        <div style={fWrap}><label style={lStyle}>Prev Unit</label><input style={iStyle} type="number" placeholder="0" value={prevUnit} onChange={e => setPrevUnit(e.target.value)} /></div>
                        <div style={fWrap}><label style={lStyle}>Curr Unit</label><input style={iStyle} type="number" placeholder="0" value={currUnit} onChange={e => setCurrUnit(e.target.value)} /></div>
                        <div style={fWrap}><label style={lStyle}>Rate/Unit</label><input style={iStyle} type="number" placeholder="17" value={rateUnit} onChange={e => setRateUnit(e.target.value)} /></div>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Used: <strong style={{ color: C.white }}>{unitsUsed}</strong> × Rs.{rateUnit || 17} = <strong style={{ color: C.accent }}>{fmtNPR(electricityCost)}</strong></div>

                    <div style={secTitle}>Other</div>
                    <div style={fWrap}><label style={lStyle}>Cleanliness Fee (0 to skip)</label><input style={iStyle} type="number" placeholder="200" value={cleanFee} onChange={e => setCleanFee(e.target.value)} /></div>
                    <div style={fWrap}>
                        <label style={lStyle}>Payment Method</label>
                        <select style={iStyle} value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                            <option>Bank Transfer</option><option>Cash</option><option>eSewa</option><option>Khalti</option><option>Cheque</option>
                        </select>
                    </div>
                    <div style={fWrap}><label style={lStyle}>Bank Name</label><input style={iStyle} placeholder="e.g. Global IME Bank" value={bankName} onChange={e => setBankName(e.target.value)} /></div>

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                        <button onClick={handleSaveDraft} disabled={saving}
                            style={{ ...styles.btn(C.accent), flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "11px 16px", fontWeight: 700 }}>
                            <FileText size={15} /> {saving ? "Saving..." : editId ? "Update" : "Save Draft"}
                        </button>
                        <button onClick={() => window.print()}
                            style={{ ...styles.btn(C.card2), display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "11px 14px", color: C.muted }}>
                            <Printer size={15} />
                        </button>
                        <button onClick={() => downloadReceiptPDF(receiptNumber)}
                            style={{ ...styles.btn(C.card2), display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "11px 14px", color: C.muted }}>
                            <Download size={15} />
                        </button>
                    </div>
                    {editId && (
                        <button onClick={() => setMarkPaidReceipt({ id: editId, tenant_name: tenantName, receipt_name: receiptName, status: "draft" })}
                            style={{ ...styles.btn(C.green), width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "11px 16px", fontWeight: 700, marginTop: 10 }}>
                            <CheckCircle size={15} /> Mark as Paid
                        </button>
                    )}
                    {saveMsg && <div style={{ fontSize: 12, color: saveMsg.startsWith("Error") ? C.red : C.green, marginTop: 10, textAlign: "center" }}>{saveMsg}</div>}
                </div>

                {/* ── LIVE PREVIEW ── */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Live Preview</div>
                    <ReceiptPreview r={previewData} />
                </div>
            </div>

            {markPaidReceipt && <MarkAsPaidModal receipt={markPaidReceipt} onClose={() => setMarkPaidReceipt(null)} onPaid={async () => { setMarkPaidReceipt(null); await loadAll(); setView("list"); }} />}
        </div>
    );
}

// ─── Public Staff Profile (no login required) ─────────────────────────────────
function PublicStaffProfile({ slug }) {
    const [staff, setStaff] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const data = await sb(`public_staff_profiles?select=id,name,designation,employment_type,join_date,profile_image&order=name`);
                const match = (data || []).find(s => {
                    const s2 = s.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
                    return s2 === slug;
                });
                if (match) setStaff(match); else setNotFound(true);
            } catch { setNotFound(true); }
            finally { setLoading(false); }
        })();
    }, [slug]);

    const etLabel = staff?.employment_type === "freelancer" ? "Freelancer" : staff?.employment_type === "part_time" ? "Part Time" : "Full Time";
    const etColor = staff?.employment_type === "freelancer" ? C.purple : staff?.employment_type === "part_time" ? C.yellow : C.green;
    const initials = staff ? staff.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() : "";

    if (loading) return (
        <div style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ color: "#94a3b8", fontSize: 16 }}>Loading…</div>
        </div>
    );
    if (notFound || !staff) return (
        <div style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
            <div style={{ fontSize: 48 }}>🔍</div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 22 }}>Profile not found</div>
            <div style={{ color: "#64748b", fontSize: 14 }}>This staff profile does not exist or has been removed.</div>
            <a href="/" style={{ color: "#6366f1", fontSize: 14, marginTop: 8 }}>← Back to Foxbeep Attendance</a>
        </div>
    );

    return (
        <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
            <div style={{ background: "#1e293b", borderRadius: 24, overflow: "hidden", width: "100%", maxWidth: 380, boxShadow: "0 24px 64px #0008" }}>
                {/* Blue header */}
                <div style={{ background: "linear-gradient(135deg, #1565C0, #42A5F5)", padding: "32px 24px 56px", position: "relative", textAlign: "center" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: 2, textTransform: "uppercase" }}>Foxbeep Technology</div>
                    <div style={{ fontSize: 11, color: "rgba(200,220,255,0.7)", marginTop: 2 }}>Staff Identity Verification</div>
                </div>
                {/* Photo */}
                <div style={{ display: "flex", justifyContent: "center", marginTop: -48, position: "relative", zIndex: 2 }}>
                    <div style={{ width: 96, height: 96, borderRadius: "50%", overflow: "hidden", border: "4px solid #1e293b", background: "#1565C0", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px #0005" }}>
                        {staff.profile_image
                            ? <img src={staff.profile_image} alt={staff.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : <span style={{ fontSize: 36, fontWeight: 900, color: "#fff" }}>{initials}</span>}
                    </div>
                </div>
                {/* Info */}
                <div style={{ padding: "16px 24px 28px", textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "#fff", marginBottom: 4 }}>{staff.name}</div>
                    <div style={{ fontSize: 15, color: "#93c5fd", fontWeight: 500, marginBottom: 12 }}>{staff.designation || "Staff"}</div>
                    <span style={{ background: etColor + "22", color: etColor, border: `1px solid ${etColor}44`, borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700 }}>{etLabel}</span>
                    <div style={{ marginTop: 20, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                        {staff.join_date && (
                            <div style={{ background: "#0f172a", borderRadius: 12, padding: "10px 16px", textAlign: "center" }}>
                                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Joined</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 4 }}>{new Date(staff.join_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</div>
                            </div>
                        )}
                        <div style={{ background: "#0f172a", borderRadius: 12, padding: "10px 16px", textAlign: "center" }}>
                            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>EMP ID</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 4 }}>{staff.id.slice(-8).toUpperCase()}</div>
                        </div>
                    </div>
                    <div style={{ marginTop: 20, padding: "12px 16px", background: "#0f172a", borderRadius: 12, display: "flex", alignItems: "center", gap: 10 }}>
                        <CheckCircle size={18} color={C.green} />
                        <div style={{ fontSize: 13, color: "#94a3b8" }}>This is a <strong style={{ color: "#fff" }}>verified staff member</strong> of Foxbeep Technology Pvt. Ltd.</div>
                    </div>
                    <a href="https://att.foxbeep.com" style={{ display: "block", marginTop: 16, fontSize: 12, color: "#475569", textDecoration: "none" }}>att.foxbeep.com</a>
                </div>
            </div>
        </div>
    );
}

export default function App() {
    const [user, setUser] = useState(null);
    const [page, setPage] = useState(() => {
        const hash = window.location.hash.replace("#", "");
        return hash || sessionStorage.getItem("foxbeep_page") || "dashboard";
    });
    const [profileId, setProfileId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const navigateTo = (p) => {
        window.location.hash = p;
        sessionStorage.setItem("foxbeep_page", p);
        setPage(p);
        setPageLoading(true);
        setTimeout(() => setPageLoading(false), 250);
    };

    useEffect(() => {
        const onHash = () => {
            const p = window.location.hash.replace("#", "");
            if (p) { setPage(p); }
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);
    const [, forceUpdate] = useState(0);
    const [seenCounts, setSeenCounts] = useState({});
    const [menuOpen, setMenuOpen] = useState(false);
    const [showPointsModal, setShowPointsModal] = useState(false);
    const mobile = useMobile();

    const loadAll = useCallback(async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) { setLoading(false); return; }

            // Resolve the current user by auth id. Admins load full staff rows;
            // everyone else loads the safe directory plus their own full row.
            const ownRows = await db.getOwnStaff(session.user.id).catch(() => []);
            const currentStaffRecord = Array.isArray(ownRows) ? ownRows[0] : null;
            const currentStaffId = currentStaffRecord?.id || null;
            const staffData = currentStaffRecord?.role === "admin"
                ? await db.getStaff()
                : (await db.getStaffDirectory()).map(s => (s.id === currentStaffId ? currentStaffRecord : s));

            const [salaryHistoryData, attendanceData, holidayData, projectData, commissionsData, bonusesData, withdrawData, salaryData, settingsData, pointsData, rewardsData, redemptionsData, postsData, repliesData, notifsData, moodsData, sotmData, workLogsData, pollsData, announcementsData, leaveData, roomsData, bookingsData] = await Promise.all([
                getSalaryHistory().catch(() => null), // null → table not deployed yet
                db.getAttendance(),
                db.getHolidays(),
                db.getProjects(),
                db.getCommissions(),
                db.getBonuses(),
                db.getWithdrawRequests(),
                db.getSalaryPayments(),
                db.getAppSettings(),
                db.getPointsLedger(),
                db.getRewards(),
                db.getRedemptions(),
                db.getPosts(),
                db.getReplies(),
                currentStaffId ? db.getNotifications(currentStaffId) : Promise.resolve([]),
                db.getMoods(),
                db.getSotm(),
                db.getWorkLogs(),
                db.getPolls(),
                db.getAnnouncements(),
                db.getLeaveRequests(),
                db.getMeetingRooms(),
                db.getBookings(),
            ]);
            _staff = staffData || [];
            _attendance = {};
            (attendanceData || []).forEach(r => { _attendance[`${r.staff_id}_${r.date}`] = r; });
            _holidayRecords = holidayData || [];
            _holidays = _holidayRecords.map(h => h.date);
            _projects = projectData || [];
            _commissions = commissionsData || [];
            _bonuses = bonusesData || [];
            _withdrawRequests = withdrawData || [];
            _salaryPayments = salaryData || [];
            _salaryHistoryAvailable = Array.isArray(salaryHistoryData);
            _salaryHistory = salaryHistoryData || [];
            if (settingsData && settingsData.length > 0) _appSettings = settingsData[0];
            _pointsLedger = pointsData || [];
            _rewards = rewardsData || [];
            _redemptions = redemptionsData || [];
            _posts = postsData || [];
            _replies = repliesData || [];
            _notifications = notifsData || [];
            _moods = moodsData || [];
            _sotm = sotmData || [];
            _workLogs = workLogsData || [];
            _polls = pollsData || [];
            _announcements = announcementsData || [];
            _leaveRequests = leaveData || [];
            _meetingRooms = roomsData || [];
            _bookings = bookingsData || [];

            // Always re-derive user from DB to prevent role tampering via sessionStorage
            const fresh = currentStaffRecord;
            if (fresh) {
                setUser({ ...fresh, role: fresh.role || "staff" });
                // Load this user's granted menu access (staff only)
                if (fresh.role !== "admin") {
                    try {
                        const ma = await sb(`user_menu_access?staff_id=eq.${fresh.id}&select=menu_id`);
                        _userMenuAccess = (Array.isArray(ma) ? ma : []).map(r => r.menu_id);
                    } catch (e) { _userMenuAccess = []; }
                } else {
                    _userMenuAccess = [];
                }
            }

            forceUpdate(x => x + 1);
        } catch (e) {
            console.error("Supabase load error:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    // On mount: check existing session, then keep in sync with auth state
    useEffect(() => {
        loadAll();
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
            if (event === "SIGNED_OUT") { setUser(null); setPage("dashboard"); }
        });
        return () => subscription.unsubscribe();
    }, [loadAll]);

    // Load seen-badge counts from localStorage when user is known
    useEffect(() => {
        if (user?.id) {
            try {
                const stored = localStorage.getItem(`seen_${user.id}`);
                setSeenCounts(stored ? JSON.parse(stored) : {});
            } catch { setSeenCounts({}); }
        }
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mark current page as seen whenever user navigates to it
    useEffect(() => {
        if (!user) return;
        const count = getPageCount(page, user.id, user.role === "admin");
        setSeenCounts(prev => {
            const updated = { ...prev, [page]: count };
            try { localStorage.setItem(`seen_${user.id}`, JSON.stringify(updated)); } catch {}
            return updated;
        });
    }, [page, user]); // eslint-disable-line react-hooks/exhaustive-deps

    // Heartbeat — update last_login every 2 min so online dot stays green while active
    useEffect(() => {
        if (!user || user.role === "admin") return;
        const ping = () => db.updateStaff(user.id, { last_login: new Date().toISOString() });
        ping();
        const t = setInterval(ping, 2 * 60 * 1000);
        return () => clearInterval(t);
    }, [user]);

    // Public profile — accessible without login
    const publicSlug = (() => { const h = window.location.hash.replace("#", ""); return h.startsWith("p/") ? h.slice(2) : null; })();
    if (publicSlug) return <PublicStaffProfile slug={publicSlug} />;

    if (loading) return <SkeletonScreen />;

    if (!user) return <LoginScreen onLogin={async (u) => {
        setUser(u);
        const dp = u.role === "admin" ? "dashboard" : (_appSettings.default_page || "explore");
        navigateTo(dp);
        await loadAll();
    }} />;

    const isAdmin = user.role === "admin";
    const getBadge = (pageId) => {
        const current = getPageCount(pageId, user.id, isAdmin);
        const seen = seenCounts[pageId] ?? 0;
        return Math.max(0, current - seen);
    };
    const adminNavSections = [
        {
            title: "Main", items: [
                { id: "dashboard", icon: Home, label: "Dashboard" },
                { id: "explore", icon: Globe, label: "Feeds", badge: getBadge("explore") },
                { id: "trello", icon: LayoutGrid, label: "Trello Boards" },
                { id: "announcements", icon: Megaphone, label: "Announcements" },
            ]
        },
        {
            title: "People & HR", items: [
                { id: "staff", icon: Users, label: "Manage Staff" },
                { id: "idcards", icon: Fingerprint, label: "ID Cards" },
                { id: "team", icon: Contact, label: "Team Profiles" },
                { id: "useraccess", icon: Shield, label: "User Access" },
                { id: "leaverequests", icon: Umbrella, label: "Leave Requests", badge: getBadge("leaverequests") },
                { id: "sotm", icon: Award, label: "Staff of Month" },
                { id: "badges", icon: Medal, label: "Badges" },
            ]
        },
        {
            title: "Attendance", items: [
                { id: "editattendance", icon: PenLine, label: "Edit Attendance" },
                { id: "holidays", icon: Calendar, label: "Holidays" },
                { id: "report", icon: BarChart2, label: "Reports" },
            ]
        },
        {
            title: "Finance", items: [
                { id: "salarypayments", icon: Wallet, label: "Salaries", badge: getBadge("salarypayments") },
                { id: "commissions", icon: DollarSign, label: "Commissions", badge: getBadge("commissions") },
                { id: "bonuses", icon: Gift, label: "Bonuses", badge: getBadge("bonuses") },
                { id: "projects", icon: Briefcase, label: "Freelancer Projects" },
                { id: "withdrawrequests", icon: CreditCard, label: "Payments", badge: getBadge("withdrawrequests") },
                { id: "redeemedpoints", icon: Gift, label: "Redeemed Points" },
            ]
        },
        {
            // Admin only: never add Accounts to GRANTABLE_MENUS (the database also refuses it).
            title: "Accounts", items: [
                { id: "accounts", icon: Landmark, label: "Accounts" },
            ]
        },
        {
            // Admin only, separate from Finance/Accounts: an independent operational module. Never add to GRANTABLE_MENUS
            // (the database refuses sales*/client* grants and every Sales table/RPC checks is_admin()).
            title: "Sales", items: [
                { id: "sales", icon: Handshake, label: "Sales" },
            ]
        },
        {
            // Admin only, a standalone USD spending tracker with its own qd_* tables: no link to Accounts, Sales or any other
            // module. Never add to GRANTABLE_MENUS (the database refuses "queen*" grants and every qd_* table/RPC checks is_admin()).
            title: "Queen Dollar", items: [
                { id: "queendollar", icon: BadgeDollarSign, label: "Queen Dollar" },
            ]
        },
        {
            title: "Workspace", items: [
                { id: "polls", icon: Vote, label: "Polls", badge: getBadge("polls") },
                { id: "worklogs", icon: ClipboardList, label: "Work Logs", badge: getBadge("worklogs") },
                { id: "rooms", icon: Building2, label: "Meeting Rooms" },
            ]
        },
        {
            title: "Tools", items: [
                { id: "rentreceipt", icon: Receipt, label: "Rent Receipts" },
                { id: "tenants", icon: Users, label: "Manage Tenants" },
            ]
        },
        {
            title: "Account", items: [
                { id: "settings", icon: Settings, label: "Settings" },
            ]
        },
    ];
    const currentStaff = _staff.find(s => s.id === user.id);
    const isFreelancer2 = currentStaff?.employment_type === "freelancer";
    const commissionIsNew = new Date() < new Date("2026-04-15T00:00:00");
    const bonusIsNew = new Date() < new Date("2026-04-16T00:00:00");
    const isModStaff = !isAdmin && (currentStaff?.mod_staff === true);
    const isModFreelancer = !isAdmin && (currentStaff?.mod_freelancer === true);
    const isModerator = isModStaff || isModFreelancer;
    const staffNavSections = isFreelancer2 ? [
        {
            title: "Main", items: [
                { id: "dashboard", icon: Home, label: "Dashboard" },
                { id: "explore", icon: Globe, label: "Feeds", badge: getBadge("explore") },
                { id: "trello", icon: LayoutGrid, label: "Trello Boards" },
                { id: "announcements", icon: Megaphone, label: "Announcements", badge: getBadge("announcements") },
            ]
        },
        {
            title: "Work", items: [
                { id: "myprojects", icon: Briefcase, label: "My Projects", badge: getBadge("myprojects") },
                { id: "analytics", icon: TrendingUp, label: "Analytics" },
                { id: "mycommission", icon: DollarSign, label: "My Commission", newBadge: commissionIsNew, badge: getBadge("mycommission") },
                { id: "mybonus", icon: Gift, label: "My Bonus", newBadge: bonusIsNew, badge: getBadge("mybonus") },
                { id: "withdrawalhistory", icon: Receipt, label: "Withdrawal History", badge: getBadge("withdrawalhistory") },
                { id: "polls", icon: Vote, label: "Polls", badge: getBadge("polls") },
                { id: "rooms", icon: Building2, label: "Meeting Rooms" },
            ]
        },
        {
            title: "Profile", items: [
                { id: "badges", icon: Medal, label: "My Badges" },
                { id: "settings", icon: Settings, label: "Settings" },
            ]
        },
    ] : [
        {
            title: "Main", items: [
                { id: "dashboard", icon: Home, label: "My Dashboard" },
                ...(isModerator ? [{ id: "teamdashboard", icon: Monitor, label: "Team Dashboard" }] : []),
                { id: "explore", icon: Globe, label: "Feeds", badge: getBadge("explore") },
                ...(isModFreelancer ? [{ id: "trello", icon: LayoutGrid, label: "Trello Boards" }] : []),
                { id: "announcements", icon: Megaphone, label: "Announcements", badge: getBadge("announcements") },
            ]
        },
        {
            title: "My Work", items: [
                { id: "attendance", icon: ClipboardCheck, label: "Attendance" },
                { id: "mysalary", icon: Landmark, label: "My Salary", badge: getBadge("mysalary") },
                { id: "mycommission", icon: DollarSign, label: "My Commission", newBadge: commissionIsNew, badge: getBadge("mycommission") },
                { id: "mybonus", icon: Gift, label: "My Bonus", newBadge: bonusIsNew, badge: getBadge("mybonus") },
                { id: "leaverequests", icon: Umbrella, label: "Leave Requests", badge: getBadge("leaverequests") },
                { id: "worklogs", icon: ClipboardList, label: "Work Log" },
                ...(isModerator ? [{ id: "report", icon: BarChart2, label: isModStaff && isModFreelancer ? "Reports (All)" : isModStaff ? "Reports (Staff)" : "Reports (FL)" }] : []),
            ]
        },
        {
            title: "Tools", items: [
                { id: "tools_compressor", icon: ImageDown, label: "Image Compressor" },
            ]
        },
        {
            title: "Workspace", items: [
                { id: "polls", icon: Vote, label: "Polls", badge: getBadge("polls") },
                { id: "rooms", icon: Building2, label: "Meeting Rooms" },
            ]
        },
        {
            title: "Profile", items: [
                { id: "badges", icon: Medal, label: "My Badges" },
                { id: "settings", icon: Settings, label: "Settings" },
            ]
        },
    ];
    // Build extended access section from admin-granted menus
    const baseNavIds = staffNavSections.flatMap(s => s.items.map(i => i.id));
    const grantedExtras = GRANTABLE_MENUS.filter(m => _userMenuAccess.includes(m.id) && !baseNavIds.includes(m.id));
    const fullStaffNavSections = grantedExtras.length > 0
        ? [...staffNavSections, { title: "Extended Access", items: grantedExtras }]
        : staffNavSections;
    const navSections = isAdmin ? adminNavSections : fullStaffNavSections;

    // Data + helpers handed to the finance modules (src/finance). Built per render
    // from the in-memory caches so pages always see the latest loadAll() data.
    const financeCtx = {
        user, staff: _staff, salaryPayments: _salaryPayments, bonuses: _bonuses, today: todayStr(),
        salaryRowsFor, hasAttendanceInMonth, statusForDate: getStatusForDate, staffName: getStaffName, reload: loadAll,
    };

    const renderPage = () => {
        if (isAdmin) {
            if (page === "dashboard") return <AdminDashboard reload={loadAll} />;
            if (page === "staff") return <AdminManageStaff reload={loadAll} />;
            if (page === "holidays") return <AdminHolidays reload={loadAll} />;
            if (page === "editattendance") return <AdminEditAttendance reload={loadAll} />;
            if (page === "report") return <AdminReport />;
            if (page === "projects") return <AdminProjects reload={loadAll} />;
            if (page === "withdrawrequests") return <AdminWithdrawRequests reload={loadAll} />;
            if (page === "salarypayments") return <SalaryAdminPage ctx={financeCtx} />;
            if (page === "commissions") return <AdminCommissionsPage reload={loadAll} />;
            if (page === "bonuses") return <BonusAdminPage ctx={financeCtx} />;
            if (page === "accounts") return <AccountsPage />;
            if (page === "sales") return <SalesPage />;
            if (page === "queendollar") return <QueenDollarPage />;
            if (page === "idcards") return <AdminIdCardsPage />;
            if (page === "team") return <TeamProfiles setPage={navigateTo} setProfileId={setProfileId} />;
            if (page === "profile") return <StaffProfile staffId={profileId} setPage={navigateTo} isAdmin={true} />;
            if (page === "redeemedpoints") return <AdminRedeemedPoints reload={loadAll} />;
            if (page === "explore") return <ExplorePage user={user} reload={loadAll} />;
            if (page === "trello") return <TrelloPage />;
            if (page === "announcements") return <div style={{ maxWidth: 680 }}><h2 style={{ margin: "0 0 20px", fontWeight: 900, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><Megaphone size={22} /> Announcements</h2><AnnouncementsPanel user={user} isAdmin={true} reload={loadAll} /></div>;
            if (page === "polls") return <PollsPage user={user} isAdmin={true} reload={loadAll} />;
            if (page === "worklogs") return <WorkLogPage user={user} isAdmin={true} reload={loadAll} />;
            if (page === "leaverequests") return <LeaveRequestsPage user={user} isAdmin={true} reload={loadAll} />;
            if (page === "rooms") return <MeetingRoomsPage user={user} isAdmin={true} reload={loadAll} />;
            if (page === "tools_compressor") return <ImageCompressorTool />;
            if (page === "badges") return <BadgesPage user={user} isAdmin={true} />;
            if (page === "sotm") return <div style={{ maxWidth: 600 }}><h2 style={{ margin: "0 0 20px", fontWeight: 900, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><Award size={22} /> Staff of the Month</h2><StaffOfMonth isAdmin={true} reload={loadAll} /><MoodAdminView /></div>;
            if (page === "rentreceipt") return <RentReceiptPage user={user} isAdmin={true} />;
            if (page === "tenants") return <ManageTenantsPage user={user} isAdmin={true} />;
            if (page === "useraccess") return <UserAccessPage />;
            if (page === "settings") return <SettingsPage user={user} isAdmin={true} reload={loadAll} setUser={setUser} />;
        } else {
            const staffInfo2 = _staff.find(s => s.id === user.id);
            const isFreelancer = staffInfo2?.employment_type === "freelancer";
            if (page === "dashboard") return isFreelancer ? <FreelancerDashboard user={user} reload={loadAll} /> : <StaffDashboard user={user} reload={loadAll} isModerator={isModerator} isModStaff={isModStaff} isModFreelancer={isModFreelancer} />;
            const granted = (id) => _userMenuAccess.includes(id);
            if (page === "teamdashboard" && (isModerator || granted("teamdashboard"))) return <ModeratorDashboard user={user} reload={loadAll} isModStaff={isModStaff} isModFreelancer={isModFreelancer} />;
            if (page === "attendance") return <StaffAttendance user={user} />;
            if (page === "mysalary") return <MySalaryPage ctx={financeCtx} />;
            if (page === "mycommission") return <MyCommissionsPage user={user} />;
            if (page === "mybonus") return <MyBonusPage ctx={financeCtx} />;
            if (page === "withdrawalhistory") return <FreelancerWithdrawalHistory user={user} />;
            if (page === "myprojects" && isFreelancer) return <FreelancerProjectsPage user={user} />;
            if (page === "analytics" && (isFreelancer || granted("analytics"))) return <FreelancerAnalytics user={user} />;
            if (page === "report" && (isModerator || granted("report"))) return <AdminReport modStaff={isModStaff} modFreelancer={isModFreelancer} showPay={false} />;
            if (page === "editattendance" && granted("editattendance")) return <AdminEditAttendance reload={loadAll} />;
            if (page === "explore") return <ExplorePage user={user} reload={loadAll} />;
            if (page === "trello" && (isFreelancer || isModFreelancer || granted("trello"))) return <TrelloPage />;
            if (page === "rentreceipt" && granted("rentreceipt")) return <RentReceiptPage user={user} isAdmin={false} />;
            if (page === "tenants" && granted("tenants")) return <ManageTenantsPage user={user} isAdmin={false} />;
            if (page === "announcements") return <div style={{ maxWidth: 680 }}><h2 style={{ margin: "0 0 20px", fontWeight: 900, fontSize: 22, display: "flex", alignItems: "center", gap: 10 }}><Megaphone size={22} /> Announcements</h2><AnnouncementsPanel user={user} isAdmin={false} reload={loadAll} /></div>;
            if (page === "polls") return <PollsPage user={user} isAdmin={false} reload={loadAll} />;
            if (page === "worklogs") return <WorkLogPage user={user} isAdmin={granted("worklogs")} reload={loadAll} />;
            if (page === "leaverequests") return <LeaveRequestsPage user={user} isAdmin={granted("leaverequests")} reload={loadAll} />;
            if (page === "rooms") return <MeetingRoomsPage user={user} isAdmin={false} reload={loadAll} />;
            if (page === "tools_compressor") return <ImageCompressorTool />;
            if (page === "badges") return <BadgesPage user={user} isAdmin={false} />;
            if (page === "settings") return <SettingsPage user={user} isAdmin={false} reload={loadAll} setUser={setUser} />;
        }
        // Unknown page, or a page this account may not open (data is also blocked server-side).
        return (
            <div style={{ ...styles.card, maxWidth: 480, margin: "40px auto", textAlign: "center" }}>
                <div style={{ fontWeight: 800, fontSize: 18, color: C.white }}>You don't have access to this page</div>
                <div style={{ color: C.muted, fontSize: 13, margin: "8px 0 18px" }}>It may be for administrators only, or the link is out of date.</div>
                <button style={styles.btn(C.accent)} onClick={() => navigateTo("dashboard")}>Go to dashboard</button>
            </div>
        );
    };

    const staffInfo = isAdmin ? null : _staff.find(s => s.id === user.id);

    const doSignOut = async () => { sessionStorage.removeItem("foxbeep_page"); await supabase.auth.signOut(); };

    const etColor = !isAdmin && staffInfo?.employment_type === "freelancer" ? C.purple : !isAdmin && staffInfo?.employment_type === "part_time" ? C.yellow : C.green;
    const etLabel = !isAdmin && staffInfo?.employment_type === "freelancer" ? "Freelancer" : !isAdmin && staffInfo?.employment_type === "part_time" ? "Part Time" : "Full Time";

    return (
        <div style={styles.app}>
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
            <style>{`@keyframes newPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.08)} } @keyframes toastIn { from{opacity:0;transform:translateX(-50%) translateY(12px)} to{opacity:1;transform:translateX(-50%) translateY(0)} } @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.4)} }`}</style>
            <GlobalToast />
            <div style={{ display: "flex", flexDirection: mobile ? "column" : "row" }}>

                {/* ── MOBILE TOP BAR ── */}
                {mobile && (
                    <div style={{ position: "sticky", top: 0, zIndex: 200, background: C.card, borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <img src="/logo.png" alt="Foxbeep" style={{ width: 30, height: 30, borderRadius: 8, objectFit: "cover" }} />
                                <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.border, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                                    {staffInfo?.profile_image ? <img src={staffInfo.profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
                                </div>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 900, color: C.white }}>{user.name}</div>
                                    <div style={{ fontSize: 10, color: C.muted }}>{isAdmin ? "Admin Panel" : staffInfo?.designation || ""}</div>
                                </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {!isAdmin && <NotificationBell user={user} reload={loadAll} />}
                                {!isAdmin && <PointsBadge userId={user.id} onClick={() => setShowPointsModal(true)} />}
                                <div onClick={doSignOut} style={{ cursor: "pointer", padding: "7px 11px", borderRadius: 8, color: C.red, fontWeight: 700, fontSize: 13, background: C.red + "11", border: `1px solid ${C.red}22`, display: "flex", alignItems: "center" }}><LogOut size={16} /></div>
                                <div onClick={() => setMenuOpen(v => !v)} style={{ cursor: "pointer", padding: "7px 11px", borderRadius: 8, background: menuOpen ? C.accent + "22" : C.card2, border: `1px solid ${menuOpen ? C.accent : C.border}`, color: C.white, fontWeight: 700, fontSize: 16, display: "flex", alignItems: "center" }}><Menu size={18} /></div>
                            </div>
                        </div>
                        {/* Mobile nav dropdown */}
                        {menuOpen && (
                            <div style={{ borderTop: `1px solid ${C.border}`, paddingBottom: 8, maxHeight: "70vh", overflowY: "auto" }}>
                                {navSections.map((section, si) => (
                                    <div key={si}>
                                        <div style={{ padding: "8px 20px 3px", fontSize: 9, fontWeight: 800, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase" }}>{section.title}</div>
                                        {section.items.map(n => (
                                            <div key={n.id} onClick={() => { navigateTo(n.id); setMenuOpen(false); }}
                                                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 20px", cursor: "pointer", background: page === n.id ? C.accent + "22" : "transparent", borderLeft: page === n.id ? `3px solid ${C.accent}` : "3px solid transparent", color: page === n.id ? C.white : C.muted, fontWeight: page === n.id ? 700 : 500, fontSize: 13 }}>
                                                <n.icon size={16} color="currentColor" style={{ flexShrink: 0 }} />
                                                <span style={{ flex: 1 }}>{n.label}</span>
                                                {n.newBadge && <span style={{ background: "linear-gradient(90deg,#f59e0b,#ef4444)", color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 10, fontWeight: 800, letterSpacing: 0.5, animation: "newPulse 1.6s ease-in-out infinite" }}>NEW</span>}
                                                {n.badge > 0 && <span style={{ background: C.red, color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>{n.badge}</span>}
                                            </div>
                                        ))}
                                        {si < navSections.length - 1 && <div style={{ margin: "4px 20px 0", borderBottom: `1px solid ${C.border}` }} />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ── DESKTOP SIDEBAR ── */}
                {!mobile && (
                    <div style={{ ...styles.sidebar, position: "sticky", top: 0, height: "100vh", overflowY: "auto" }}>
                        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                                <img src="/logo.png" alt="Foxbeep" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover" }} />
                                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 2 }}>FOXBEEP</div>
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: C.white, lineHeight: 1.3 }}>Attendance</div>
                            <div style={{ fontSize: 11, color: C.muted }}>{isAdmin ? "Admin Panel" : "Team Management Portal"}</div>
                        </div>
                        <div style={{ paddingTop: 6, flex: 1 }}>
                            {navSections.map((section, si) => (
                                <div key={si}>
                                    <div style={{ padding: "10px 16px 4px", fontSize: 9, fontWeight: 800, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase", userSelect: "none" }}>{section.title}</div>
                                    {section.items.map(n => (
                                        <div key={n.id} onClick={() => navigateTo(n.id)}
                                            style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", margin: "1px 8px", cursor: "pointer", borderRadius: 8, background: page === n.id ? C.accent + "22" : "transparent", color: page === n.id ? C.white : C.muted, fontWeight: page === n.id ? 700 : 500, fontSize: 13, transition: "background 0.15s, color 0.15s", borderLeft: page === n.id ? `2px solid ${C.accent}` : "2px solid transparent" }}>
                                            <n.icon size={15} color="currentColor" style={{ flexShrink: 0 }} />
                                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.label}</span>
                                            {n.newBadge && <span style={{ background: "linear-gradient(90deg,#f59e0b,#ef4444)", color: "#fff", borderRadius: 20, padding: "1px 6px", fontSize: 10, fontWeight: 800, letterSpacing: 0.5, flexShrink: 0, animation: "newPulse 1.6s ease-in-out infinite" }}>NEW</span>}
                                            {n.badge > 0 && <span style={{ background: C.red, color: "#fff", borderRadius: 20, padding: "1px 6px", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{n.badge}</span>}
                                        </div>
                                    ))}
                                    {si < navSections.length - 1 && <div style={{ margin: "6px 16px 2px", borderBottom: `1px solid ${C.border}` }} />}
                                </div>
                            ))}
                        </div>
                        <div style={{ borderTop: `1px solid ${C.border}`, padding: 16 }}>
                            <div style={{ ...styles.card2, marginBottom: 10 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.border, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                                        {staffInfo?.profile_image ? <img src={staffInfo.profile_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: C.white, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</div>
                                        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{isAdmin ? "Administrator" : staffInfo?.designation}</div>
                                    </div>
                                </div>
                                {!isAdmin && staffInfo?.employment_type && <div style={{ fontSize: 10, display: "flex", gap: 4, flexWrap: "wrap", marginTop: 2 }}>
                                    <span style={{ background: etColor + "33", color: etColor, borderRadius: 6, padding: "2px 7px", fontWeight: 700 }}>{etLabel}</span>
                                    {isModStaff && <span style={{ background: C.green + "33", color: C.green, borderRadius: 6, padding: "2px 7px", fontWeight: 700 }}>🛡 Staff Mod</span>}
                                    {isModFreelancer && <span style={{ background: C.purple + "33", color: C.purple, borderRadius: 6, padding: "2px 7px", fontWeight: 700 }}>🟣 FL Mod</span>}
                                </div>}
                            </div>
                            <div onClick={doSignOut} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", cursor: "pointer", borderRadius: 10, color: C.red, fontWeight: 600, fontSize: 14, background: C.red + "11", border: `1px solid ${C.red}22` }}>
                                <LogOut size={16} /> Sign Out
                            </div>
                        </div>
                    </div>
                )}

                {/* ── MAIN CONTENT ── */}
                <div style={{ flex: 1, padding: mobile ? 14 : 32, minWidth: 0, display: "flex", flexDirection: "column", minHeight: mobile ? "unset" : "100vh" }}>
                    {!mobile && !isAdmin && (
                        <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: 16, gap: 10, alignItems: "center" }}>
                            {!isAdmin && <NotificationBell user={user} reload={loadAll} />}
                            <PointsBadge userId={user.id} onClick={() => setShowPointsModal(true)} />
                        </div>
                    )}
                    <div style={{ flex: 1 }}>
                        {pageLoading ? (
                            <div style={{ padding: 8 }}>
                                <SkeletonBlock w={200} h={26} radius={8} mb={8} />
                                <SkeletonBlock w={140} h={13} radius={6} mb={24} />
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 14, marginBottom: 20 }}>
                                    {Array.from({ length: 4 }).map((_, i) => <div key={i} style={{ background: C.card, borderRadius: 14, padding: 18 }}><SkeletonBlock w="50%" h={11} radius={4} mb={10} /><SkeletonBlock w="40%" h={22} radius={6} /></div>)}
                                </div>
                                <div style={{ background: C.card, borderRadius: 14, padding: 20 }}>
                                    {Array.from({ length: 4 }).map((_, i) => <div key={i} style={{ display: "flex", gap: 10, marginBottom: 14 }}><SkeletonBlock w={36} h={36} radius="50%" /><div style={{ flex: 1 }}><SkeletonBlock w="55%" h={12} radius={4} mb={7} /><SkeletonBlock w="35%" h={10} radius={4} /></div></div>)}
                                </div>
                            </div>
                        ) : renderPage()}
                    </div>
                    {showPointsModal && !isAdmin && <PointsModal user={user} onClose={() => setShowPointsModal(false)} reload={loadAll} />}
                    <div style={{ textAlign: "center", padding: "16px", color: C.muted, fontSize: 12, borderTop: `1px solid ${C.border}`, marginTop: 32 }}>
                        Made with ❤️ by <a href="https://foxbeep.com" target="_blank" rel="noreferrer" style={{ color: C.accent, textDecoration: "none", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}><img src="/foxbeep-logo-triangle.png" alt="Foxbeep" style={{ height: 14, width: "auto" }} />Foxbeep Team</a>
                    </div>
                </div>
            </div>
        </div>
    );
}